import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  BookOpen,
  CheckCircle,
  CheckSquare,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Cloud,
  Cpu,
  FileText,
  Globe,
  HardDrive,
  Import,
  Link2,
  Loader2,
  Pencil,
  Plus,
  Search,
  Send,
  Server,
  Settings,
  Shield,
  SquareArrowOutUpRight,
  Trash2,
  Upload,
  X,
  XCircle
} from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router";
import {
  abortImportSession, addComponent, addControlImplementation, commitImportSession, createComponentDefinition, deleteComponent,
  deleteComponentDefinition, deleteImplementedRequirements, downloadFile, exportCatalogDoc, getAPI, getCatalog, getComponentDefinition, getDocument, getExportDownloadUrl, getExportJob, getImportSession, importCatalog,
  importCatalogFromUrl, listCatalogs, listComponentDefinitions, listComponents, listControlImplementations, transitionDocument, updateControlImplementation, validateImport, type AddControlImplementationPayload, type CatalogResponse, type ComponentDefinitionResponse,
  type ComponentDefinitionSummaryResponse, type ComponentResponse, type ComponentType, type ControlImplementationResponse, type ControlResponse, type DocumentResponse, type GroupResponse, type ImportJobResponse,
  type ImportSessionResponse,
  type ValidateResponse
} from "../../api/frameworksApi";
import { API_URL } from "../../constants";
import { useHasPermission } from "../../hooks/usePermission";
import { useRBACPermission } from "../../hooks/useRBACPermission";
import { useToastStore } from "../../stores/toastStore";
import {
  getPageSizeOptions,
  resolvePageSizeForViewMode,
  safeFormatDate,
} from "../../utils/formatters";
import { DataTable, ViewModeToggle } from "../components/DataTable";
import { DeleteConfirmDialog } from "../components/DeleteConfirmDialog";
import { Dropdown } from "../components/Dropdown";
import {
  EntityCardGrid,
  MetaDates,
  StatTile,
  StatTiles,
  TitleTooltip,
  statusAccent,
} from "../components/EntityCardGrid";
import {
  FormField,
  FormInput,
  FormSelect,
  FormTextarea,
  Modal,
  ModalFooterButtons,
} from "../components/Modal";
import { RBACAction } from "../components/RBACButton";
import { SkeletonCard } from "../components/Skeleton";
import { StatusBadge } from "../components/StatusBadge";
import { Tip } from "../components/ui/tooltip";
import { ComponentDetailsPanel } from "./visualizer/ComponentDetailsPanel";
import { ComponentGraph } from "./visualizer/ComponentGraph";
import { ComponentTree } from "./visualizer/ComponentTree";
import { useVisualizerStore } from "./visualizer/store";
import type { OscalComponentDefinition } from "./visualizer/types";

type FilterType = "All" | ComponentType;

const COMPONENT_TYPES: ComponentType[] = [
  "software",
  "hardware",
  "service",
  "policy",
  "process",
  "procedure",
  "plan",
  "guidance",
  "standard",
  "validation",
  "operating-system",
];

const typeIconMap: Record<ComponentType, React.ElementType> = {
  software: Cloud,
  hardware: HardDrive,
  service: Server,
  policy: FileText,
  process: Settings,
  procedure: BookOpen,
  plan: FileText,
  guidance: BookOpen,
  standard: Shield,
  validation: CheckSquare,
  "operating-system": Cpu,
};

const typeLabel = (t: ComponentType) => {
  if (t === "operating-system") return "Operating System";
  return t.charAt(0).toUpperCase() + t.slice(1);
};

const filterOptions: FilterType[] = ["All", ...COMPONENT_TYPES];

type ComponentWithDef = ComponentResponse & { definitionId: string };

type WizardStep = "title" | "components" | "review";

const wizardSteps: { key: WizardStep; label: string; num: number }[] = [
  { key: "title", label: "Title", num: 1 },
  { key: "components", label: "Components & Controls", num: 2 },
  { key: "review", label: "Review", num: 3 },
];

export function ComponentLibrary() {
  const navigate = useNavigate();
  const { id: componentIdFromUrl } = useParams<{ id: string }>();

  // Permission checks
  const canCreate = useHasPermission("component", "create");
  const canDelete = useHasPermission("component", "delete");
  const canUpdate = useHasPermission("component", "update");

  const [activeFilter, setActiveFilter] = useState<FilterType>("All");
  const [showWizard, setShowWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState<WizardStep>("title");
  const [createdDefId, setCreatedDefId] = useState<string | null>(null);
  const [createdDefTitle, setCreatedDefTitle] = useState<string>("");
  const [createdComponents, setCreatedComponents] = useState<
    {
      id: string;
      title: string;
      componentType: ComponentType;
      description: string;
      purpose: string;
    }[]
  >([]);
  const [stepLoading, setStepLoading] = useState(false);
  const [wizardError, setWizardError] = useState<string | null>(null);
  const [compForm, setCompForm] = useState({
    title: "",
    componentType: "" as ComponentType | "",
    description: "",
    purpose: "",
    props: [{ name: "Group Name", value: "" }] as {
      name: string;
      value: string;
    }[],
  });
  // Step 2: Control mapping state
  const [wizCatalogs, setWizCatalogs] = useState<CatalogResponse[]>([]);
  const [wizSelectedCatalogId, setWizSelectedCatalogId] = useState("");
  const [wizControls, setWizControls] = useState<ControlResponse[]>([]);
  const [wizControlDetails, setWizControlDetails] = useState<
    Record<string, ControlResponse>
  >({});
  const [wizLoadingCatalogs, setWizLoadingCatalogs] = useState(false);
  const [wizLoadingControls, setWizLoadingControls] = useState(false);
  const [wizLoadingDetails, setWizLoadingDetails] = useState(false);
  const [wizPendingControlCalls, setWizPendingControlCalls] = useState(0);
  const [wizComponentControls, setWizComponentControls] = useState<
    Record<
      string,
      {
        controlId: string;
        title: string;
        description: string;
        catalogId: string;
        setParameters: { paramId: string; values: string }[];
      }[]
    >
  >({});
  const [wizActiveComponentId, setWizActiveComponentId] = useState<
    string | null
  >(null);

  // Data state
  const [definitions, setDefinitions] = useState<ComponentDefinitionResponse[]>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [docTitles, setDocTitles] = useState<Record<string, string>>({});
  const [defCounts, setDefCounts] = useState<
    Record<
      string,
      { componentCount: number; controlCount: number; lifecycleStatus: string }
    >
  >({});

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  // Card grids use multiples of 12 so the responsive 1/2/3/4-column layout
  // always fills complete rows (matches the SSP list). Table view keeps 10.
  const [pageSize, setPageSize] = useState(12);
  const [viewMode, setViewMode] = useState<"card" | "table">("card");
  // Default page size must be a valid option for the default view mode above
  // ("card") — using the plain table default (10) here would mount the
  // dropdown with a value that isn't one of its own options.
  const [totalDefinitions, setTotalDefinitions] = useState(0);

  // Sort state for definitions table
  const [compSortKey, setCompSortKey] = useState<string | null>(null);
  const [compSortDir, setCompSortDir] = useState<"asc" | "desc">("asc");

  // Definition detail view state
  const [selectedDefinition, setSelectedDefinition] =
    useState<ComponentDefinitionResponse | null>(null);
  const [defComponents, setDefComponents] = useState<ComponentResponse[]>([]);
  const [loadingDefComponents, setLoadingDefComponents] = useState(false);
  const [defTypeFilter, setDefTypeFilter] = useState<FilterType>("All");
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [expandedCompIds, setExpandedCompIds] = useState<Set<string>>(
    new Set(),
  );

  // Tag-based filtering state
  const [selectedGroupTags, setSelectedGroupTags] = useState<Set<string>>(
    new Set(),
  );
  const [selectedTypeTags, setSelectedTypeTags] = useState<Set<string>>(
    new Set(),
  );
  const [detailFilterQuery, setDetailFilterQuery] = useState("");
  const [groupFilterSearch, setGroupFilterSearch] = useState("");
  const [typeFilterSearch, setTypeFilterSearch] = useState("");
  const [showAwsFilterDropdown, setShowAwsFilterDropdown] = useState(false);
  const [filterKey, setFilterKey] = useState<"Group" | "Type" | null>(null);
  const awsFilterRef = useRef<HTMLDivElement>(null);

  // Filter pills scroll state
  const filterPillsRef = useRef<HTMLDivElement>(null);
  const [filterCanScrollLeft, setFilterCanScrollLeft] = useState(false);
  const [filterCanScrollRight, setFilterCanScrollRight] = useState(false);

  const updateFilterScrollArrows = useCallback(() => {
    const el = filterPillsRef.current;
    if (!el) return;
    const sl = Math.round(el.scrollLeft);
    setFilterCanScrollLeft(sl > 0);
    setFilterCanScrollRight(sl + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    if (error) useToastStore.getState().showError("Error", error);
  }, [error]);

  useEffect(() => {
    const el = filterPillsRef.current;
    if (!el) return;
    const raf = requestAnimationFrame(updateFilterScrollArrows);
    el.addEventListener("scroll", updateFilterScrollArrows, { passive: true });
    window.addEventListener("resize", updateFilterScrollArrows);
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener("scroll", updateFilterScrollArrows);
      window.removeEventListener("resize", updateFilterScrollArrows);
    };
  }, [updateFilterScrollArrows, selectedDefinition]);

  const scrollFilterPills = (direction: "left" | "right") => {
    const el = filterPillsRef.current;
    if (!el) return;
    el.scrollBy({
      left: direction === "left" ? -150 : 150,
      behavior: "smooth",
    });
    let frame = 0;
    const poll = () => {
      updateFilterScrollArrows();
      frame++;
      if (frame < 20) requestAnimationFrame(poll);
    };
    requestAnimationFrame(poll);
  };

  // View detail state
  const [selectedComponent, setSelectedComponent] =
    useState<ComponentWithDef | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Lifecycle state
  type LifecycleStatus =
    | "draft"
    | "in_review"
    | "approved"
    | "published"
    | "rejected";
  const [lifecycleData, setLifecycleData] = useState<DocumentResponse | null>(
    null,
  );
  const [transitioningLifecycle, setTransitioningLifecycle] = useState(false);
  const [showTransitionModal, setShowTransitionModal] = useState(false);
  const [transitionTarget, setTransitionTarget] = useState("");
  const [transitionComment, setTransitionComment] = useState("");

  const lifecycle: LifecycleStatus =
    (lifecycleData?.lifecycleStatus as LifecycleStatus) || "draft";
  const isPublished = lifecycle === "published";

  const fetchLifecycle = async (documentId: string) => {
    try {
      const res = await getDocument(documentId);
      setLifecycleData(res.data);
    } catch {
      setLifecycleData(null);
    }
  };

  const handleLifecycleTransition = async () => {
    if (!selectedDefinition?.documentId || !transitionTarget) return;
    setTransitioningLifecycle(true);
    try {
      await transitionDocument(selectedDefinition.documentId, {
        targetStatus: transitionTarget,
        comments: transitionComment || undefined,
      });
      await fetchLifecycle(selectedDefinition.documentId);
      await fetchDefinitions();
      setShowTransitionModal(false);
      setTransitionTarget("");
      setTransitionComment("");
      useToastStore
        .getState()
        .showSuccess(
          "Success",
          `Status changed to "${transitionTarget.replace(/_/g, " ")}"`,
        );
    } catch (err) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error ? err.message : "Failed to transition lifecycle",
        );
    } finally {
      setTransitioningLifecycle(false);
    }
  };

  const lifecycleActions: Record<
    string,
    { label: string; target: string; color: string; icon: React.ReactNode }[]
  > = {
    draft: [
      {
        label: "Submit for Review",
        target: "in_review",
        color: "bg-blue-600 hover:bg-blue-700 shadow-blue-500/20",
        icon: <Send className="size-3.5" />,
      },
    ],
    in_review: [
      {
        label: "Approve",
        target: "approved",
        color: "bg-emerald-600 hover:bg-emerald-700 shadow-emerald-500/20",
        icon: <CheckCircle className="size-3.5" />,
      },
      {
        label: "Reject",
        target: "draft",
        color: "bg-red-600 hover:bg-red-700 shadow-red-500/20",
        icon: <XCircle className="size-3.5" />,
      },
    ],
    approved: [
      {
        label: "Publish",
        target: "published",
        color: "bg-indigo-600 hover:bg-indigo-700 shadow-indigo-500/20",
        icon: <Globe className="size-3.5" />,
      },
    ],
    published: [],
    rejected: [
      {
        label: "Submit for Review",
        target: "in_review",
        color: "bg-blue-600 hover:bg-blue-700 shadow-blue-500/20",
        icon: <Send className="size-3.5" />,
      },
    ],
  };

  const hasUpdatePermission = useRBACPermission("component", "update");
  const hasApprovePermission = useRBACPermission("component", "approve");
  const hasPublishPermission = useRBACPermission("component", "publish");
  const currentLifecycleActions = (lifecycleActions[lifecycle] ?? []).filter(
    (a) => {
      // Show "Submit for Review" only if user has update permission
      if (a.label === "Submit for Review") return hasUpdatePermission;
      // Show "Approve" and "Reject" only if user has approve permission
      if (["Approve", "Reject"].includes(a.label)) return hasApprovePermission;
      // Show "Publish" only if user has publish permission
      if (a.label === "Publish") return hasPublishPermission;
      // Show "Supersede" and "Withdraw" only if user has update permission
      if (["Supersede", "Withdraw"].includes(a.label))
        return hasUpdatePermission;
      return true;
    },
  );

  const openTransitionModal = (target: string) => {
    setTransitionTarget(target);
    setTransitionComment("");
    setShowTransitionModal(true);
  };

  // Delete state
  const [deleteTarget, setDeleteTarget] = useState<ComponentWithDef | null>(
    null,
  );
  const [pendingRemoveComp, setPendingRemoveComp] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Card menu state
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  // Add Control Implementation modal state
  const [ciTargetComponent, setCiTargetComponent] =
    useState<ComponentWithDef | null>(null);
  const [showAddCI, setShowAddCI] = useState(false);
  const [ciMode, setCiMode] = useState<"add" | "edit">("add");
  const [editingCiId, setEditingCiId] = useState<string | null>(null);
  const [ciSelectedIdx, setCiSelectedIdx] = useState<number | null>(null);
  const [ciControlSearch, setCiControlSearch] = useState("");
  const [addingCI, setAddingCI] = useState(false);
  const [ciSelectAllLoading, setCISelectAllLoading] = useState(false);
  const [ciForm, setCIForm] = useState<{
    catalogId: string;
    description: string;
    setParameters: { paramId: string; values: string; remarks: string }[];
    requirements: {
      controlId: string;
      description: string;
      setParameters: { paramId: string; values: string; remarks: string }[];
      statements: {
        statementId: string;
        description: string;
        sortOrder: number;
      }[];
    }[];
  }>({
    catalogId: "",
    description: "",
    setParameters: [],
    requirements: [
      { controlId: "", description: "", setParameters: [], statements: [] },
    ],
  });

  // Catalog & control data from API
  const [catalogs, setCatalogs] = useState<CatalogResponse[]>([]);
  const [catalogControls, setCatalogControls] = useState<ControlResponse[]>([]);
  const [loadingCatalogs, setLoadingCatalogs] = useState(false);
  const [loadingControls, setLoadingControls] = useState(false);

  // Per-requirement control detail (fetched from catalog)
  const [ciControlDetails, setCiControlDetails] = useState<
    Record<number, ControlResponse | null>
  >({});
  const [ciLoadingDetails, setCiLoadingDetails] = useState<
    Record<number, boolean>
  >({});

  // Add Capability modal state
  const [showAddCap, setShowAddCap] = useState(false);
  const [addingCap, setAddingCap] = useState(false);
  const [capForm, setCapForm] = useState({
    name: "",
    description: "",
  });
  // Which definition to add capability to (used in list view)
  const [capTargetDefId, setCapTargetDefId] = useState<string | null>(null);

  // Search state (frontend filtering)
  const [searchQuery, setSearchQuery] = useState("");
  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(0);
  };

  // Status filter state (frontend filtering by lifecycle status)
  const [filterStatus, setFilterStatus] = useState("");
  const handleStatusFilterChange = (status: string) => {
    setFilterStatus(status);
    setCurrentPage(0);
  };

  const resetDetailFilterState = useCallback(() => {
    setSelectedGroupTags(new Set());
    setSelectedTypeTags(new Set());
    setDetailFilterQuery("");
    setGroupFilterSearch("");
    setTypeFilterSearch("");
    setFilterKey(null);
    setShowAwsFilterDropdown(false);
  }, []);

  const handleCompSort = (key: string) => {
    if (compSortKey === key)
      setCompSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setCompSortKey(key);
      setCompSortDir("asc");
    }
    setCurrentPage(0);
  };

  /* ── OSCAL Import pipeline state ── */
  const [showImport, setShowImport] = useState(false);
  const [importStep, setImportStep] = useState<
    | "select"
    | "url"
    | "validating"
    | "validated"
    | "importing"
    | "findings"
    | "committed"
  >("select");
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importUrl, setImportUrl] = useState("");
  const [importMode, setImportMode] = useState<"full" | "delta">("full");
  const [importError, setImportError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<ImportJobResponse | null>(
    null,
  );
  const [validateResult, setValidateResult] = useState<ValidateResponse | null>(
    null,
  );
  const [validating, setValidating] = useState(false);

  /* ── Visualizer preview state ── */
  const [showPreview, setShowPreview] = useState(false);
  const loadVisualizerJson = useVisualizerStore((s) => s.loadJson);
  const resetVisualizer = useVisualizerStore((s) => s.reset);

  /* ── Import Session modal state ── */
  const [showSession, setShowSession] = useState(false);
  const [sessionData, setSessionData] = useState<ImportSessionResponse | null>(
    null,
  );
  const [sessionLoading, setSessionLoading] = useState(false);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [committing, setCommitting] = useState(false);
  const [aborting, setAborting] = useState(false);

  /* ── Organization state ── */
  const [accessibleOrgs, setAccessibleOrgs] = useState<any[]>([]);
  const [accessibleOrgsLoading, setAccessibleOrgsLoading] = useState(false);
  const [importOrgId, setImportOrgId] = useState("");
  const [wizardOrgId, setWizardOrgId] = useState("");

  /* ── Fetch accessible organizations ── */
  const fetchAccessibleOrgs = async (module: string, action: string) => {
    setAccessibleOrgsLoading(true);
    try {
      const res = await getAPI(
        `${API_URL.ACCESSIBLE_ORGS}?module=${module}&action=${action}&org_type=ORGANIZATION`,
      );
      const orgs = res?.data?.organizations ?? [];
      setAccessibleOrgs(orgs);
      if (orgs.length === 1) {
        setImportOrgId(orgs[0].id);
      }
    } catch {
      setAccessibleOrgs([]);
    } finally {
      setAccessibleOrgsLoading(false);
    }
  };

  /* ── Import handlers ── */

  const startValidation = async () => {
    if (!importFile && !importUrl.trim()) return;
    setImportStep("validating");
    setImportError(null);
    setValidateResult(null);

    if (!importFile) {
      await runImport();
      return;
    }

    try {
      const res = await validateImport(importFile);
      if (res.data.documentType.toLowerCase() !== "component") {
        setImportError(
          `Expected a component-definition document but got "${res.data.documentType}"`,
        );
        setImportStep("findings");
        return;
      }
      setValidateResult(res.data);
      setImportStep("validated");
    } catch (err: unknown) {
      setImportError(err instanceof Error ? err.message : "Validation failed");
      setImportStep("findings");
    }
  };

  const runImport = async () => {
    setImportStep("importing");
    setImportError(null);
    try {
      let res;
      if (importFile) {
        res = await importCatalog(
          importFile,
          importMode,
          importOrgId || undefined,
        );
      } else {
        res = await importCatalogFromUrl(
          importUrl.trim(),
          importOrgId || undefined,
        );
      }
      setImportResult(res.data);
      setShowImport(false);
      openSessionModal(res.data.id);
    } catch (err: unknown) {
      setImportError(err instanceof Error ? err.message : "Import failed");
      setImportStep("findings");
    }
  };

  const openSessionModal = async (sessionId: string) => {
    setShowSession(true);
    setSessionData(null);
    setSessionError(null);
    setSessionLoading(true);
    setCommitting(false);
    setAborting(false);
    try {
      const res = await getImportSession(sessionId);
      setSessionData(res.data);
    } catch (err: unknown) {
      setSessionError(
        err instanceof Error ? err.message : "Failed to fetch session",
      );
    } finally {
      setSessionLoading(false);
    }
  };

  useEffect(() => {
    if (!showSession || !sessionData) return;
    const status = sessionData.jobStatus.toLowerCase();
    if (status === "validated" || status === "failed" || status === "committed")
      return;
    const timer = setInterval(async () => {
      try {
        const res = await getImportSession(sessionData.id);
        setSessionData(res.data);
      } catch {
        // silently ignore polling errors
      }
    }, 10000);
    return () => clearInterval(timer);
  }, [showSession, sessionData]);

  const handleCommitSession = async () => {
    if (!sessionData) return;
    setCommitting(true);
    setSessionError(null);
    try {
      await commitImportSession(
        sessionData.id,
        accessibleOrgs.length === 1 ? accessibleOrgs[0].id : undefined,
      );
      setShowSession(false);
      setSessionData(null);
      resetImport();
      fetchDefinitions();
      useToastStore
        .getState()
        .showSuccess("Success", "Component Definition Imported Successfully");
    } catch (err: unknown) {
      setSessionError(err instanceof Error ? err.message : "Commit failed");
    } finally {
      setCommitting(false);
    }
  };

  const handleAbortSession = async () => {
    if (!sessionData) return;
    setAborting(true);
    setSessionError(null);
    try {
      await abortImportSession(sessionData.id);
      setShowSession(false);
      setSessionData(null);
      resetImport();
      fetchDefinitions();
    } catch (err: unknown) {
      setSessionError(err instanceof Error ? err.message : "Abort failed");
    } finally {
      setAborting(false);
    }
  };

  const resetImport = () => {
    setImportStep("select");
    setImportFile(null);
    setImportUrl("");
    setImportMode("full");
    setValidateResult(null);
    setValidating(false);
    setImportError(null);
    setImportResult(null);
    setImportOrgId("");
  };

  /* ── Export handler ── */
  const [exporting, setExporting] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportTargetDocId, setExportTargetDocId] = useState<string | null>(
    null,
  );

  const openExportModal = (documentId: string) => {
    setExportTargetDocId(documentId);
    setShowExportModal(true);
  };

  const handleExport = async (format: "json" | "xml" | "yaml") => {
    if (!exportTargetDocId) return;
    setExporting(true);
    setShowExportModal(false);
    try {
      const definition = definitions.find(
        (d) => d.documentId === exportTargetDocId,
      );
      const componentName =
        definition?.components?.[0]?.name ||
        definition?.id?.substring(0, 20) ||
        "component-definition";
      const res = await exportCatalogDoc(exportTargetDocId, format);
      let job = res.data;
      const getJobId = (j: typeof job) => j.jobId || j.id || "";
      const jobId = getJobId(job);

      if (job.status === "pending") {
        for (let i = 0; i < 30; i++) {
          await new Promise((r) => setTimeout(r, 2000));
          const poll = await getExportJob(jobId);
          job = poll.data;
          if (job.status !== "pending") break;
        }
      }

      if (job.status === "completed") {
        const downloadUrl = job.downloadUrl || getExportDownloadUrl(jobId);
        const downloadRes = await downloadFile(downloadUrl);
        if (!downloadRes) throw new Error("No response from download");

        // Extract documentTitle from response headers
        let documentTitle = componentName;
        const xDocumentTitle = downloadRes.headers?.["x-document-title"];
        if (xDocumentTitle) {
          documentTitle = xDocumentTitle;
        } else {
          // Try to extract from Content-Disposition header
          const contentDisposition =
            downloadRes.headers?.["content-disposition"] || "";
          const filenameMatch = contentDisposition.match(
            /filename\*?=(?:"([^"]*)"|([^;,\s]*))/,
          );
          if (filenameMatch && (filenameMatch[1] || filenameMatch[2])) {
            const filename = (filenameMatch[1] || filenameMatch[2]).split(
              ".",
            )[0];
            documentTitle = filename;
          }
        }

        // Get MIME type from API response Content-Type header first
        let mimeType = downloadRes.headers?.["content-type"] || "";
        if (!mimeType) {
          // Fallback: use selected format
          const mimeTypes: Record<string, string> = {
            json: "application/json",
            xml: "application/xml",
            yaml: "application/yaml",
          };
          mimeType = mimeTypes[format] || "application/octet-stream";
        }
        const blob = new Blob([downloadRes.data], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${documentTitle}.${format}`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
        useToastStore
          .getState()
          .showSuccess("Success", "Export ready - download started");
      } else if (job.status === "failed") {
        useToastStore.getState().showError("Error", "Export failed");
      } else {
        useToastStore
          .getState()
          .showError("Error", "Export timed out - please try again");
      }
    } catch (err: unknown) {
      const errorMsg = err instanceof Error ? err.message : "Export failed";
      useToastStore.getState().showError("Error", errorMsg);
    } finally {
      setExporting(false);
      setExportTargetDocId(null);
    }
  };

  // Fetch all definitions once (client-side pagination + search)
  const fetchDefinitions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const summaryRes = await listComponentDefinitions();
      const summaries: ComponentDefinitionSummaryResponse[] = Array.isArray(
        summaryRes?.data,
      )
        ? summaryRes.data
        : [];

      // Build title map and definitions from summaries - no extra API calls
      const titleMap: Record<string, string> = {};
      const countsMap: Record<
        string,
        {
          componentCount: number;
          controlCount: number;
          lifecycleStatus: string;
        }
      > = {};
      const defs: ComponentDefinitionResponse[] = summaries.map((s) => {
        titleMap[s.documentId] = s.title || s.documentId;
        countsMap[s.id] = {
          componentCount: s.componentCount || 0,
          controlCount: s.controlCount || 0,
          lifecycleStatus: s.lifecycleStatus || "draft",
        };
        return {
          id: s.id,
          documentId: s.documentId,
          components: [],
          capabilities: [],
          createdAt: s.createdAt,
          updatedAt: s.updatedAt,
        };
      });
      setDocTitles(titleMap);
      setDefCounts(countsMap);
      setDefinitions(defs);
      setTotalDefinitions(defs.length);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to load component definitions",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDefinitions();
  }, [fetchDefinitions]);

  /* ── Load component definition from URL parameter on mount ── */
  useEffect(() => {
    if (!componentIdFromUrl) return;

    let cancelled = false;

    const loadComponentFromUrl = async () => {
      const existingDef = definitions.find((d) => d.id === componentIdFromUrl);
      if (existingDef && !cancelled) {
        setSelectedDefinition(existingDef);
        await fetchDefComponents(componentIdFromUrl);
      } else if (!cancelled) {
        // Directly fetch if not in list yet
        try {
          let compDef;
          try {
            const res = await getComponentDefinition(componentIdFromUrl);
            compDef = res.data;
          } catch (fetchErr) {
            // Fallback for view-only users: try to find in definitions list
            const foundDef = definitions.find(
              (d) => d.id === componentIdFromUrl,
            );
            if (foundDef && !cancelled) {
              setSelectedDefinition(foundDef);
              await fetchDefComponents(componentIdFromUrl);
              return;
            }
            throw fetchErr;
          }

          if (!cancelled) {
            setSelectedDefinition(compDef);
            await fetchDefComponents(componentIdFromUrl);
          }
        } catch (err) {
          console.error("Failed to load component definition:", err);
        }
      }
    };

    loadComponentFromUrl();
    return () => {
      cancelled = true;
    };
  }, [componentIdFromUrl, definitions]);

  const handlePageSizeChange = (ps: number) => {
    setPageSize(ps);
    setCurrentPage(0);
  };

  // Close AWS filter dropdown when clicking outside
  useEffect(() => {
    if (!showAwsFilterDropdown) return;
    const handleClick = (e: MouseEvent) => {
      if (
        awsFilterRef.current &&
        !awsFilterRef.current.contains(e.target as Node)
      ) {
        setShowAwsFilterDropdown(false);
        setFilterKey(null);
        setGroupFilterSearch("");
        setTypeFilterSearch("");
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showAwsFilterDropdown]);

  // Close menu when clicking outside
  useEffect(() => {
    if (!openMenuId) return;
    const close = () => setOpenMenuId(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [openMenuId]);

  // Flatten all components from all definitions
  const allComponents: ComponentWithDef[] = definitions.flatMap((def) =>
    (def.components || []).map((c) => ({ ...c, definitionId: def.id })),
  );

  // Frontend search: filter definitions by query matching doc title or component titles/types
  const searchFilteredDefinitions = definitions.filter((def) => {
    // Status filter (by lifecycle status)
    if (filterStatus) {
      const lc = String(defCounts[(def as any).id]?.lifecycleStatus || "draft");
      if (lc !== filterStatus) return false;
    }
    // Search filter
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    const docTitle = (docTitles[def.documentId] || "").toLowerCase();
    if (docTitle.includes(q)) return true;
    return def.components.some(
      (c) =>
        c.title.toLowerCase().includes(q) ||
        c.componentType.toLowerCase().includes(q) ||
        (c.description || "").toLowerCase().includes(q),
    );
  });

  // Client-side sorting on filtered results
  const sortedDefinitions = [...searchFilteredDefinitions].sort((a, b) => {
    if (!compSortKey) return 0;
    if (compSortKey === "name") {
      const aVal =
        docTitles[(a as any).documentId] || (a as any).documentId || "";
      const bVal =
        docTitles[(b as any).documentId] || (b as any).documentId || "";
      return compSortDir === "asc"
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }
    if (compSortKey === "components") {
      const aVal = defCounts[(a as any).id]?.componentCount ?? 0;
      const bVal = defCounts[(b as any).id]?.componentCount ?? 0;
      return compSortDir === "asc" ? aVal - bVal : bVal - aVal;
    }
    if (compSortKey === "controls") {
      const aVal = defCounts[(a as any).id]?.controlCount ?? 0;
      const bVal = defCounts[(b as any).id]?.controlCount ?? 0;
      return compSortDir === "asc" ? aVal - bVal : bVal - aVal;
    }
    if (compSortKey === "status") {
      const aVal = String(defCounts[(a as any).id]?.lifecycleStatus || "");
      const bVal = String(defCounts[(b as any).id]?.lifecycleStatus || "");
      return compSortDir === "asc"
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }
    if (compSortKey === "updated") {
      const aVal = (a as any).updatedAt || "";
      const bVal = (b as any).updatedAt || "";
      return compSortDir === "asc"
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }
    return 0;
  });

  // Client-side pagination on filtered results
  const paginatedDefinitions = sortedDefinitions.slice(
    currentPage * pageSize,
    (currentPage + 1) * pageSize,
  );

  const baseComponents = allComponents;

  const filtered =
    activeFilter === "All"
      ? baseComponents
      : baseComponents.filter((c) => c.componentType === activeFilter);

  // Stats
  const totalComponents = allComponents.length;
  const totalCapabilities = definitions.reduce(
    (acc, d) => acc + d.capabilities.length,
    0,
  );

  // Wizard reset
  const resetWizard = () => {
    setWizardStep("title");
    setCreatedDefId(null);
    setCreatedDefTitle("");
    setCreatedComponents([]);
    setCompForm({ title: "", componentType: "", description: "", purpose: "" });
    setWizCatalogs([]);
    setWizSelectedCatalogId("");
    setWizControls([]);
    setWizControlDetails({});
    setWizComponentControls({});
    setWizActiveComponentId(null);
    setWizardError(null);
    setWizardOrgId("");
  };

  // Reset the component form fields for next component
  const resetCompFields = () => {
    setCompForm((f) => ({
      ...f,
      title: "",
      componentType: "",
      description: "",
      purpose: "",
      props: [{ name: "Group Name", value: "" }],
    }));
  };

  // Step: Title - Create Component Definition via API
  const handleWizardCreateDef = async () => {
    if (!compForm.title) return;
    setStepLoading(true);
    setWizardError(null);
    try {
      const owningOrgUnitId =
        accessibleOrgs.length === 1
          ? accessibleOrgs[0].id
          : wizardOrgId || undefined;
      const defRes = await createComponentDefinition({
        title: compForm.title,
        owningOrgUnitId,
      });
      setCreatedDefId(defRes.data.id);
      setCreatedDefTitle(compForm.title);
      resetCompFields();
      setWizardStep("components");
    } catch (err) {
      setWizardError(
        err instanceof Error
          ? err.message
          : "Failed to create component definition",
      );
    } finally {
      setStepLoading(false);
    }
  };

  // Step: Components - Add a single Component via API (stays on same step for adding more)
  const handleAddOneComponent = async () => {
    if (
      !createdDefId ||
      !compForm.title.trim() ||
      !compForm.componentType ||
      !compForm.purpose.trim()
    )
      return;
    setStepLoading(true);
    setWizardError(null);
    try {
      const groupProps = compForm.props
        .filter((p) => p.value.trim())
        .map((p) => ({ name: "Group Name", value: p.value }));
      const compRes = await addComponent(createdDefId, {
        componentType: compForm.componentType as ComponentType,
        title: compForm.title || compForm.componentType,
        description: compForm.description || undefined,
        purpose: compForm.purpose || undefined,
        props: groupProps.length > 0 ? groupProps : undefined,
        sortOrder: createdComponents.length,
      });
      const newId = compRes.data.id;
      setCreatedComponents((prev) => [
        ...prev,
        {
          id: newId,
          title: compForm.title || compForm.componentType,
          componentType: compForm.componentType as ComponentType,
          description: compForm.description || "",
          purpose: compForm.purpose || "",
        },
      ]);
      resetCompFields();
      // Auto-expand the control mapping panel for the newly added component
      handleExpandComponentControls(newId);
    } catch (err) {
      setWizardError(
        err instanceof Error ? err.message : "Failed to add component",
      );
    } finally {
      setStepLoading(false);
    }
  };

  // Remove a component from the definition
  const handleRemoveComponent = async (componentId: string) => {
    if (!createdDefId) return;
    setStepLoading(true);
    setWizardError(null);
    try {
      await deleteComponent(createdDefId, componentId);
      setCreatedComponents((prev) => prev.filter((c) => c.id !== componentId));
    } catch (err) {
      setWizardError(
        err instanceof Error ? err.message : "Failed to remove component",
      );
    } finally {
      setStepLoading(false);
    }
  };

  // Components → Controls: Save pending form data (if any) then advance to control mapping
  const handleComponentsContinue = async () => {
    // If the form has data, save it first before advancing
    if (createdDefId && compForm.componentType) {
      setStepLoading(true);
      setWizardError(null);
      try {
        const groupProps2 = compForm.props
          .filter((p) => p.value.trim())
          .map((p) => ({ name: "Group Name", value: p.value }));
        const compRes = await addComponent(createdDefId, {
          componentType: compForm.componentType as ComponentType,
          title: compForm.title || compForm.componentType,
          description: compForm.description || undefined,
          purpose: compForm.purpose || undefined,
          props: groupProps2.length > 0 ? groupProps2 : undefined,
          sortOrder: createdComponents.length,
        });
        setCreatedComponents((prev) => [
          ...prev,
          {
            id: compRes.data.id,
            title: compForm.title || compForm.componentType,
            componentType: compForm.componentType as ComponentType,
            description: compForm.description || "",
            purpose: compForm.purpose || "",
          },
        ]);
        resetCompFields();
      } catch (err) {
        setWizardError(
          err instanceof Error ? err.message : "Failed to add component",
        );
        setStepLoading(false);
        return;
      } finally {
        setStepLoading(false);
      }
    }
    // Need at least one component to proceed
    if (createdComponents.length === 0 && !compForm.componentType) return;
    // Save controls and go to review
    await handleWizardSaveControls();
  };

  // Toggle control mapping panel for a component and lazy-load catalogs
  const handleExpandComponentControls = async (componentId: string) => {
    if (wizActiveComponentId === componentId) {
      setWizActiveComponentId(null);
      return;
    }
    setWizActiveComponentId(componentId);
    // Lazy-load catalogs if not already loaded
    if (wizCatalogs.length === 0) {
      setWizLoadingCatalogs(true);
      try {
        const catRes = await listCatalogs();
        setWizCatalogs(
          catRes.data.filter((p) => p.lifecycleStatus === "published"),
        );
      } catch {
        /* non-blocking */
      }
      setWizLoadingCatalogs(false);
    }
  };

  // Controls step helper: Load controls when a catalog is selected
  const handleWizCatalogChange = async (catalogId: string) => {
    setWizSelectedCatalogId(catalogId);
    setWizControls([]);
    setWizControlDetails({});
    if (!catalogId) return;
    setWizLoadingControls(true);
    try {
      // Fetch full catalog with controls nested in groups
      const res = await getCatalog(catalogId);
      // Flatten controls from nested groups structure
      const flatControls: ControlResponse[] = [];
      const extractControls = (groups?: GroupResponse[]) => {
        if (groups) {
          groups.forEach((group) => {
            if (group.controls) {
              flatControls.push(...group.controls);
            }
            // Recursively extract from nested groups
            if (group.groups && group.groups.length > 0) {
              extractControls(group.groups);
            }
          });
        }
      };
      extractControls(res.data.groups);
      setWizControls(flatControls);
    } catch (err) {
      setWizardError(
        err instanceof Error ? err.message : "Failed to load controls",
      );
    } finally {
      setWizLoadingControls(false);
    }
  };

  // Step 3 helper: Toggle a control selection for a specific component
  const toggleWizControl = async (
    componentId: string,
    ctrl: ControlResponse,
  ) => {
    const compControls = wizComponentControls[componentId] || [];
    const exists = compControls.find((c) => c.controlId === ctrl.controlId);

    // If already selected, just deselect
    if (exists) {
      setWizComponentControls((prev) => ({
        ...prev,
        [componentId]: (prev[componentId] || []).filter(
          (c) => c.controlId !== ctrl.controlId,
        ),
      }));
      return;
    }

    // Extract control detail from already-loaded wizControls (from catalog API)
    const detail = wizControls.find((c) => c.controlId === ctrl.controlId);
    const catalogId = wizSelectedCatalogId;

    // Pre-populate setParameters from catalog control details
    const params =
      detail?.params?.map((p) => ({
        paramId: p.paramId,
        values: p.values?.join(", ") ?? "",
      })) ?? [];

    setWizComponentControls((prev) => ({
      ...prev,
      [componentId]: [
        ...(prev[componentId] || []),
        {
          controlId: ctrl.controlId,
          title: detail?.title || ctrl.title,
          description: "",
          catalogId,
          setParameters: params,
        },
      ],
    }));
  };

  // Select All / Deselect All controls for a component
  const selectAllWizControls = async (componentId: string) => {
    const compControls = wizComponentControls[componentId] || [];
    const allSelected =
      wizControls.length > 0 && compControls.length === wizControls.length;

    if (allSelected) {
      // Deselect all
      setWizComponentControls((prev) => ({
        ...prev,
        [componentId]: [],
      }));
      return;
    }

    // Select all: fetch details for controls in parallel, then add all
    setWizLoadingDetails(true);
    try {
      const catalogId = wizSelectedCatalogId;

      // Use control details already loaded from catalog API
      const fetchPromises = wizControls.map(async (ctrl) => {
        // Detail is already available from catalog API in wizControls
        const detail = ctrl;

        const params =
          detail?.params?.map((p) => ({
            paramId: p.paramId,
            values: p.values?.join(", ") ?? "",
          })) ?? [];

        return {
          controlId: ctrl.controlId,
          title: detail?.title || ctrl.title,
          description: "",
          catalogId,
          setParameters: params,
        };
      });

      // Wait for all API calls to complete
      const newEntries = await Promise.all(fetchPromises);

      setWizComponentControls((prev) => ({
        ...prev,
        [componentId]: newEntries,
      }));
    } finally {
      setWizLoadingDetails(false);
    }
  };

  // Step 3 helper: Update a parameter value for a selected control on a specific component
  const updateWizControlParam = (
    componentId: string,
    controlId: string,
    paramId: string,
    values: string,
  ) => {
    setWizComponentControls((prev) => ({
      ...prev,
      [componentId]: (prev[componentId] || []).map((c) =>
        c.controlId === controlId
          ? {
            ...c,
            setParameters: c.setParameters.map((p) =>
              p.paramId === paramId ? { ...p, values } : p,
            ),
          }
          : c,
      ),
    }));
  };

  // Step 3 helper: Update description for a selected control on a specific component
  const updateWizControlDesc = (
    componentId: string,
    controlId: string,
    description: string,
  ) => {
    setWizComponentControls((prev) => ({
      ...prev,
      [componentId]: (prev[componentId] || []).map((c) =>
        c.controlId === controlId ? { ...c, description } : c,
      ),
    }));
  };

  // Controls step: Save Control Implementations via API (per component)
  const handleWizardSaveControls = async () => {
    if (!createdDefId || createdComponents.length === 0) return;
    // Count total controls across all components
    const totalControls = Object.values(wizComponentControls).reduce(
      (sum, arr) => sum + arr.length,
      0,
    );
    // If no controls selected for any component, skip to review
    if (totalControls === 0) {
      setWizardStep("review");
      await fetchDefinitions();
      return;
    }
    setStepLoading(true);
    setWizardError(null);
    try {
      // Save control implementations per component, grouped by catalog
      for (const comp of createdComponents) {
        const compControls = wizComponentControls[comp.id] || [];
        if (compControls.length === 0) continue;
        // Group controls by their source catalog
        const byCatalog: Record<string, typeof compControls> = {};
        for (const sc of compControls) {
          (byCatalog[sc.catalogId] ??= []).push(sc);
        }
        for (const [catId, controls] of Object.entries(byCatalog)) {
          const selectedCatalog = wizSelectedCatalogId
            ? wizCatalogs.find((catalog) => catalog.id === wizSelectedCatalogId)
            : null;
          const sourceName =
            selectedCatalog?.title || selectedCatalog?.documentId || catId;
          const payload: AddControlImplementationPayload = {
            source: sourceName,
            description: `Controls implemented by ${comp.title}`,
            implementedRequirements: controls.map((sc) => {
              const fullCtrl =
                wizControlDetails[sc.controlId] ||
                wizControls.find((c) => c.controlId === sc.controlId);
              const setParams = sc.setParameters
                .filter((p) => p.paramId && p.values.trim())
                .map((p) => ({
                  paramId: p.paramId,
                  values: p.values
                    .split(",")
                    .map((v) => v.trim())
                    .filter(Boolean),
                }));
              return {
                controlId: sc.controlId,
                description: sc.description || undefined,
                ...(setParams.length > 0 ? { setParameters: setParams } : {}),
                statements:
                  fullCtrl?.parts
                    ?.filter((p) => p.name === "statement")
                    .map((p, idx) => ({
                      statementId: `${sc.controlId}_smt.${String.fromCharCode(97 + idx)}`,
                      description: p.prose,
                      sortOrder: idx + 1,
                    })) || [],
              };
            }),
          };
          await addControlImplementation(createdDefId, comp.id, payload);
        }
      }
      setWizardStep("review");
      await fetchDefinitions();
    } catch (err) {
      setWizardError(
        err instanceof Error
          ? err.message
          : "Failed to save control implementations",
      );
    } finally {
      setStepLoading(false);
    }
  };

  // Wizard done - close and refresh
  const handleWizardDone = async () => {
    setShowWizard(false);
    resetWizard();
    await fetchDefinitions();
  };

  const currentStepIndex = wizardSteps.findIndex((s) => s.key === wizardStep);

  // Delete handler
  const deleteTargetRef = useRef<ComponentWithDef | null>(null);
  // Keep ref in sync with state so async handler always has the value
  deleteTargetRef.current = deleteTarget;

  const handleDelete = async () => {
    const target = deleteTargetRef.current;
    if (!target) return;
    setDeleting(true);
    try {
      await deleteComponentDefinition(
        target.definitionId,
        "Deleted from Component Library",
      );
      useToastStore
        .getState()
        .showSuccess("Success", "Component definition deleted");
      setDeleteTarget(null);
      if (selectedComponent?.definitionId === target.definitionId) {
        setSelectedComponent(null);
      }
      if (selectedDefinition?.id === target.definitionId) {
        setSelectedDefinition(null);
      }
      await fetchDefinitions();
    } catch (err) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error
            ? err.message
            : "Failed to delete component definition",
        );
    } finally {
      setDeleting(false);
    }
  };

  // All components for the selected definition (unfiltered)
  const [allDefComponents, setAllDefComponents] = useState<ComponentResponse[]>(
    [],
  );

  // Fetch all components for a definition (no type param - filter client-side)
  const fetchDefComponents = async (defId: string) => {
    setLoadingDefComponents(true);
    try {
      const res = await listComponents(defId);
      setAllDefComponents(res.data);
      setDefComponents(res.data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load components",
      );
    } finally {
      setLoadingDefComponents(false);
    }
  };

  const handleSelectDefinition = async (def: ComponentDefinitionResponse) => {
    navigate(`/components/${def.id}`);
    setSelectedComponent(null);
    setDefTypeFilter("All");
    resetDetailFilterState();
    // Fetch full definition detail (with components) on click
    try {
      const fullDef = await getComponentDefinition(def.id);
      setSelectedDefinition(fullDef.data);
      // Update the definition in the list so allComponents stays in sync
      setDefinitions((prev) =>
        prev.map((d) => (d.id === def.id ? fullDef.data : d)),
      );
      setAllDefComponents(fullDef.data.components || []);
      setDefComponents(fullDef.data.components || []);
    } catch {
      setSelectedDefinition(def);
      await fetchDefComponents(def.id);
    }
    // Fetch lifecycle status
    fetchLifecycle(def.documentId);
  };

  const handleDefTypeFilterChange = (filter: FilterType) => {
    setDefTypeFilter(filter);
    if (filter === "All") {
      setDefComponents(allDefComponents);
    } else {
      setDefComponents(
        allDefComponents.filter((c) => c.componentType === filter),
      );
    }
  };

  // Add a component to the selected definition (from definition detail view)
  const [showDefAddComp, setShowDefAddComp] = useState(false);
  const [defCompForm, setDefCompForm] = useState({
    title: "",
    componentType: "" as ComponentType | "",
    description: "",
    purpose: "",
    groupName: "",
  });
  const [addingDefComp, setAddingDefComp] = useState(false);

  const handleAddDefComponent = async () => {
    if (
      !selectedDefinition ||
      !defCompForm.title.trim() ||
      !defCompForm.componentType ||
      !defCompForm.purpose.trim()
    )
      return;
    setAddingDefComp(true);
    try {
      await addComponent(selectedDefinition.id, {
        componentType: defCompForm.componentType as ComponentType,
        title: defCompForm.title || defCompForm.componentType,
        description: defCompForm.description || undefined,
        purpose: defCompForm.purpose || undefined,
        props: defCompForm.groupName.trim()
          ? [{ name: "Group Name", value: defCompForm.groupName.trim() }]
          : undefined,
        sortOrder: defComponents.length,
      });
      // Refresh components list then re-apply current filter
      await fetchDefComponents(selectedDefinition.id);
      if (defTypeFilter !== "All") {
        handleDefTypeFilterChange(defTypeFilter);
      }
      setDefCompForm({
        title: "",
        componentType: "",
        description: "",
        purpose: "",
        groupName: "",
      });
      setShowDefAddComp(false);
      await fetchDefinitions();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add component");
    } finally {
      setAddingDefComp(false);
    }
  };

  // Remove a component from the selected definition
  const handleRemoveDefComponent = async (componentId: string) => {
    if (!selectedDefinition) return;
    try {
      await deleteComponent(selectedDefinition.id, componentId);
      setDefComponents((prev) => prev.filter((c) => c.id !== componentId));
      setAllDefComponents((prev) => prev.filter((c) => c.id !== componentId));
      useToastStore.getState().showSuccess("Success", "Component removed");
      await fetchDefinitions();
    } catch (err) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error ? err.message : "Failed to remove component",
        );
    }
  };

  // Fetch component details from API when clicking a component
  const handleSelectComponent = async (comp: ComponentWithDef) => {
    setLoadingDetail(true);
    setSelectedComponent(comp);
    try {
      const res = await listComponents(comp.definitionId);
      const freshComp = res.data.find((c) => c.id === comp.id);
      if (freshComp) {
        setSelectedComponent({ ...freshComp, definitionId: comp.definitionId });
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load component details",
      );
    } finally {
      setLoadingDetail(false);
    }
  };

  // Reset CI form
  const resetCIForm = () => {
    setCIForm({
      catalogId: "",
      description: "",
      setParameters: [],
      requirements: [
        { controlId: "", description: "", setParameters: [], statements: [] },
      ],
    });
    setCatalogControls([]);
  };

  // Fetch catalogs from API when modal opens
  const fetchCatalogs = useCallback(async (): Promise<CatalogResponse[]> => {
    setLoadingCatalogs(true);
    try {
      const res = await listCatalogs();
      const published = res.data.filter((p) => p.lifecycleStatus === "published");
      setCatalogs(published);
      return published;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load catalogs");
      return [];
    } finally {
      setLoadingCatalogs(false);
    }
  }, []);

  // Fetch controls when a catalog is selected
  const fetchControlsForCatalog = useCallback(
    async (catalogId: string): Promise<ControlResponse[]> => {
      if (!catalogId) {
        setCatalogControls([]);
        return [];
      }
      setLoadingControls(true);
      try {
        // Fetch full catalog with controls nested in groups
        const res = await getCatalog(catalogId);
        // Flatten controls from nested groups structure
        const flatControls: ControlResponse[] = [];
        const extractControls = (groups?: GroupResponse[]) => {
          if (groups) {
            groups.forEach((group) => {
              if (group.controls) {
                flatControls.push(...group.controls);
              }
              // Recursively extract from nested groups
              if (group.groups && group.groups.length > 0) {
                extractControls(group.groups);
              }
            });
          }
        };
        extractControls(res.data.groups);
        setCatalogControls(flatControls);
        return flatControls;
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to load controls",
        );
        return [];
      } finally {
        setLoadingControls(false);
      }
    },
    [],
  );

  // Select All / Deselect All controls for CI modal
  const ciSelectAllControls = async () => {
    const allSelected =
      catalogControls.length > 0 &&
      ciForm.requirements.filter((r) => r.controlId).length ===
      catalogControls.length;

    if (allSelected) {
      // Deselect all
      setCIForm((prev) => ({
        ...prev,
        requirements: [
          { controlId: "", description: "", setParameters: [], statements: [] },
        ],
      }));
      return;
    }

    // Select all: fetch details for controls in parallel
    setCISelectAllLoading(true);
    try {
      const catalogId = ciForm.catalogId;
      const newRequirements: typeof ciForm.requirements = [];

      // Use control details already loaded from catalog API
      const fetchPromises = catalogControls.map(async (ctrl) => {
        // Detail is already available from catalog API in catalogControls
        return {
          controlId: ctrl.controlId,
          description: "",
          setParameters:
            ctrl?.params?.map((p) => ({
              paramId: p.paramId,
              values: p.values?.join(", ") ?? "",
              remarks: "",
            })) ?? [],
          statements: [],
        };
      });

      // Wait for all API calls to complete
      const results = await Promise.all(fetchPromises);
      newRequirements.push(...results);

      setCIForm((prev) => ({
        ...prev,
        requirements:
          newRequirements.length > 0 ? newRequirements : prev.requirements,
      }));
    } finally {
      setCISelectAllLoading(false);
    }
  };

  // Add Control Implementation handler
  const handleAddControlImplementation = async () => {
    const targetComp = ciTargetComponent || selectedComponent;
    const validReqs = ciForm.requirements.filter((r) => r.controlId);
    if (!targetComp || validReqs.length === 0) return;
    setAddingCI(true);
    try {
      const selectedCatalog = catalogs.find((c) => c.id === ciForm.catalogId);

      const buildSetParams = (
        params: { paramId: string; values: string; remarks: string }[],
      ) =>
        params
          .filter((p) => p.paramId)
          .map((p) => ({
            paramId: p.paramId,
            values: p.values
              .split(",")
              .map((v) => v.trim())
              .filter(Boolean),
            ...(p.remarks ? { remarks: p.remarks } : {}),
          }));

      const topParams = buildSetParams(ciForm.setParameters);

      const payload: AddControlImplementationPayload = {
        source: selectedCatalog?.title || ciForm.catalogId,
        description: ciForm.description || undefined,
        ...(topParams.length > 0 ? { setParameters: topParams } : {}),
        implementedRequirements: validReqs.map((req) => {
          const reqParams = buildSetParams(req.setParameters);
          return {
            controlId: req.controlId,
            description: req.description || undefined,
            ...(reqParams.length > 0 ? { setParameters: reqParams } : {}),
            statements:
              req.statements.length > 0
                ? req.statements
                  .filter((s) => s.statementId)
                  .map((s, idx) => ({
                    statementId: s.statementId,
                    description: s.description || undefined,
                    sortOrder: s.sortOrder ?? idx,
                  }))
                : [],
          };
        }),
      };

      await addControlImplementation(
        targetComp.definitionId,
        targetComp.id,
        payload,
      );

      // Refresh the control implementations for this component
      const ciRes = await listControlImplementations(
        targetComp.definitionId,
        targetComp.id,
      );

      // Update selectedComponent if it's the same component (detail view)
      if (selectedComponent && selectedComponent.id === targetComp.id) {
        setSelectedComponent({
          ...selectedComponent,
          controlImplementations: ciRes.data,
        });
      }

      // Refresh the definition components list so accordion view updates
      if (selectedDefinition) {
        try {
          const compRes = await listComponents(targetComp.definitionId);
          setAllDefComponents(compRes.data);
          setDefComponents(
            defTypeFilter === "All"
              ? compRes.data
              : compRes.data.filter((c) => c.componentType === defTypeFilter),
          );
        } catch {
          // ignore refresh error
        }
      }
      await fetchDefinitions();

      useToastStore
        .getState()
        .showSuccess("Success", "Control implementation added");
      setShowAddCI(false);
      setCiTargetComponent(null);
      resetCIForm();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to add control implementation",
      );
    } finally {
      setAddingCI(false);
    }
  };

  // ── Open edit CI modal — pre-fills form from existing ControlImplementationResponse ──
  const openEditCI = async (ci: ControlImplementationResponse, comp: ComponentWithDef) => {
    resetCIForm();
    setCiMode("edit");
    setEditingCiId(ci.id);
    setCiTargetComponent(comp);

    // Try to find catalog by matching source title
    // Use the returned list directly to avoid stale closure issue
    const freshCatalogs = await fetchCatalogs();
    const matchedCatalog = freshCatalogs.find(
      (c) => c.title?.toLowerCase() === ci.source?.toLowerCase()
    );
    if (matchedCatalog) {
      await fetchControlsForCatalog(matchedCatalog.id);
    }

    setCIForm({
      catalogId: matchedCatalog?.id ?? ci.source ?? "",
      description: ci.description ?? "",
      setParameters: (ci.setParameters ?? []).map((p) => ({
        paramId: p.paramId,
        values: Array.isArray(p.values) ? p.values.join(", ") : (p.values ?? ""),
        remarks: (p as any).remarks ?? "",
      })),
      requirements: (ci.implementedRequirements ?? []).map((ir) => ({
        controlId: ir.controlId,
        description: ir.description ?? "",
        setParameters: (ir.setParameters ?? []).map((p) => ({
          paramId: p.paramId,
          values: Array.isArray(p.values) ? p.values.join(", ") : (p.values ?? ""),
          remarks: (p as any).remarks ?? "",
        })),
        statements: (ir.statements ?? []).map((s) => ({
          statementId: s.statementId,
          description: s.description ?? "",
          sortOrder: (s as any).sortOrder ?? 0,
        })),
      })),
    });
    setShowAddCI(true);
  };

  // ── Update (PUT) a control implementation ──
  const handleUpdateControlImplementation = async () => {
    const targetComp = ciTargetComponent || selectedComponent;
    if (!targetComp || !editingCiId) return;
    const validReqs = ciForm.requirements.filter((r) => r.controlId);
    if (validReqs.length === 0) return;
    setAddingCI(true);
    try {
      const selectedCatalog = catalogs.find((c) => c.id === ciForm.catalogId);

      const buildSetParams = (
        params: { paramId: string; values: string; remarks: string }[],
      ) =>
        params
          .filter((p) => p.paramId)
          .map((p) => ({
            paramId: p.paramId,
            values: p.values.split(",").map((v) => v.trim()).filter(Boolean),
            ...(p.remarks ? { remarks: p.remarks } : {}),
          }));

      const topParams = buildSetParams(ciForm.setParameters);
      const payload: AddControlImplementationPayload = {
        source: selectedCatalog?.title || ciForm.catalogId,
        description: ciForm.description || undefined,
        ...(topParams.length > 0 ? { setParameters: topParams } : {}),
        implementedRequirements: validReqs.map((req) => {
          const reqParams = buildSetParams(req.setParameters);
          return {
            controlId: req.controlId,
            description: req.description || undefined,
            ...(reqParams.length > 0 ? { setParameters: reqParams } : {}),
            statements: req.statements
              .filter((s) => s.statementId)
              .map((s, idx) => ({
                statementId: s.statementId,
                description: s.description || undefined,
                sortOrder: s.sortOrder ?? idx,
              })),
          };
        }),
      };

      const res = await updateControlImplementation(
        targetComp.definitionId,
        targetComp.id,
        editingCiId,
        payload,
      );

      // Re-bind to response IDs (IDs are regenerated on full replace)
      const ciRes = await listControlImplementations(targetComp.definitionId, targetComp.id);
      if (selectedComponent && selectedComponent.id === targetComp.id) {
        setSelectedComponent({ ...selectedComponent, controlImplementations: ciRes.data });
      }

      // Refresh the definition detail list view so the inline control list updates
      await fetchDefComponents(targetComp.definitionId);

      useToastStore.getState().showSuccess("Updated", "Control implementation updated.");
      setShowAddCI(false);
      setCiTargetComponent(null);
      setCiMode("add");
      setEditingCiId(null);
      resetCIForm();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to update control implementation";
      if (msg.includes("CMP-0006")) {
        useToastStore.getState().showError("Locked", "This definition is frozen and cannot be edited.");
      } else {
        useToastStore.getState().showError("Error", msg);
      }
    } finally {
      setAddingCI(false);
    }
  };

  // Add Capability handler
  // const handleAddCapability = async () => {
  //   const defId = selectedComponent ? selectedComponent.definitionId : capTargetDefId;
  //   if (!defId || !capForm.name) return;
  //   setAddingCap(true);
  //   try {
  //     const payload: AddCapabilityPayload = {
  //       name: capForm.name,
  //       description: capForm.description || undefined,
  //       sortOrder: 0,
  //     };

  //     await addCapability(defId, payload);

  //     // Refresh definitions to get updated capabilities
  //     await fetchDefinitions();

  //     setShowAddCap(false);
  //     setCapForm({ name: "", description: "" });
  //     setCapTargetDefId(null);
  //   } catch (err) {
  //     setError(
  //       err instanceof Error
  //         ? err.message
  //         : "Failed to add capability",
  //     );
  //   } finally {
  //     setAddingCap(false);
  //   }
  // };

  // Handle catalog selection - fetch controls for the chosen catalog
  const handleCatalogChange = (catalogId: string) => {
    setCIForm((f) => ({
      ...f,
      catalogId,
      requirements: [
        { controlId: "", description: "", setParameters: [], statements: [] },
      ],
    }));
    setCiControlDetails({});
    setCiLoadingDetails({});
    fetchControlsForCatalog(catalogId);
  };

  // CI form helpers
  const addRequirement = () => {
    setCIForm((f) => ({
      ...f,
      requirements: [
        ...f.requirements,
        { controlId: "", description: "", setParameters: [], statements: [] },
      ],
    }));
  };

  const removeRequirement = (idx: number) => {
    setCIForm((f) => ({
      ...f,
      requirements: f.requirements.filter((_, i) => i !== idx),
    }));
    setCiControlDetails((prev) => {
      const next = { ...prev };
      delete next[idx];
      return next;
    });
  };

  const updateRequirement = (
    idx: number,
    field: "controlId" | "description",
    value: string,
  ) => {
    setCIForm((f) => ({
      ...f,
      requirements: f.requirements.map((r, i) => {
        if (i !== idx) return r;
        if (field === "controlId") {
          const control = catalogControls.find((c) => c.controlId === value);
          return {
            ...r,
            controlId: value,
            description: control?.title || "",
            setParameters: [],
            statements: [],
          };
        }
        return { ...r, [field]: value };
      }),
    }));

    // Fetch control detail when controlId changes
    if (field === "controlId" && value) {
      setCiLoadingDetails((prev) => ({ ...prev, [idx]: true }));
      setCiControlDetails((prev) => ({ ...prev, [idx]: null }));

      // First, try to find in already-loaded catalogControls
      let selectedCtrl = catalogControls.find((c) => c.controlId === value);

      // If not found and we have a catalog, fetch the catalog controls if needed
      if (!selectedCtrl && ciForm.catalogId) {
        fetchControlsForCatalog(ciForm.catalogId)
          .then((fetchedControls) => {
            // Use the returned controls directly (not stale state)
            const ctrl = fetchedControls.find((c) => c.controlId === value);
            if (ctrl) {
              setCiControlDetails((prev) => ({ ...prev, [idx]: ctrl }));
              populateControlDetails(ctrl, idx);
            }
            setCiLoadingDetails((prev) => ({ ...prev, [idx]: false }));
          })
          .catch(() => {
            setCiLoadingDetails((prev) => ({ ...prev, [idx]: false }));
          });
        return;
      }

      // Use control data already loaded from catalog API
      if (selectedCtrl) {
        setCiControlDetails((prev) => ({ ...prev, [idx]: selectedCtrl }));
        populateControlDetails(selectedCtrl, idx);
        setCiLoadingDetails((prev) => ({ ...prev, [idx]: false }));
      } else {
        setCiLoadingDetails((prev) => ({ ...prev, [idx]: false }));
      }
    }
  };

  // Helper function to populate form with control details
  const populateControlDetails = (
    selectedCtrl: ControlResponse,
    idx: number,
  ) => {
    // Auto-populate parameters and statements from the control
    setCIForm((f) => ({
      ...f,
      requirements: f.requirements.map((r, i) => {
        if (i !== idx) return r;
        const newParams =
          selectedCtrl.params?.length > 0
            ? selectedCtrl.params.map((p) => ({
              paramId: p.paramId,
              values: "",
              remarks: "",
            }))
            : r.setParameters;
        const newStmts: {
          statementId: string;
          description: string;
          sortOrder: number;
        }[] = [];
        selectedCtrl.parts
          .filter((p) => p.name === "statement")
          .forEach((part) => {
            newStmts.push({
              statementId: part.partId,
              description: part.prose || "",
              sortOrder: newStmts.length,
            });
            part.parts.forEach((sub) => {
              newStmts.push({
                statementId: sub.partId,
                description: sub.prose || "",
                sortOrder: newStmts.length,
              });
            });
          });
        return {
          ...r,
          setParameters: newParams,
          statements: newStmts.length > 0 ? newStmts : r.statements,
        };
      }),
    }));
  };

  // ─── Top-level setParameters helpers ───
  const addTopLevelParam = () => {
    setCIForm((f) => ({
      ...f,
      setParameters: [
        ...f.setParameters,
        { paramId: "", values: "", remarks: "" },
      ],
    }));
  };
  const removeTopLevelParam = (idx: number) => {
    setCIForm((f) => ({
      ...f,
      setParameters: f.setParameters.filter((_, i) => i !== idx),
    }));
  };
  const updateTopLevelParam = (idx: number, field: string, value: string) => {
    setCIForm((f) => ({
      ...f,
      setParameters: f.setParameters.map((p, i) =>
        i === idx ? { ...p, [field]: value } : p,
      ),
    }));
  };

  // ─── Requirement-level setParameters helpers ───
  const addReqParam = (reqIdx: number) => {
    setCIForm((f) => ({
      ...f,
      requirements: f.requirements.map((r, i) =>
        i === reqIdx
          ? {
            ...r,
            setParameters: [
              ...r.setParameters,
              { paramId: "", values: "", remarks: "" },
            ],
          }
          : r,
      ),
    }));
  };
  const removeReqParam = (reqIdx: number, paramIdx: number) => {
    setCIForm((f) => ({
      ...f,
      requirements: f.requirements.map((r, i) =>
        i === reqIdx
          ? {
            ...r,
            setParameters: r.setParameters.filter((_, pi) => pi !== paramIdx),
          }
          : r,
      ),
    }));
  };
  const updateReqParam = (
    reqIdx: number,
    paramIdx: number,
    field: string,
    value: string,
  ) => {
    setCIForm((f) => ({
      ...f,
      requirements: f.requirements.map((r, i) =>
        i === reqIdx
          ? {
            ...r,
            setParameters: r.setParameters.map((p, pi) =>
              pi === paramIdx ? { ...p, [field]: value } : p,
            ),
          }
          : r,
      ),
    }));
  };

  // ─── Statement helpers ───
  const addStatement = (reqIdx: number) => {
    setCIForm((f) => ({
      ...f,
      requirements: f.requirements.map((r, i) =>
        i === reqIdx
          ? {
            ...r,
            statements: [
              ...r.statements,
              {
                statementId: "",
                description: "",
                sortOrder: r.statements.length,
              },
            ],
          }
          : r,
      ),
    }));
  };
  const removeStatement = (reqIdx: number, stmtIdx: number) => {
    setCIForm((f) => ({
      ...f,
      requirements: f.requirements.map((r, i) =>
        i === reqIdx
          ? { ...r, statements: r.statements.filter((_, si) => si !== stmtIdx) }
          : r,
      ),
    }));
  };
  const updateStatement = (
    reqIdx: number,
    stmtIdx: number,
    field: string,
    value: string | number,
  ) => {
    setCIForm((f) => ({
      ...f,
      requirements: f.requirements.map((r, i) =>
        i === reqIdx
          ? {
            ...r,
            statements: r.statements.map((s, si) =>
              si === stmtIdx ? { ...s, [field]: value } : s,
            ),
          }
          : r,
      ),
    }));
  };

  // ─── Add Control Implementation Modal (shared across views) ───
  const ciModalElement = (
    <Modal
      open={showAddCI}
      onClose={() => {
        setShowAddCI(false);
        setCiTargetComponent(null);
        setCiSelectedIdx(null);
        setCiControlSearch("");
        setCiMode("add");
        setEditingCiId(null);
      }}
      title={ciMode === "edit" ? "Edit Control Implementation" : "Add Control Implementation"}
      description={
        ciForm.catalogId ? undefined : "Select a catalog to load controls"
      }
      size="lg"
      footer={
        <ModalFooterButtons
          onCancel={() => {
            setShowAddCI(false);
            setCiTargetComponent(null);
            setCiSelectedIdx(null);
            setCiMode("add");
            setEditingCiId(null);
          }}
          onSubmit={ciMode === "edit" ? handleUpdateControlImplementation : handleAddControlImplementation}
          submitLabel={
            ciMode === "edit"
              ? addingCI
                ? "Saving…"
                : "Save Changes"
              : !ciForm.catalogId
                ? "Select Catalog"
                : addingCI
                  ? "Adding"
                  : `Add ${ciForm.requirements.filter((r) => r.controlId).length} Control${ciForm.requirements.filter((r) => r.controlId).length !== 1 ? "s" : ""}`
          }
          submitDisabled={
            !ciForm.catalogId ||
            !ciForm.requirements.some((r) => r.controlId) ||
            addingCI
          }
        />
      }
    >
      {/* Catalog selector */}
      {!ciForm.catalogId ? (
        <FormField
          label="Catalog"
          required
          hint="Select a catalog to load controls from"
        >
          <FormSelect
            value={ciForm.catalogId}
            onChange={(v) => {
              handleCatalogChange(v);
              setCiSelectedIdx(null);
              setCiControlSearch("");
            }}
            options={[
              {
                value: "",
                label: loadingCatalogs
                  ? "Loading catalogs"
                  : "Select a catalog",
              },
              ...catalogs.map((c) => ({
                value: c.id,
                label: c.title || c.documentId,
              })),
            ]}
          />
        </FormField>
      ) : (
        <>
          {/* Catalog header bar */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold dark:text-white text-foreground">Catalog:</span>
                <span className="text-xs font-medium text-foreground">
                  {catalogs.find((c) => c.id === ciForm.catalogId)?.title ||
                    ciForm.catalogId}
                </span>
              </div>
              <button
                type="button"
                onClick={ciSelectAllControls}
                disabled={ciSelectAllLoading || catalogControls.length === 0}
                className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-500/10 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {ciSelectAllLoading ? (
                  <Loader2 className="size-3 animate-spin" />
                ) : (
                  <div
                    className={`w-3 h-3 rounded flex items-center justify-center flex-shrink-0 transition-colors ${catalogControls.length > 0 &&
                      ciForm.requirements.filter((r) => r.controlId).length ===
                      catalogControls.length
                      ? "bg-indigo-600 dark:bg-indigo-600"
                      : "border border-indigo-400 dark:border-indigo-500"
                      }`}
                  >
                    {catalogControls.length > 0 &&
                      ciForm.requirements.filter((r) => r.controlId).length ===
                      catalogControls.length && (
                        <span className="text-white text-[8px] font-bold">
                          {"✓"}
                        </span>
                      )}
                  </div>
                )}
                {ciSelectAllLoading
                  ? "Loading"
                  : catalogControls.length > 0 &&
                    ciForm.requirements.filter((r) => r.controlId).length ===
                    catalogControls.length
                    ? "Deselect All"
                    : "Select All"}
              </button>
            </div>
            <button
              type="button"
              onClick={() => {
                handleCatalogChange("");
                setCiSelectedIdx(null);
                setCiControlSearch("");
              }}
              className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
            >
              Change
            </button>
          </div>

          {/* Split panel: controls list (left) + detail (right) */}
          <div className="flex gap-3 h-[500px]">
            {/* Left: controls list */}
            <div className="w-64 flex-shrink-0 rounded-lg border border-border overflow-hidden flex flex-col">
              {/* Search */}
              <div className="p-2 border-b border-border">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 dark:text-white text-foreground/50 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Search controls"
                    value={ciControlSearch}
                    onChange={(e) => setCiControlSearch(e.target.value)}
                    className="w-full pl-8 pr-2 py-1.5 text-xs bg-muted/50 rounded-md border-0 outline-none placeholder:d/50 focus:ring-1 focus:ring-indigo-500/30"
                  />
                </div>
              </div>
              {/* Controls list */}
              <div className="flex-1 overflow-y-auto">
                {loadingControls ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="size-4 animate-spin dark:text-white text-foreground" />
                  </div>
                ) : (
                  catalogControls
                    .filter(
                      (c) =>
                        !ciControlSearch ||
                        c.controlId
                          .toLowerCase()
                          .includes(ciControlSearch.toLowerCase()) ||
                        (c.title || "")
                          .toLowerCase()
                          .includes(ciControlSearch.toLowerCase()),
                    )
                    .map((ctrl, idx) => {
                      const isSelected = ciForm.requirements.some(
                        (r) => r.controlId === ctrl.controlId,
                      );
                      const isActive =
                        ciSelectedIdx !== null &&
                        catalogControls[ciSelectedIdx]?.controlId ===
                        ctrl.controlId;
                      const realIdx = catalogControls.findIndex(
                        (c) => c.controlId === ctrl.controlId,
                      );
                      return (
                        <button
                          key={ctrl.controlId}
                          type="button"
                          onClick={() => {
                            if (isSelected) {
                              if (isActive) {
                                // Already viewing this control — second click deselects it
                                const reqIdx = ciForm.requirements.findIndex(
                                  (r) => r.controlId === ctrl.controlId,
                                );
                                if (reqIdx >= 0) {
                                  removeRequirement(reqIdx);
                                }
                                setCiSelectedIdx(null);
                              } else {
                                // First click on a selected control: just show its details
                                setCiSelectedIdx(realIdx);
                              }
                            } else {
                              setCiSelectedIdx(realIdx);
                              // Auto-add this control as a requirement
                              const existingEmpty =
                                ciForm.requirements.findIndex(
                                  (r) => !r.controlId,
                                );
                              if (existingEmpty >= 0) {
                                updateRequirement(
                                  existingEmpty,
                                  "controlId",
                                  ctrl.controlId,
                                );
                              } else {
                                // Add requirement with control data directly
                                const newReq = {
                                  controlId: ctrl.controlId,
                                  description: ctrl.title || "",
                                  setParameters: [],
                                  statements: [],
                                };
                                const newIdx = ciForm.requirements.length;
                                setCIForm((f) => ({
                                  ...f,
                                  requirements: [...f.requirements, newReq],
                                }));

                                // Load control details for the new requirement
                                setCiLoadingDetails((prev) => ({
                                  ...prev,
                                  [newIdx]: true,
                                }));
                                setCiControlDetails((prev) => ({
                                  ...prev,
                                  [newIdx]: null,
                                }));

                                let selectedCtrl = catalogControls.find(
                                  (c) => c.controlId === ctrl.controlId,
                                );
                                if (!selectedCtrl && ciForm.catalogId) {
                                  fetchControlsForCatalog(ciForm.catalogId)
                                    .then((fetchedControls) => {
                                      selectedCtrl = fetchedControls.find(
                                        (c) => c.controlId === ctrl.controlId,
                                      );
                                      if (selectedCtrl) {
                                        setCiControlDetails((prev) => ({
                                          ...prev,
                                          [newIdx]: selectedCtrl,
                                        }));
                                        populateControlDetails(
                                          selectedCtrl,
                                          newIdx,
                                        );
                                      }
                                      setCiLoadingDetails((prev) => ({
                                        ...prev,
                                        [newIdx]: false,
                                      }));
                                    })
                                    .catch(() => {
                                      setCiLoadingDetails((prev) => ({
                                        ...prev,
                                        [newIdx]: false,
                                      }));
                                    });
                                } else if (selectedCtrl) {
                                  setCiControlDetails((prev) => ({
                                    ...prev,
                                    [newIdx]: selectedCtrl,
                                  }));
                                  populateControlDetails(selectedCtrl, newIdx);
                                  setCiLoadingDetails((prev) => ({
                                    ...prev,
                                    [newIdx]: false,
                                  }));
                                } else {
                                  setCiLoadingDetails((prev) => ({
                                    ...prev,
                                    [newIdx]: false,
                                  }));
                                }
                              }
                            }
                          }}
                          className={`w-full text-left px-3 py-2.5 border-b border-border transition-colors ${isActive
                            ? "bg-indigo-50 dark:bg-indigo-500/10"
                            : "hover:bg-muted/50"
                            }`}
                        >
                          <div className="flex items-center gap-2">
                            {isSelected && (
                              <CheckCircle className="size-3 text-emerald-500 flex-shrink-0" />
                            )}
                            <span
                              className={`text-[10px] font-black ${isActive ? "text-indigo-600 dark:text-indigo-400" : "d"}`}
                            >
                              {ctrl.controlId?.toUpperCase()}
                            </span>
                          </div>
                          <p className="text-[11px] dark:text-white text-foreground mt-0.5 line-clamp-1">
                            {ctrl.title || ctrl.controlId?.toUpperCase()}
                          </p>
                        </button>
                      );
                    })
                )}
              </div>
              <div className="px-3 py-2 border-t border-border bg-muted/30">
                <span className="text-[10px] dark:text-white text-foreground">
                  {ciForm.requirements.filter((r) => r.controlId).length}{" "}
                  selected
                </span>
              </div>
            </div>

            {/* Right: control detail */}
            <div className="flex-1 rounded-lg border border-border overflow-y-auto max-h-[600px]">
              {ciSelectedIdx !== null && catalogControls[ciSelectedIdx] ? (
                (() => {
                  const ctrl = catalogControls[ciSelectedIdx];
                  const reqIdx = ciForm.requirements.findIndex(
                    (r) => r.controlId === ctrl.controlId,
                  );
                  const req = reqIdx >= 0 ? ciForm.requirements[reqIdx] : null;
                  const detail = ciControlDetails[reqIdx];
                  const isLoading = ciLoadingDetails[reqIdx];
                  return (
                    <div className="p-4 space-y-4">
                      {/* Control header */}
                      <div>
                        <div className="items-center gap-2">
                          <span className="px-2 py-0.5 rounded bg-indigo-50 dark:bg-indigo-500/10 text-[10px] font-black text-indigo-600 dark:text-indigo-400">
                            {ctrl.controlId?.toUpperCase()}
                          </span>
                          <h4 className="text-sm font-bold text-foreground">
                            {ctrl.title || ctrl.controlId?.toUpperCase()}
                          </h4>
                        </div>
                      </div>

                      {isLoading && (
                        <div className="flex items-center gap-2 text-sm dark:text-white text-foreground py-4">
                          <Loader2 className="size-4 animate-spin" /> Loading
                          details
                        </div>
                      )}

                      {detail && !isLoading && (
                        <>
                          {/* Statements */}
                          {detail.parts.filter((p) => p.name === "statement")
                            .length > 0 && (
                              <div className="rounded-lg border border-border p-3 space-y-2">
                                <p className="text-xs font-bold text-foreground">
                                  Statements
                                </p>
                                {detail.parts
                                  .filter((p) => p.name === "statement")
                                  .map((part) => {
                                    const stmts = [
                                      { id: part.partId, prose: part.prose },
                                      ...part.parts.map((s) => ({
                                        id: s.partId,
                                        prose: s.prose,
                                      })),
                                    ];
                                    return stmts.map((stmt) => (
                                      <div
                                        key={stmt.id}
                                        className="flex items-start gap-2.5 p-2.5 rounded-lg border border-indigo-300 dark:border-indigo-500/30 bg-indigo-50/50 dark:bg-indigo-500/5"
                                      >
                                        <CheckCircle className="size-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                                        <div className="flex-1 min-w-0">
                                          <span className="text-[10px] font-bold dark:text-white text-foreground ">
                                            {stmt.id}
                                          </span>
                                          {stmt.prose && (
                                            <p className="text-xs text-foreground mt-0.5">
                                              {stmt.prose}
                                            </p>
                                          )}
                                        </div>
                                      </div>
                                    ));
                                  })}
                              </div>
                            )}

                          {/* Guidance */}
                          {detail.parts.filter((p) => p.name === "guidance")
                            .length > 0 && (
                              <div className="rounded-lg border border-amber-200 dark:border-amber-500/20 bg-amber-50/50 dark:bg-amber-500/5 p-3 space-y-1.5">
                                <p className="text-xs font-bold text-amber-800 dark:text-amber-400">
                                  Guidance
                                </p>
                                {detail.parts
                                  .filter((p) => p.name === "guidance")
                                  .map((part) => (
                                    <p
                                      key={part.id}
                                      className="text-xs text-amber-700 dark:text-amber-300/80"
                                    >
                                      {part.prose}
                                    </p>
                                  ))}
                              </div>
                            )}

                          {/* Parameters */}
                          {req && req.setParameters.length > 0 && (
                            <div className="rounded-lg border border-border p-3 space-y-3">
                              <p className="text-xs font-bold text-foreground">
                                Parameters
                              </p>
                              {req.setParameters.map((param) => {
                                const catalogParam = detail.params.find(
                                  (p) => p.paramId === param.paramId,
                                );
                                return (
                                  <div
                                    key={param.paramId}
                                    className="rounded-lg border border-dashed border-border p-3 space-y-1"
                                  >
                                    <div className="flex items-center gap-2">
                                      <span className="text-[10px] font-bold dark:text-white text-foreground ">
                                        {param.paramId}
                                      </span>
                                      {catalogParam?.label && (
                                        <span className="text-[10px] font-medium text-indigo-600 dark:text-indigo-400">
                                          {catalogParam.label}
                                        </span>
                                      )}
                                    </div>
                                    <FormInput
                                      placeholder={
                                        catalogParam?.values?.length
                                          ? `e.g. ${catalogParam.values.join(", ")}`
                                          : "Enter value"
                                      }
                                      value={param.values}
                                      onChange={(v) =>
                                        setCIForm((f) => ({
                                          ...f,
                                          requirements: f.requirements.map(
                                            (r, i) =>
                                              i === reqIdx
                                                ? {
                                                  ...r,
                                                  setParameters:
                                                    r.setParameters.map(
                                                      (p) =>
                                                        p.paramId ===
                                                          param.paramId
                                                          ? {
                                                            ...p,
                                                            values: v,
                                                          }
                                                          : p,
                                                    ),
                                                }
                                                : r,
                                          ),
                                        }))
                                      }
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })()
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center py-16">
                  <Search className="size-8 dark:text-white text-foreground/30 mb-3" />
                  <p className="text-sm font-medium dark:text-white text-foreground">Select a control</p>
                  <p className="text-xs dark:text-white text-foreground mt-1">
                    Click a control from the list to view details
                  </p>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </Modal>
  );

  // Suppress unused function warnings
  void updateTopLevelParam;
  void removeTopLevelParam;

  /* dead-block-start
              Implemented Requirements
            </label>
            <button
              type="button"
              onClick={addRequirement}
              className="flex items-center gap-1 text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
            >
              <Plus className="size-3" />
              Add Requirement
            </button>
          </div>

          {ciForm.requirements.map((req, reqIdx) => (
            <div
              key={reqIdx}
              className="rounded-lg border border-border p-4 space-y-3"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-black tracking-widest dark:text-white text-foreground">
                  Requirement #{reqIdx + 1}
                </span>
                {ciForm.requirements.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeRequirement(reqIdx)}
                    className="text-xs text-red-500 hover:text-red-700 transition-colors font-medium"
                  >
                    Remove
                  </button>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <FormField label="Control ID" required>
                  <FormSelect
                    value={req.controlId}
                    onChange={(v) => updateRequirement(reqIdx, "controlId", v)}
                    options={[
                      {
                        value: "",
                        label: loadingControls
                          ? "Loading"
                          : "Select a control",
                      },
                      ...catalogControls.map((c) => ({
                        value: c.controlId,
                        label: c.controlId.toUpperCase(),
                      })),
                    ]}
                  />
                </FormField>
                <FormField label="Description">
                  <FormInput
                    placeholder="How this control is implemented"
                    value={req.description}
                    onChange={(v) =>
                      updateRequirement(reqIdx, "description", v)
                    }
                  />
                </FormField>
              </div>

              {ciLoadingDetails[reqIdx] && (
                <div className="flex items-center gap-2 text-sm dark:text-white text-foreground py-2">
                  <Loader2 className="size-4 animate-spin" />
                  Loading control details
                </div>
              )}

              {ciControlDetails[reqIdx] &&
                !ciLoadingDetails[reqIdx] &&
                (() => {
                  const detail = ciControlDetails[reqIdx]!;
                  return (
                    <>
                      {detail.parts.filter((p) => p.name === "guidance")
                        .length > 0 && (
                        <div className="rounded-lg border border-amber-200 dark:border-amber-500/20 bg-amber-50/50 dark:bg-amber-500/5 p-3 space-y-1.5">
                          <p className="text-xs font-semibold text-amber-800 dark:text-amber-400">
                            Guidance
                          </p>
                          {detail.parts
                            .filter((p) => p.name === "guidance")
                            .map((part) => (
                              <p
                                key={part.id}
                                className="text-xs text-amber-700 dark:text-amber-300/80"
                              >
                                {part.prose}
                              </p>
                            ))}
                        </div>
                      )}

                      {detail.parts.filter((p) => p.name === "statement")
                        .length > 0 && (
                        <div className="rounded-lg border border-border p-3 space-y-2">
                          <p className="text-xs font-semibold">Statements</p>
                          {detail.parts
                            .filter((p) => p.name === "statement")
                            .map((part) => {
                              const allStatements = [
                                { id: part.partId, prose: part.prose },
                                ...part.parts.map((sub) => ({
                                  id: sub.partId,
                                  prose: sub.prose,
                                })),
                              ];
                              return allStatements.map((stmt) => (
                                <div
                                  key={stmt.id}
                                  className="flex items-start gap-2.5 p-2.5 rounded-lg border border-indigo-300 dark:border-indigo-500/30 bg-indigo-50/50 dark:bg-indigo-500/5"
                                >
                                  <CheckCircle className="size-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                                  <div className="flex-1 min-w-0">
                                    <span className="text-[10px] font-bold dark:text-white text-foreground ">
                                      {stmt.id}
                                    </span>
                                    {stmt.prose && (
                                      <p className="text-xs text-foreground mt-0.5">
                                        {stmt.prose}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              ));
                            })}
                        </div>
                      )}

                      {req.setParameters.length > 0 && (
                        <div className="rounded-lg border border-border p-3 space-y-3">
                          <p className="text-xs font-semibold">Parameters</p>
                          {req.setParameters.map((param, pIdx) => {
                            const catalogParam = detail.params.find(
                              (p) => p.paramId === param.paramId,
                            );
                            return (
                              <div
                                key={param.paramId}
                                className="rounded-lg border border-dashed border-border p-3 space-y-1"
                              >
                                <div className="flex items-center gap-2">
                                  <span className="text-[10px] font-bold dark:text-white text-foreground ">
                                    {param.paramId}
                                  </span>
                                  {catalogParam?.label && (
                                    <span className="text-[10px] font-medium text-indigo-600 dark:text-indigo-400">
                                      {catalogParam.label}
                                    </span>
                                  )}
                                </div>
                                <FormInput
                                  placeholder={
                                    catalogParam?.values &&
                                    catalogParam.values.length > 0
                                      ? `e.g. ${catalogParam.values.join(", ")}`
                                      : "Enter value"
                                  }
                                  value={param.values}
                                  onChange={(v) =>
                                    setCIForm((f) => ({
                                      ...f,
                                      requirements: f.requirements.map(
                                        (r, i) =>
                                          i === reqIdx
                                            ? {
                                                ...r,
                                                setParameters:
                                                  r.setParameters.map(
                                                    (p, pi) =>
                                                      pi === pIdx
                                                        ? { ...p, values: v }
                                                        : p,
                                                  ),
                                              }
                                            : r,
                                      ),
                                    }))
                                  }
                                />
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </>
                  );
                })()}
            </div>
          ))}
        </div>
      )}
    dead-block-end */

  // ─── Wizard View ──────────────────────────────────────────────
  if (showWizard) {
    return (
      <div className="space-y-6">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setShowWizard(false);
              resetWizard();
            }}
            className="flex items-center gap-1.5 text-sm dark:text-white text-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="size-3.5" />
            Component Definition
          </button>
          <ChevronRight className="size-3.5 dark:text-white text-foreground" />
          <span className="text-sm font-medium">New Component Definition</span>
        </div>

        {/* Header */}
        <div className="rounded-lg border border-border bg-card p-5">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-lg font-semibold">
                New Component Definition
              </h2>
              <p className="text-sm dark:text-white text-foreground mt-0.5">
                Follow the steps to build your OSCAL component definition
              </p>
            </div>
          </div>
        </div>

        {/* Step indicator */}
        <div className="rounded-lg border border-border bg-card px-6 py-5">
          <div className="flex items-center">
            {wizardSteps.map((s, i) => (
              <Fragment key={s.key}>
                <div
                  className={`flex flex-col items-center gap-1.5 ${i === 0 ? "" : i === wizardSteps.length - 1 ? "" : ""}`}
                >
                  <div
                    className={`size-10 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${i < currentStepIndex
                      ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400 ring-2 ring-emerald-200 dark:ring-emerald-500/30"
                      : i === currentStepIndex
                        ? "bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-400 ring-2 ring-indigo-200 dark:ring-indigo-500/30"
                        : "bg-muted d"
                      }`}
                  >
                    {i < currentStepIndex ? (
                      <CheckCircle className="size-5" />
                    ) : (
                      <span>{s.num}</span>
                    )}
                  </div>
                  <span
                    className={`text-sm font-semibold transition-colors whitespace-nowrap ${i < currentStepIndex
                      ? "text-emerald-700 dark:text-emerald-400"
                      : i === currentStepIndex
                        ? "text-indigo-700 dark:text-indigo-400"
                        : "d"
                      }`}
                  >
                    {s.label}
                  </span>
                </div>
                {i < wizardSteps.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mx-4 mb-6 rounded-full transition-colors ${i < currentStepIndex
                      ? "bg-emerald-300 dark:bg-emerald-500/40"
                      : "bg-border"
                      }`}
                  />
                )}
              </Fragment>
            ))}
          </div>
        </div>

        {/* Error */}
        {wizardError && (
          <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-sm text-red-700 dark:text-red-400">
            <AlertCircle className="size-4 flex-shrink-0" />
            <span className="text-xs">{wizardError}</span>
          </div>
        )}

        {/* Step content card */}
        <div className="rounded-lg border border-border bg-card p-6">
          {/* ── Step: Title ── */}
          {wizardStep === "title" && (
            <div className="space-y-4">
              <div className="rounded-lg border border-border bg-muted/30 p-4">
                <h3 className="text-sm font-semibold mb-1">
                  Step 1: Create Component Definition
                </h3>
                <p className="text-xs dark:text-white text-foreground">
                  Create the top-level OSCAL{" "}
                  <code className="font-mono text-[10px] bg-muted px-1 py-0.5 rounded">
                    component-definition
                  </code>{" "}
                  document
                </p>
              </div>
              <FormField
                label="Definition Title"
                required
                hint="This becomes the metadata title of the OSCAL component definition"
              >
                <FormInput
                  placeholder="e.g. Auth Stack Components"
                  value={compForm.title}
                  onChange={(v) => setCompForm((f) => ({ ...f, title: v }))}
                />
              </FormField>
              <FormField label="Organization" required>
                {accessibleOrgsLoading ? (
                  <div className="flex items-center gap-2 py-2">
                    <Loader2 className="size-4 animate-spin dark:text-white text-foreground" />
                    <span className="text-sm dark:text-white text-foreground">Loading</span>
                  </div>
                ) : accessibleOrgs.length === 0 ? (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200/50 dark:border-red-500/20">
                    <span className="text-sm text-red-600 dark:text-red-400">
                      No accessible organizations.
                    </span>
                  </div>
                ) : accessibleOrgs.length === 1 ? (
                  <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[#f5f7fb] dark:bg-white/[0.04] border border-[#e2e8f0] dark:border-white/[0.07] text-sm">
                    <span className="font-medium">
                      {accessibleOrgs[0].name}
                    </span>
                    <span className="d">({accessibleOrgs[0].shortName})</span>
                  </div>
                ) : (
                  <FormSelect
                    options={accessibleOrgs.map((o) => ({
                      value: o.id,
                      label: `${o.name} (${o.shortName})`,
                    }))}
                    value={wizardOrgId}
                    onChange={setWizardOrgId}
                    placeholder="Select organization"
                  />
                )}
              </FormField>
            </div>
          )}

          {/* ── Step: Components ── */}
          {wizardStep === "components" && (
            <div className="space-y-4">
              <div className="rounded-lg border border-border bg-muted/30 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-sm font-semibold">
                    Step 2: Components & Controls
                  </h3>
                  {createdDefId && (
                    <span className="flex items-center gap-1 text-[10px] font-mono text-emerald-600 dark:text-emerald-400">
                      <CheckCircle className="size-3" /> Definition created
                    </span>
                  )}
                </div>
                <p className="text-xs dark:text-white text-foreground">
                  Add components and map controls to them. Click "Map Controls"
                  on any component to assign controls from a catalog.
                </p>
              </div>

              {/* Already added components with inline control mapping */}
              {createdComponents.length > 0 && (
                <div className="space-y-3">
                  {createdComponents.map((c) => {
                    const CIcon = typeIconMap[c.componentType] || Cpu;
                    const isExpanded = wizActiveComponentId === c.id;
                    const compControls = wizComponentControls[c.id] || [];
                    return (
                      <div
                        key={c.id}
                        className="rounded-lg border border-border overflow-hidden"
                      >
                        {/* Component header row */}
                        <div className="flex items-center gap-3 px-4 py-3">
                          <div className="size-8 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                            <CIcon className="size-4 text-emerald-600 dark:text-emerald-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium truncate">
                                {c.title}
                              </p>
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide bg-muted dark:text-white text-foreground flex-shrink-0">
                                {c.componentType}
                              </span>
                              {compControls.length > 0 && (
                                <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-indigo-100 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex-shrink-0">
                                  {compControls.length} control
                                  {compControls.length !== 1 ? "s" : ""}
                                </span>
                              )}
                            </div>
                            {c.description && (
                              <p className="text-xs dark:text-white text-foreground mt-0.5 line-clamp-2">
                                {c.description}
                              </p>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => handleExpandComponentControls(c.id)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex-shrink-0 ${isExpanded
                              ? "bg-indigo-100 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-300"
                              : "border border-border d hover:text-foreground hover:bg-accent"
                              }`}
                          >
                            <Shield className="size-3" />
                            Map Controls
                            <ChevronDown
                              className={`size-3 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                            />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRemoveComponent(c.id)}
                            disabled={stepLoading}
                            className="p-1.5 rounded-md dark:text-white text-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
                            title="Remove component"
                          >
                            <Trash2 className="size-3.5" />
                          </button>
                        </div>

                        {/* Inline control mapping panel */}
                        <AnimatePresence>
                          {isExpanded && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              <div className="border-t border-border">
                                <div className="grid grid-cols-[200px_1fr] gap-0 h-[360px]">
                                  {/* Left: Catalogs */}
                                  <div className="border-r border-border flex flex-col min-h-0">
                                    <div className="px-3 py-2 bg-muted/30 border-b border-border flex-shrink-0">
                                      <span className="text-[11px] font-semibold dark:text-white text-foreground tracking-wide">
                                        Catalogs
                                      </span>
                                    </div>
                                    <div className="flex-1 overflow-y-auto min-h-0 divide-y divide-border">
                                      {wizLoadingCatalogs ? (
                                        <div className="flex items-center justify-center py-8">
                                          <Loader2 className="size-4 text-indigo-500 animate-spin" />
                                        </div>
                                      ) : wizCatalogs.length === 0 ? (
                                        <p className="text-xs dark:text-white text-foreground p-3 text-center">
                                          No catalogs found
                                        </p>
                                      ) : (
                                        wizCatalogs.map((catalog) => {
                                          const isActive =
                                            wizSelectedCatalogId === catalog.id;
                                          return (
                                            <button
                                              key={catalog.id}
                                              type="button"
                                              onClick={() =>
                                                handleWizCatalogChange(
                                                  catalog.id,
                                                )
                                              }
                                              className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors ${isActive
                                                ? "bg-indigo-50 dark:bg-indigo-500/10 border-l-2 border-l-indigo-600 dark:border-l-indigo-500"
                                                : "hover:bg-accent border-l-2 border-l-transparent"
                                                }`}
                                            >
                                              <BookOpen
                                                className={`size-3.5 flex-shrink-0 ${isActive ? "text-indigo-600 dark:text-indigo-400" : "d"}`}
                                              />
                                              <span
                                                className={`text-xs font-medium truncate ${isActive ? "text-indigo-700 dark:text-indigo-300" : ""}`}
                                              >
                                                {catalog.title ||
                                                  catalog.documentId}
                                              </span>
                                            </button>
                                          );
                                        })
                                      )}
                                    </div>
                                  </div>

                                  {/* Right: Controls */}
                                  <div className="flex flex-col min-h-0">
                                    <div className="px-3 py-2 bg-muted/30 border-b border-border flex items-center justify-between flex-shrink-0">
                                      <span className="text-[11px] font-semibold dark:text-white text-foreground tracking-wide">
                                        Controls
                                      </span>
                                      <div className="flex items-center gap-2">
                                        {wizControls.length > 0 &&
                                          wizSelectedCatalogId && (
                                            <button
                                              type="button"
                                              onClick={() =>
                                                selectAllWizControls(c.id)
                                              }
                                              disabled={wizLoadingDetails}
                                              className="flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-semibold text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-500/10 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                            >
                                              {wizLoadingDetails ? (
                                                <Loader2 className="size-3.5 animate-spin" />
                                              ) : (
                                                <div
                                                  className={`w-3.5 h-3.5 rounded flex items-center justify-center flex-shrink-0 transition-colors ${compControls.length ===
                                                    wizControls.length
                                                    ? "bg-indigo-600 dark:bg-indigo-600"
                                                    : "border border-indigo-400 dark:border-indigo-500"
                                                    }`}
                                                >
                                                  {compControls.length ===
                                                    wizControls.length && (
                                                      <span className="text-white text-[8px] font-bold">
                                                        {"\u2713"}
                                                      </span>
                                                    )}
                                                </div>
                                              )}
                                              {wizLoadingDetails
                                                ? "Loading"
                                                : compControls.length ===
                                                  wizControls.length
                                                  ? "Deselect All"
                                                  : "Select All"}
                                            </button>
                                          )}
                                        {compControls.length > 0 && (
                                          <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-indigo-100 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400">
                                            {compControls.length} selected
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex-1 overflow-y-auto min-h-0 scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent relative">
                                      {wizLoadingDetails && (
                                        <div className="absolute inset-0 z-20 flex items-center justify-center bg-background/70 backdrop-blur-sm">
                                          <div className="flex flex-col items-center gap-2 px-5 py-4 rounded-xl bg-card border border-border shadow-lg">
                                            <Loader2 className="size-5 text-indigo-500 animate-spin" />
                                            <p className="text-xs font-medium text-foreground">
                                              Selecting all controls…
                                            </p>
                                            <p className="text-[11px] dark:text-white text-foreground">
                                              Loading control details, please
                                              wait.
                                            </p>
                                          </div>
                                        </div>
                                      )}
                                      {!wizSelectedCatalogId ? (
                                        <div className="flex flex-col items-center justify-center h-full py-12 text-center px-4">
                                          <BookOpen className="size-5 dark:text-white text-foreground/40 mb-2" />
                                          <p className="text-xs dark:text-white text-foreground">
                                            Select a catalog to load controls
                                          </p>
                                        </div>
                                      ) : wizLoadingControls ? (
                                        <div className="flex items-center justify-center py-12">
                                          <Loader2 className="size-5 text-indigo-500 animate-spin" />
                                          <span className="ml-2 text-sm dark:text-white text-foreground">
                                            Loading controls
                                          </span>
                                        </div>
                                      ) : wizControls.length === 0 ? (
                                        <p className="text-sm dark:text-white text-foreground py-8 text-center">
                                          No controls found
                                        </p>
                                      ) : (
                                        <div className="divide-y divide-border">
                                          {wizControls.map((ctrl) => {
                                            const isSelected =
                                              !!compControls.find(
                                                (sc) =>
                                                  sc.controlId ===
                                                  ctrl.controlId,
                                              );
                                            const selectedEntry =
                                              compControls.find(
                                                (sc) =>
                                                  sc.controlId ===
                                                  ctrl.controlId,
                                              );
                                            const detail =
                                              wizControlDetails[
                                              ctrl.controlId
                                              ] || ctrl;
                                            return (
                                              <div key={ctrl.id}>
                                                <button
                                                  type="button"
                                                  onClick={() =>
                                                    toggleWizControl(c.id, ctrl)
                                                  }
                                                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${isSelected
                                                    ? "bg-indigo-50/50 dark:bg-indigo-500/5"
                                                    : "hover:bg-accent"
                                                    }`}
                                                >
                                                  <div
                                                    className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 transition-colors ${isSelected
                                                      ? "bg-indigo-600 dark:bg-indigo-600"
                                                      : "border border-border"
                                                      }`}
                                                  >
                                                    {isSelected && (
                                                      <span className="text-white text-[10px] font-bold">
                                                        {"\u2713"}
                                                      </span>
                                                    )}
                                                  </div>
                                                  <span className="px-1.5 py-0.5 rounded text-[10px] font-bold font-mono bg-muted dark:text-white text-foreground flex-shrink-0">
                                                    {ctrl.controlId?.toUpperCase()}
                                                  </span>
                                                  <span className="text-xs truncate flex-1">
                                                    {ctrl.title}
                                                  </span>
                                                  {detail && (
                                                    <span className="flex items-center gap-1.5 flex-shrink-0">
                                                      {detail.parts.length >
                                                        0 && (
                                                          <span className="text-[9px] font-mono px-1 py-0.5 rounded bg-muted dark:text-white text-foreground">
                                                            {detail.parts.length}p
                                                          </span>
                                                        )}
                                                      {detail.params.length >
                                                        0 && (
                                                          <span className="text-[9px] font-mono px-1 py-0.5 rounded bg-muted dark:text-white text-foreground">
                                                            {detail.params.length}
                                                            pr
                                                          </span>
                                                        )}
                                                    </span>
                                                  )}
                                                </button>
                                                {/* Expanded detail when selected */}
                                                {isSelected && detail && (
                                                  <div className="px-3 pb-3 pl-10 space-y-2">
                                                    <input
                                                      type="text"
                                                      placeholder="How this component implements this control"
                                                      value={
                                                        selectedEntry?.description ??
                                                        ""
                                                      }
                                                      onChange={(e) =>
                                                        updateWizControlDesc(
                                                          c.id,
                                                          ctrl.controlId,
                                                          e.target.value,
                                                        )
                                                      }
                                                      onClick={(e) =>
                                                        e.stopPropagation()
                                                      }
                                                      className="w-full text-xs px-2.5 py-1.5 rounded-md border border-border bg-background outline-none focus:border-indigo-400 dark:focus:border-indigo-500 placeholder:d"
                                                    />
                                                    <div className="rounded-md border border-border bg-muted/30 overflow-hidden text-xs">
                                                      {detail.parts.length >
                                                        0 && (
                                                          <div className="divide-y divide-border">
                                                            {detail.parts.map(
                                                              (part) => (
                                                                <div
                                                                  key={part.id}
                                                                  className="px-3 py-2"
                                                                >
                                                                  <div className="flex items-center gap-1.5 mb-1">
                                                                    <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400">
                                                                      {part.name}
                                                                    </span>
                                                                    {part.title && (
                                                                      <span className="text-[10px] font-medium text-foreground">
                                                                        {
                                                                          part.title
                                                                        }
                                                                      </span>
                                                                    )}
                                                                  </div>
                                                                  {part.prose && (
                                                                    <p className="d leading-relaxed whitespace-pre-wrap">
                                                                      {part.prose}
                                                                    </p>
                                                                  )}
                                                                  {part.parts &&
                                                                    part.parts
                                                                      .length >
                                                                    0 && (
                                                                      <div className="mt-1.5 ml-3 space-y-1.5 border-l-2 border-border pl-2.5">
                                                                        {part.parts.map(
                                                                          (
                                                                            sub,
                                                                            sIdx,
                                                                          ) => (
                                                                            <div
                                                                              key={
                                                                                sIdx
                                                                              }
                                                                            >
                                                                              <span className="text-[9px] font-bold dark:text-white text-foreground">
                                                                                {sub.partId ||
                                                                                  sub.name}
                                                                              </span>
                                                                              {sub.prose && (
                                                                                <p className="d leading-relaxed whitespace-pre-wrap">
                                                                                  {
                                                                                    sub.prose
                                                                                  }
                                                                                </p>
                                                                              )}
                                                                            </div>
                                                                          ),
                                                                        )}
                                                                      </div>
                                                                    )}
                                                                </div>
                                                              ),
                                                            )}
                                                          </div>
                                                        )}
                                                      {detail.params.length >
                                                        0 && (
                                                          <div className="border-t border-border px-3 py-2">
                                                            <span className="text-[9px] font-bold dark:text-white text-foreground block mb-1.5">
                                                              Parameters
                                                            </span>
                                                            <div className="space-y-2.5">
                                                              {detail.params.map(
                                                                (param) => {
                                                                  const paramEntry =
                                                                    selectedEntry?.setParameters?.find(
                                                                      (p) =>
                                                                        p.paramId ===
                                                                        param.paramId,
                                                                    );
                                                                  return (
                                                                    <div
                                                                      key={
                                                                        param.id
                                                                      }
                                                                      className="space-y-1"
                                                                    >
                                                                      <div className="flex items-center gap-1.5">
                                                                        <span className="text-[10px] font-mono font-bold text-indigo-600 dark:text-indigo-400">
                                                                          {
                                                                            param.paramId
                                                                          }
                                                                        </span>
                                                                        <span className="text-[10px] dark:text-white text-foreground">
                                                                          {
                                                                            param.label
                                                                          }
                                                                        </span>
                                                                      </div>
                                                                      {param.guidelines &&
                                                                        param
                                                                          .guidelines
                                                                          .length >
                                                                        0 && (
                                                                          <p className="text-[10px] d/70">
                                                                            {
                                                                              param
                                                                                .guidelines[0]
                                                                                .prose
                                                                            }
                                                                          </p>
                                                                        )}
                                                                      <input
                                                                        type="text"
                                                                        placeholder="Enter value"
                                                                        value={
                                                                          paramEntry?.values ??
                                                                          ""
                                                                        }
                                                                        onChange={(
                                                                          e,
                                                                        ) =>
                                                                          updateWizControlParam(
                                                                            c.id,
                                                                            ctrl.controlId,
                                                                            param.paramId,
                                                                            e
                                                                              .target
                                                                              .value,
                                                                          )
                                                                        }
                                                                        onClick={(
                                                                          e,
                                                                        ) =>
                                                                          e.stopPropagation()
                                                                        }
                                                                        className="w-full text-xs px-2.5 py-1.5 rounded-md border border-border bg-background outline-none focus:border-indigo-400 dark:focus:border-indigo-500 placeholder:d"
                                                                      />
                                                                    </div>
                                                                  );
                                                                },
                                                              )}
                                                            </div>
                                                          </div>
                                                        )}
                                                    </div>
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Add component form */}
              <div className="rounded-lg border border-dashed border-border p-5 space-y-4">
                <p className="text-sm font-semibold">
                  {createdComponents.length === 0
                    ? "Add your first component"
                    : "Add another component"}
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <FormField label="Component Name" required>
                    <FormInput
                      placeholder="e.g. CrowdStrike Falcon"
                      value={compForm.title}
                      onChange={(v) => setCompForm((f) => ({ ...f, title: v }))}
                    />
                  </FormField>
                  <FormField label="Component Type" required>
                    <FormSelect
                      options={COMPONENT_TYPES.map((t) => ({
                        value: t,
                        label: typeLabel(t),
                      }))}
                      placeholder="Select type"
                      value={compForm.componentType || ""}
                      onChange={(v) =>
                        setCompForm((f) => ({
                          ...f,
                          componentType: v as ComponentType,
                        }))
                      }
                    />
                  </FormField>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <FormField label="Purpose" required>
                    <FormInput
                      placeholder="e.g. Satisfies SI and AU control families"
                      value={compForm.purpose}
                      onChange={(v) =>
                        setCompForm((f) => ({ ...f, purpose: v }))
                      }
                    />
                  </FormField>
                  <FormField label="Grouping Name">
                    {compForm.props.map((p, idx) => (
                      <input
                        key={idx}
                        type="text"
                        className="w-full px-3 py-1.5 text-sm bg-[#f5f7fb] dark:bg-muted border border-border rounded-lg focus:border-indigo-500 dark:focus:border-indigo-500 outline-none text-foreground placeholder:d dark:placeholder:d"
                        placeholder="Value (e.g. on-premise)"
                        value={p.value}
                        onChange={(e) =>
                          setCompForm((f) => ({
                            ...f,
                            props: f.props.map((prop, i) =>
                              i === idx
                                ? { ...prop, value: e.target.value }
                                : prop,
                            ),
                          }))
                        }
                      />
                    ))}
                  </FormField>
                </div>
                <FormField label="Description">
                  <FormTextarea
                    placeholder="Brief description of the component"
                    value={compForm.description}
                    onChange={(v) =>
                      setCompForm((f) => ({ ...f, description: v }))
                    }
                  />
                </FormField>

                <div className="flex items-center justify-end gap-3">
                  {createdComponents.length > 0 &&
                    (() => {
                      const last =
                        createdComponents[createdComponents.length - 1];
                      const lastControls = wizComponentControls[last.id] || [];
                      return lastControls.length === 0;
                    })() && (
                      <p className="text-xs text-amber-600 dark:text-amber-400">
                        Map at least one control to the last component before
                        adding another
                      </p>
                    )}
                  <button
                    type="button"
                    onClick={handleAddOneComponent}
                    disabled={
                      !compForm.title.trim() ||
                      !compForm.componentType ||
                      !compForm.purpose.trim() ||
                      stepLoading ||
                      (createdComponents.length > 0 &&
                        (
                          wizComponentControls[
                          createdComponents[createdComponents.length - 1].id
                          ] || []
                        ).length === 0)
                    }
                    className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {stepLoading && (
                      <Loader2 className="size-3.5 animate-spin" />
                    )}
                    <Plus className="size-3.5" />
                    {stepLoading ? "Adding" : "Add New"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Controls step removed - merged into components step */}
          {false && (
            <div className="space-y-4">
              <div className="rounded-lg border border-border bg-muted/30 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-sm font-semibold">
                    Step 3: Control Mapping
                  </h3>
                  <span className="flex items-center gap-1 text-[10px] font-mono text-emerald-600 dark:text-emerald-400">
                    <CheckCircle className="size-3" />{" "}
                    {createdComponents.length} component
                    {createdComponents.length !== 1 ? "s" : ""} created
                  </span>
                </div>
                <p className="text-xs dark:text-white text-foreground">
                  Select a component, then a catalog to load its controls, then
                  pick controls to map. You can skip this step.
                </p>
              </div>

              {/* 3-column layout */}
              <div className="grid grid-cols-[200px_200px_1fr] gap-3 h-[500px] overflow-hidden">
                {/* ── Left: Components ── */}
                <div className="rounded-lg border border-border overflow-hidden flex flex-col min-h-0">
                  <div className="px-3 py-2 bg-muted/30 border-b border-border flex-shrink-0">
                    <span className="text-[11px] font-semibold dark:text-white text-foreground tracking-wide">
                      Components
                    </span>
                  </div>
                  <div className="flex-1 overflow-y-auto min-h-0 divide-y divide-border">
                    {createdComponents.map((comp) => {
                      const CIcon = typeIconMap[comp.componentType] || Cpu;
                      const compControls = wizComponentControls[comp.id] || [];
                      const isActive = wizActiveComponentId === comp.id;
                      return (
                        <button
                          key={comp.id}
                          type="button"
                          onClick={() => setWizActiveComponentId(comp.id)}
                          className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors ${isActive
                            ? "bg-indigo-50 dark:bg-indigo-500/10 border-l-2 border-l-indigo-600 dark:border-l-indigo-500"
                            : "hover:bg-accent border-l-2 border-l-transparent"
                            }`}
                        >
                          <div className="size-6 rounded-md bg-muted flex items-center justify-center flex-shrink-0">
                            <CIcon
                              className={`size-3 ${isActive ? "text-indigo-600 dark:text-indigo-400" : "d"}`}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p
                              className={`text-xs font-medium truncate ${isActive ? "text-indigo-700 dark:text-indigo-300" : ""}`}
                            >
                              {comp.title}
                            </p>
                            {compControls.length > 0 && (
                              <p className="text-[10px] text-indigo-600 dark:text-indigo-400 font-mono mt-0.5">
                                {compControls.length} control
                                {compControls.length !== 1 ? "s" : ""}
                              </p>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* ── Middle: Catalogs ── */}
                <div className="rounded-lg border border-border overflow-hidden flex flex-col min-h-0">
                  <div className="px-3 py-2 bg-muted/30 border-b border-border flex-shrink-0">
                    <span className="text-[11px] font-semibold dark:text-white text-foreground tracking-wide">
                      Catalogs
                    </span>
                  </div>
                  <div className="flex-1 overflow-y-auto min-h-0 divide-y divide-border">
                    {wizLoadingCatalogs ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="size-4 text-indigo-500 animate-spin" />
                      </div>
                    ) : wizCatalogs.length === 0 ? (
                      <p className="text-xs dark:text-white text-foreground p-3 text-center">
                        No catalogs found
                      </p>
                    ) : (
                      wizCatalogs.map((catalog) => {
                        const isActive = wizSelectedCatalogId === catalog.id;
                        return (
                          <button
                            key={catalog.id}
                            type="button"
                            onClick={() => handleWizCatalogChange(catalog.id)}
                            className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors ${isActive
                              ? "bg-indigo-50 dark:bg-indigo-500/10 border-l-2 border-l-indigo-600 dark:border-l-indigo-500"
                              : "hover:bg-accent border-l-2 border-l-transparent"
                              }`}
                          >
                            <Shield
                              className={`size-3.5 flex-shrink-0 ${isActive ? "text-indigo-600 dark:text-indigo-400" : "d"}`}
                            />
                            <div className="flex-1 min-w-0">
                              <span
                                className={`text-xs font-medium truncate block ${isActive ? "text-indigo-700 dark:text-indigo-300" : ""}`}
                              >
                                {catalog.title || catalog.documentId}
                              </span>
                              <span className="text-[9px] dark:text-white text-foreground">
                                {catalog.controlCount ?? 0} control
                                {(catalog.controlCount ?? 0) !== 1 ? "s" : ""}
                              </span>
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* ── Right: Controls ── */}
                <div className="rounded-lg border border-border overflow-hidden flex flex-col min-h-0">
                  <div className="px-3 py-2 bg-muted/30 border-b border-border flex items-center justify-between flex-shrink-0">
                    <span className="text-[11px] font-semibold dark:text-white text-foreground tracking-wide">
                      Controls
                    </span>
                    {wizActiveComponentId &&
                      (wizComponentControls[wizActiveComponentId] || [])
                        .length > 0 && (
                        <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-indigo-100 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400">
                          {
                            (wizComponentControls[wizActiveComponentId] || [])
                              .length
                          }{" "}
                          selected
                        </span>
                      )}
                  </div>
                  <div className="flex-1 overflow-y-auto min-h-0 scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent">
                    {!wizActiveComponentId ? (
                      <div className="flex flex-col items-center justify-center h-full py-12 text-center px-4">
                        <ArrowLeft className="size-5 dark:text-white text-foreground/40 mb-2" />
                        <p className="text-xs dark:text-white text-foreground">Select a component first</p>
                      </div>
                    ) : !wizSelectedCatalogId ? (
                      <div className="flex flex-col items-center justify-center h-full py-12 text-center px-4">
                        <BookOpen className="size-5 dark:text-white text-foreground/40 mb-2" />
                        <p className="text-xs dark:text-white text-foreground">
                          Select a catalog to load controls
                        </p>
                      </div>
                    ) : wizLoadingControls ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="size-5 text-indigo-500 animate-spin" />
                        <span className="ml-2 text-sm dark:text-white text-foreground">Loading controls</span>
                      </div>
                    ) : wizControls.length === 0 ? (
                      <p className="text-sm dark:text-white text-foreground py-8 text-center">
                        No controls found
                      </p>
                    ) : (
                      <div className="divide-y divide-border">
                        {wizControls.map((ctrl) => {
                          const compControls =
                            wizComponentControls[wizActiveComponentId] || [];
                          const isSelected = !!compControls.find(
                            (c) => c.controlId === ctrl.controlId,
                          );
                          const selectedEntry = compControls.find(
                            (c) => c.controlId === ctrl.controlId,
                          );
                          const detail = wizControlDetails[ctrl.controlId];
                          return (
                            <div key={ctrl.id}>
                              <button
                                type="button"
                                onClick={() =>
                                  toggleWizControl(wizActiveComponentId, ctrl)
                                }
                                className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${isSelected
                                  ? "bg-indigo-50/50 dark:bg-indigo-500/5"
                                  : "hover:bg-accent"
                                  }`}
                              >
                                <div
                                  className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 transition-colors ${isSelected
                                    ? "bg-indigo-600 dark:bg-indigo-600"
                                    : "border border-border"
                                    }`}
                                >
                                  {isSelected && (
                                    <span className="text-white text-[10px] font-bold">
                                      {"\u2713"}
                                    </span>
                                  )}
                                </div>
                                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold font-mono bg-muted dark:text-white text-foreground flex-shrink-0">
                                  {ctrl.controlId?.toUpperCase()}
                                </span>
                                <span className="text-xs truncate flex-1">
                                  {ctrl.title}
                                </span>
                                {detail && (
                                  <span className="flex items-center gap-1.5 flex-shrink-0">
                                    {detail.parts.length > 0 && (
                                      <span className="text-[9px] font-mono px-1 py-0.5 rounded bg-muted dark:text-white text-foreground">
                                        {detail.parts.length}p
                                      </span>
                                    )}
                                    {detail.params.length > 0 && (
                                      <span className="text-[9px] font-mono px-1 py-0.5 rounded bg-muted dark:text-white text-foreground">
                                        {detail.params.length}pr
                                      </span>
                                    )}
                                  </span>
                                )}
                              </button>
                              {/* Expanded detail when selected */}
                              {isSelected && (
                                <div className="px-3 pb-3 pl-10 space-y-2">
                                  {/* Description input */}
                                  <input
                                    type="text"
                                    placeholder="How this component implements this control"
                                    value={selectedEntry?.description ?? ""}
                                    onChange={(e) =>
                                      updateWizControlDesc(
                                        wizActiveComponentId,
                                        ctrl.controlId,
                                        e.target.value,
                                      )
                                    }
                                    onClick={(e) => e.stopPropagation()}
                                    className="w-full text-xs px-2.5 py-1.5 rounded-md border border-border bg-background outline-none focus:border-indigo-400 dark:focus:border-indigo-500 placeholder:d"
                                  />
                                  {/* Control detail from getControl */}
                                  {detail ? (
                                    <div className="rounded-md border border-border bg-muted/30 overflow-hidden text-xs">
                                      {/* Parts (statements, guidance, etc.) */}
                                      {detail.parts.length > 0 && (
                                        <div className="divide-y divide-border">
                                          {detail.parts.map((part) => (
                                            <div
                                              key={part.id}
                                              className="px-3 py-2"
                                            >
                                              <div className="flex items-center gap-1.5 mb-1">
                                                <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400">
                                                  {part.name}
                                                </span>
                                                {part.title && (
                                                  <span className="text-[10px] font-medium text-foreground">
                                                    {part.title}
                                                  </span>
                                                )}
                                              </div>
                                              {part.prose && (
                                                <p className="d leading-relaxed whitespace-pre-wrap">
                                                  {part.prose}
                                                </p>
                                              )}
                                              {/* Nested sub-parts */}
                                              {part.parts &&
                                                part.parts.length > 0 && (
                                                  <div className="mt-1.5 ml-3 space-y-1.5 border-l-2 border-border pl-2.5">
                                                    {part.parts.map(
                                                      (sub, sIdx) => (
                                                        <div key={sIdx}>
                                                          <span className="text-[9px] font-bold dark:text-white text-foreground">
                                                            {sub.partId ||
                                                              sub.name}
                                                          </span>
                                                          {sub.prose && (
                                                            <p className="d leading-relaxed whitespace-pre-wrap">
                                                              {sub.prose}
                                                            </p>
                                                          )}
                                                        </div>
                                                      ),
                                                    )}
                                                  </div>
                                                )}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                      {/* Parameters */}
                                      {detail.params.length > 0 && (
                                        <div className="border-t border-border px-3 py-2">
                                          <span className="text-[9px] font-bold dark:text-white text-foreground block mb-1.5">
                                            Parameters
                                          </span>
                                          <div className="space-y-2.5">
                                            {detail.params.map((param) => {
                                              const paramEntry =
                                                selectedEntry?.setParameters?.find(
                                                  (p) =>
                                                    p.paramId === param.paramId,
                                                );
                                              return (
                                                <div
                                                  key={param.id}
                                                  className="space-y-1"
                                                >
                                                  <div className="flex items-center gap-1.5">
                                                    <span className="text-[10px] font-mono font-bold text-indigo-600 dark:text-indigo-400">
                                                      {param.paramId}
                                                    </span>
                                                    <span className="text-[10px] dark:text-white text-foreground">
                                                      {param.label}
                                                    </span>
                                                  </div>
                                                  {param.guidelines &&
                                                    param.guidelines.length >
                                                    0 && (
                                                      <p className="text-[10px] d/70">
                                                        {
                                                          param.guidelines[0]
                                                            .prose
                                                        }
                                                      </p>
                                                    )}
                                                  <input
                                                    type="text"
                                                    placeholder="Enter value"
                                                    value={
                                                      paramEntry?.values ?? ""
                                                    }
                                                    onChange={(e) =>
                                                      updateWizControlParam(
                                                        wizActiveComponentId!,
                                                        ctrl.controlId,
                                                        param.paramId,
                                                        e.target.value,
                                                      )
                                                    }
                                                    onClick={(e) =>
                                                      e.stopPropagation()
                                                    }
                                                    className="w-full text-xs px-2.5 py-1.5 rounded-md border border-border bg-background outline-none focus:border-indigo-400 dark:focus:border-indigo-500 placeholder:d"
                                                  />
                                                </div>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      )}
                                      {/* Enhancements */}
                                      {detail.enhancements &&
                                        detail.enhancements.length > 0 && (
                                          <div className="border-t border-border px-3 py-2">
                                            <span className="text-[9px] font-bold dark:text-white text-foreground block mb-1.5">
                                              Enhancements
                                            </span>
                                            <div className="flex flex-wrap gap-1">
                                              {detail.enhancements.map(
                                                (enh) => (
                                                  <span
                                                    key={enh.id}
                                                    className="px-1.5 py-0.5 rounded text-[9px] font-bold font-mono bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400"
                                                    title={enh.title}
                                                  >
                                                    {enh.controlId?.toUpperCase()}
                                                  </span>
                                                ),
                                              )}
                                            </div>
                                          </div>
                                        )}
                                    </div>
                                  ) : wizLoadingDetails ? (
                                    <div className="flex items-center gap-2 py-1">
                                      <Loader2 className="size-3 dark:text-white text-foreground animate-spin" />
                                      <span className="text-[10px] dark:text-white text-foreground">
                                        Loading details
                                      </span>
                                    </div>
                                  ) : null}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Step: Review ── */}
          {wizardStep === "review" && (
            <div className="space-y-5">
              <div className="rounded-lg border border-emerald-200 dark:border-emerald-500/20 bg-emerald-50/50 dark:bg-emerald-500/5 p-4 text-center">
                <CheckCircle className="size-10 text-emerald-500 mx-auto mb-2" />
                <h3 className="text-base font-bold text-emerald-900 dark:text-emerald-300">
                  All resources created successfully
                </h3>
                <p className="text-xs text-emerald-700/70 dark:text-emerald-400/50 mt-1">
                  Your component definition, {createdComponents.length}{" "}
                  component{createdComponents.length !== 1 ? "s" : ""}, and
                  control mappings have been saved.
                </p>
              </div>

              {/* Summary */}
              <div className="rounded-lg border border-border overflow-hidden">
                <div className="px-4 py-2.5 bg-muted/30 border-b border-border">
                  <span className="text-xs font-semibold dark:text-white text-foreground">Definition</span>
                </div>
                <div className="divide-y divide-border">
                  <div className="flex items-start justify-between px-4 py-3">
                    <span className="text-xs dark:text-white text-foreground w-28 flex-shrink-0">Title</span>
                    <span className="text-xs font-medium break-all text-right">
                      {createdDefTitle}
                    </span>
                  </div>
                </div>
              </div>

              {/* Components */}
              <div className="rounded-lg border border-border overflow-hidden">
                <div className="px-4 py-2.5 bg-muted/30 border-b border-border">
                  <span className="text-xs font-semibold dark:text-white text-foreground">
                    Components ({createdComponents.length})
                  </span>
                </div>
                <div className="divide-y divide-border">
                  {createdComponents.map((c) => {
                    const CIcon = typeIconMap[c.componentType] || Cpu;
                    return (
                      <div key={c.id} className="px-4 py-3 space-y-2">
                        <div className="flex items-center gap-3">
                          <CIcon className="size-4 dark:text-white text-foreground flex-shrink-0" />
                          <span className="text-sm font-semibold flex-1">
                            {c.title}
                          </span>
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-muted dark:text-white text-foreground flex-shrink-0">
                            {c.componentType}
                          </span>
                        </div>
                        {c.description && (
                          <div className="ml-7 space-y-0.5">
                            <span className="text-[10px] font-medium dark:text-white text-foreground tracking-wide">
                              Description
                            </span>
                            <p className="text-xs text-foreground/80">
                              {c.description}
                            </p>
                          </div>
                        )}
                        {c.purpose && (
                          <div className="ml-7 space-y-0.5">
                            <span className="text-[10px] font-medium dark:text-white text-foreground tracking-wide">
                              Purpose
                            </span>
                            <p className="text-xs text-foreground/80">
                              {c.purpose}
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Controls per component */}
              {createdComponents.map((comp) => {
                const compControls = wizComponentControls[comp.id] || [];
                if (compControls.length === 0) return null;
                const CIcon = typeIconMap[comp.componentType] || Cpu;
                return (
                  <div
                    key={comp.id}
                    className="rounded-lg border border-border overflow-hidden"
                  >
                    <div className="px-4 py-2.5 bg-muted/30 border-b border-border flex items-center gap-2">
                      <CIcon className="size-3.5 dark:text-white text-foreground" />
                      <span className="text-xs font-semibold dark:text-white text-foreground">
                        {comp.title} - {compControls.length} control
                        {compControls.length !== 1 ? "s" : ""}
                      </span>
                    </div>
                    <div className="divide-y divide-border">
                      {compControls.map((sc) => (
                        <div
                          key={sc.controlId}
                          className="flex items-center gap-2 px-4 py-2.5"
                        >
                          <Shield className="size-3 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold font-mono bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex-shrink-0">
                            {sc.controlId?.toUpperCase()}
                          </span>
                          <span className="text-sm break-words break-all whitespace-normal">{sc.title}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex items-center justify-between w-full gap-3 pt-6 mt-6 border-t border-border">
            {wizardStep === "title" ? (
              <>
                <button
                  onClick={() => {
                    setShowWizard(false);
                    resetWizard();
                  }}
                  className="px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-accent transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleWizardCreateDef}
                  disabled={!compForm.title.trim() || stepLoading}
                  className="px-4 py-2 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors disabled:opacity-40 shadow-md shadow-indigo-500/20 dark:shadow-indigo-500/20"
                >
                  {stepLoading ? "Creating" : "Create & Next"}
                </button>
              </>
            ) : wizardStep === "components" ? (
              <>
                <button
                  onClick={() => {
                    setShowWizard(false);
                    resetWizard();
                  }}
                  className="px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-accent transition-colors"
                >
                  Cancel
                </button>
                <div className="flex gap-2">
                  <button
                    onClick={handleComponentsContinue}
                    disabled={
                      (createdComponents.length === 0 &&
                        !compForm.componentType) ||
                      stepLoading
                    }
                    className="px-4 py-2 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors disabled:opacity-40 shadow-md shadow-indigo-500/20 dark:shadow-indigo-500/20"
                  >
                    {(() => {
                      const totalControls = Object.values(
                        wizComponentControls,
                      ).reduce((sum, arr) => sum + arr.length, 0);
                      return stepLoading
                        ? "Saving"
                        : totalControls > 0
                          ? `Save ${totalControls} Control${totalControls !== 1 ? "s" : ""} & Review`
                          : "Save & Review";
                    })()}
                  </button>
                </div>
              </>
            ) : (
              <>
                <button
                  onClick={() => {
                    setShowWizard(false);
                    resetWizard();
                  }}
                  className="px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-accent transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={handleWizardDone}
                  className="px-4 py-2 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors shadow-md shadow-indigo-500/20 dark:shadow-indigo-500/20"
                >
                  Done
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ─── Import Preview View (Visualizer) ──────────────────────────
  if (showPreview) {
    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                setShowPreview(false);
                setShowImport(false);
                resetVisualizer();
                resetImport();
              }}
              className="flex items-center gap-1.5 text-sm dark:text-white text-foreground hover:text-foreground/50 dark:hover:text-white transition-colors"
            >
              <ArrowLeft className="size-3.5" />
              Back to Library
            </button>
            <button
              onClick={() => {
                setShowPreview(false);
                setShowImport(true);
              }}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-accent transition-colors"
            >
              Back to Import
            </button>
          </div>
          <button
            onClick={() => {
              setShowPreview(false);
              resetVisualizer();
              runImport();
            }}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors shadow-md"
          >
            Confirm Import
          </button>
        </div>

        {/* 3-panel visualizer */}
        <div
          className="flex rounded-xl border border-border overflow-hidden bg-card"
          style={{ height: "calc(100vh - 180px)" }}
        >
          {/* Left: Tree */}
          <div className="w-64 border-r border-border flex-shrink-0 overflow-hidden flex flex-col">
            <div className="px-3 py-2 border-b border-border">
              <span className="text-[10px] font-bold dark:text-white text-foreground tracking-widest">
                Navigation
              </span>
            </div>
            <div className="flex-1 overflow-y-auto">
              <ComponentTree />
            </div>
          </div>

          {/* Center: Graph */}
          <div className="flex-1 bg-background overflow-hidden">
            <ComponentGraph />
          </div>

          {/* Right: Details */}
          <div className="w-72 border-l border-border flex-shrink-0 overflow-hidden flex flex-col">
            <div className="px-3 py-2 border-b border-border">
              <span className="text-[10px] font-bold dark:text-white text-foreground tracking-widest">
                Details
              </span>
            </div>
            <div className="flex-1 overflow-y-auto">
              <ComponentDetailsPanel />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Definition Loading Skeleton ─────────────────────────────────
  if (componentIdFromUrl && !selectedDefinition) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2 animate-pulse">
          <div className="h-3.5 w-16 rounded bg-muted" />
          <div className="h-3.5 w-3 rounded bg-muted" />
          <div className="h-3.5 w-32 rounded bg-muted" />
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          <SkeletonCard lines={6} />
          <SkeletonCard lines={6} />
        </div>
      </div>
    );
  }

  // ─── Definition Detail View ─────────────────────────────────────
  if (selectedDefinition && !selectedComponent) {
    return (
      <div className="space-y-6">
        {/* Delete Definition Confirmation */}
        <DeleteConfirmDialog
          open={!!deleteTarget}
          type="Component Definition"
          name={deleteTarget?.title || ""}
          deleting={deleting}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => handleDelete()}
        />

        {/* Remove Component Confirmation */}
        <DeleteConfirmDialog
          open={!!pendingRemoveComp}
          type="Component"
          name={pendingRemoveComp?.title || ""}
          deleting={false}
          onCancel={() => setPendingRemoveComp(null)}
          onConfirm={() => {
            const comp = pendingRemoveComp;
            if (comp) {
              handleRemoveDefComponent(comp.id);
            }
          }}
        />

        {/* Lifecycle Transition Modal */}
        <Modal
          open={showTransitionModal}
          onClose={() => setShowTransitionModal(false)}
          title={`${currentLifecycleActions.find((a) => a.target === transitionTarget)?.label || "Transition Lifecycle"}`}
          description={`Move to "${transitionTarget.replace(/_/g, " ")}" status`}
          size="sm"
          footer={
            <ModalFooterButtons
              onCancel={() => setShowTransitionModal(false)}
              onSubmit={handleLifecycleTransition}
              submitLabel={
                transitioningLifecycle
                  ? "Processing"
                  : `Confirm ${currentLifecycleActions.find((a) => a.target === transitionTarget)?.label || transitionTarget.replace(/_/g, " ")}`
              }
              submitDisabled={transitioningLifecycle}
            />
          }
        >
          <p className="text-sm text-foreground mb-3">
            You are about to change the status of{" "}
            <span className="font-semibold text-foreground break-words break-all whitespace-normal">
              "
              {docTitles[selectedDefinition.documentId] ||
                selectedDefinition.documentId}
              "
            </span>{" "}
            to{" "}
            <span className="font-semibold text-foreground capitalize">
              {transitionTarget.replace(/_/g, " ")}
            </span>
            .
          </p>
          <FormField label="Comment" hint="Optional reason for this transition">
            <FormTextarea
              placeholder="e.g. All controls reviewed and approved"
              value={transitionComment}
              onChange={(v) => setTransitionComment(v)}
              rows={3}
            />
          </FormField>
        </Modal>

        {/* Breadcrumb */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              navigate("/components");
              setSelectedDefinition(null);
            }}
            className="flex items-center gap-1.5 text-sm dark:text-white text-foreground hover:text-foreground/50 dark:hover:text-white transition-colors"
          >
            <ArrowLeft className="size-3.5" />
            Component Definition
          </button>
          <ChevronRight className="size-3.5 dark:text-white text-foreground" />
          <span className="text-sm font-medium text-foreground">
            {docTitles[selectedDefinition.documentId] ||
              selectedDefinition.documentId}
          </span>

          {/* Lifecycle Progression */}
          <div className="flex items-center gap-1 ml-3">
            {[
              { value: "draft", label: "Draft", color: "bg-emerald-500" },
              { value: "in_review", label: "In Review", color: "bg-emerald-500" },
              { value: "approved", label: "Approved", color: "bg-emerald-500" },
              { value: "published", label: "Published", color: "bg-emerald-500" },
            ].map((stage, idx, arr) => {
              const currentStatus = String(lifecycle).toLowerCase();
              const isPassed =
                arr.findIndex((s) => s.value === currentStatus) >= idx;

              return (
                <Fragment key={stage.label}>
                  <div className="flex items-center gap-1">
                    <div
                      className={`size-1.5 rounded-full flex-shrink-0 ${isPassed ? stage.color : "bg-slate-400 dark:bg-slate-600"}`}
                    />
                    <span
                      className={`text-[10px] font-medium whitespace-nowrap ${isPassed ? "text-emerald-600 dark:text-emerald-400" : "text-slate-500 dark:text-slate-400"}`}
                    >
                      {stage.label}
                    </span>
                  </div>
                  {idx < arr.length - 1 && (
                    <span className={`text-slate-400 dark:text-slate-600 font-light text-[8px]`}>–</span>
                  )}
                </Fragment>
              );
            })}
          </div>
        </div>

        {/* Definition hero header */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="px-6 pt-5 pb-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="size-12 rounded-xl bg-indigo-50 dark:bg-indigo-500/15 flex items-center justify-center flex-shrink-0">
                  <FileText className="size-6 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div>
                  <h2 className="text-xl font-black text-foreground tracking-[-0.5px] leading-tight mt-0.5">
                    {docTitles[selectedDefinition.documentId] ||
                      selectedDefinition.documentId}
                  </h2>
                  <p className="text-xs dark:text-white text-foreground mt-0.5">Component Definition</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {/* Lifecycle transition buttons */}
                {currentLifecycleActions.map((action) => {
                  const button = (
                    <button
                      key={action.target}
                      onClick={() => openTransitionModal(action.target)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-white text-xs font-semibold transition-colors shadow-md ${action.color}`}
                    >
                      {action.icon}
                      {action.label}
                    </button>
                  );

                  // Determine required permission based on action label
                  if (["Approve", "Reject"].includes(action.label)) {
                    return (
                      <RBACAction
                        key={action.target}
                        module="component"
                        action="approve"
                      >
                        {button}
                      </RBACAction>
                    );
                  }
                  if (action.label === "Publish") {
                    return (
                      <RBACAction
                        key={action.target}
                        module="component"
                        action="publish"
                      >
                        {button}
                      </RBACAction>
                    );
                  }
                  if (action.label === "Submit for Review") {
                    return (
                      <RBACAction
                        key={action.target}
                        module="component"
                        action="update"
                      >
                        {button}
                      </RBACAction>
                    );
                  }
                  if (["Supersede", "Withdraw"].includes(action.label)) {
                    return (
                      <RBACAction
                        key={action.target}
                        module="component"
                        action="update"
                      >
                        {button}
                      </RBACAction>
                    );
                  }
                  return button;
                })}
              </div>
            </div>
          </div>
          <div className="px-6 py-3 bg-muted/30 border-t border-border">
            <div className="flex items-center gap-6 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold dark:text-white text-foreground tracking-wider">
                  Components
                </span>
                <span className="text-xs font-medium text-foreground">
                  {defComponents.length}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold dark:text-white text-foreground tracking-wider">
                  Controls
                </span>
                <span className="text-xs font-medium text-foreground">
                  {defComponents.reduce(
                    (acc, c) =>
                      acc +
                      (c.controlImplementations || []).reduce(
                        (a, ci) =>
                          a + (ci.implementedRequirements?.length || 0),
                        0,
                      ),
                    0,
                  )}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold dark:text-white text-foreground tracking-wider">
                  Lifecycle
                </span>
                <StatusBadge
                  status={
                    lifecycle as Parameters<typeof StatusBadge>[0]["status"]
                  }
                />
              </div>
              <span className="text-xs dark:text-white text-foreground ml-auto">
                Updated:{" "}
                {new Date(selectedDefinition.updatedAt).toLocaleDateString(
                  "en-GB",
                )}
              </span>
            </div>
          </div>
        </div>

        {/* Tag-Based Filter + Component List */}
        {(() => {
          // Build group data from all components
          const allGroups = new Map<string, ComponentResponse[]>();
          const ungrouped: ComponentResponse[] = [];
          const allTypes = new Set<string>();
          allDefComponents.forEach((comp) => {
            allTypes.add(comp.componentType);
            const groupProp = comp.props?.find(
              (p) => p.name === "Group Name" && p.value,
            );
            if (groupProp) {
              const existing = allGroups.get(groupProp.value) || [];
              existing.push(comp);
              allGroups.set(groupProp.value, existing);
            } else {
              ungrouped.push(comp);
            }
          });
          const groupNames = [...allGroups.keys()].sort();
          const typeNames = [...allTypes].sort();

          // Filter components based on selected tags
          const filteredComps = allDefComponents.filter((comp) => {
            if (selectedGroupTags.size > 0) {
              const compGroup =
                comp.props?.find((p) => p.name === "Group Name" && p.value)
                  ?.value || "";
              if (!compGroup || !selectedGroupTags.has(compGroup)) return false;
            }
            if (selectedTypeTags.size > 0) {
              if (!selectedTypeTags.has(comp.componentType)) return false;
            }
            return true;
          });

          const hasActiveFilters =
            selectedGroupTags.size > 0 || selectedTypeTags.size > 0;

          const toggleGroupTag = (name: string) => {
            setSelectedGroupTags((prev) => {
              const next = new Set(prev);
              if (next.has(name)) next.delete(name);
              else next.add(name);
              return next;
            });
          };
          const toggleTypeTag = (name: string) => {
            setSelectedTypeTags((prev) => {
              const next = new Set(prev);
              if (next.has(name)) next.delete(name);
              else next.add(name);
              return next;
            });
          };
          const clearAllFilters = () => {
            setSelectedGroupTags(new Set());
            setSelectedTypeTags(new Set());
            setDetailFilterQuery("");
            setGroupFilterSearch("");
            setTypeFilterSearch("");
            setFilterKey(null);
          };

          const filteredGroupNames = groupFilterSearch.trim()
            ? groupNames.filter((g) =>
              g.toLowerCase().includes(groupFilterSearch.toLowerCase()),
            )
            : groupNames;
          const filteredTypeNames = typeFilterSearch.trim()
            ? typeNames.filter(
              (t) =>
                t.toLowerCase().includes(typeFilterSearch.toLowerCase()) ||
                typeLabel(t as ComponentType)
                  .toLowerCase()
                  .includes(typeFilterSearch.toLowerCase()),
            )
            : typeNames;

          // AWS filter bar: search across all property values
          const awsSearchQ = detailFilterQuery.trim().toLowerCase();
          const awsMatchingGroups = awsSearchQ
            ? groupNames.filter((g) => g.toLowerCase().includes(awsSearchQ))
            : [];
          const awsMatchingTypes = awsSearchQ
            ? typeNames.filter(
              (t) =>
                t.toLowerCase().includes(awsSearchQ) ||
                typeLabel(t as ComponentType)
                  .toLowerCase()
                  .includes(awsSearchQ),
            )
            : [];
          const awsHasSearchResults =
            awsMatchingGroups.length > 0 || awsMatchingTypes.length > 0;

          // Toggle expand for component rows
          const toggleComp = (id: string) => {
            setExpandedCompIds((prev) => {
              const next = new Set(prev);
              if (next.has(id)) next.delete(id);
              else next.add(id);
              return next;
            });
          };

          const renderComponent = (comp: ComponentResponse) => {
            const CIcon = typeIconMap[comp.componentType] || Cpu;
            const controlCount =
              comp.controlImplementations?.reduce(
                (acc, ci) => acc + (ci.implementedRequirements?.length || 0),
                0,
              ) || 0;
            const isExpanded = expandedCompIds.has(comp.id);
            return (
              <div
                key={comp.id}
                className="border-b border-border last:border-0"
              >
                <div
                  className="flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors group"
                  onClick={() => toggleComp(comp.id)}
                >
                  <ChevronRight
                    className={`size-4 d transition-transform flex-shrink-0 ${isExpanded ? "rotate-90" : ""}`}
                  />
                  <div className="size-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                    <CIcon className="size-4 dark:text-white text-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="text-sm font-semibold text-foreground break-words min-w-0">
                        {comp.title}
                      </h4>
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-muted dark:text-white text-foreground capitalize flex-shrink-0">
                        {comp.componentType}
                      </span>
                      {controlCount > 0 && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex-shrink-0">
                          {controlCount} control{controlCount !== 1 ? "s" : ""}
                        </span>
                      )}
                    </div>
                    {comp.description && (
                      <p className="text-xs dark:text-white text-foreground mt-0.5 line-clamp-3 break-words">
                        {comp.description}
                      </p>
                    )}
                    {comp.purpose && (
                      <p className="text-[10px] dark:text-white text-foreground mt-0.5">
                        Purpose: {comp.purpose}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const compWithDef = {
                          ...comp,
                          definitionId: selectedDefinition!.id,
                        };
                        setCiTargetComponent(compWithDef);
                        resetCIForm();
                        fetchCatalogs();
                        setShowAddCI(true);
                      }}
                      disabled={isPublished}
                      className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:d"
                      title={
                        isPublished
                          ? "Only draft component libraries can add controls"
                          : "Add Control Implementation"
                      }
                    >
                      <Plus className="size-3" /> Add Control
                    </button>
                    <RBACAction module="component" action="delete">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setPendingRemoveComp({
                            id: comp.id,
                            title: comp.title,
                          });
                        }}
                        disabled={isPublished}
                        className="p-1.5 rounded-md dark:text-white text-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:d"
                        title={
                          isPublished
                            ? "Only draft component libraries can have components removed"
                            : "Remove component"
                        }
                      >
                        <Trash2 className="size-3.5" />
                      </button>
                    </RBACAction>
                  </div>
                </div>
                <AnimatePresence>
                  {isExpanded &&
                    comp.controlImplementations &&
                    comp.controlImplementations.length > 0 && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="ml-12 mr-4 mb-3 pl-4 border-l-2 border-indigo-200 dark:border-indigo-500/30 space-y-3">
                          {Object.entries(
                            (comp.controlImplementations || []).reduce<
                              Record<
                                string,
                                { reqs: (typeof comp.controlImplementations)[0]["implementedRequirements"]; ci: (typeof comp.controlImplementations)[0] }
                              >
                            >((groups, ci) => {
                              const key = ci.source || "Ungrouped";
                              if (!groups[key]) groups[key] = { reqs: [], ci };
                              groups[key].reqs.push(
                                ...(ci.implementedRequirements || []),
                              );
                              return groups;
                            }, {}),
                          ).map(([source, { reqs, ci: groupCi }]) => (
                            <div key={source}>
                              <div className="flex items-center gap-2 mb-1.5">
                                <BookOpen className="size-3.5 text-indigo-600 dark:text-indigo-400" />
                                <span className="text-[11px] font-bold text-foreground">
                                  {source}
                                </span>
                                <span className="text-[10px] dark:text-white text-foreground">
                                  {reqs.length} control
                                  {reqs.length !== 1 ? "s" : ""}
                                </span>
                                {!isPublished && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const compWithDef = { ...comp, definitionId: selectedDefinition!.id };
                                      openEditCI(groupCi, compWithDef);
                                    }}
                                    className="ml-1 p-1 rounded hover:bg-indigo-50 dark:hover:bg-indigo-500/10 text-indigo-500 dark:text-indigo-400 transition-colors"
                                    title="Edit control implementation"
                                  >
                                    <Pencil className="size-3" />
                                  </button>
                                )}
                              </div>
                              <div className="space-y-1">
                                {reqs.map((req) => (
                                  <div
                                    key={req.controlId + req.id}
                                    className="flex items-center gap-3 px-3 py-2 rounded-lg bg-muted/50 border border-border"
                                  >
                                    <Shield className="size-3.5 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                                    <span className="text-xs font-bold font-mono text-indigo-600 dark:text-indigo-400 whitespace-nowrap flex-shrink-0">
                                      {req.controlId?.toUpperCase()}
                                    </span>
                                    {(req.statements?.[0]?.description ||
                                      req.description) && (
                                        <span className="text-xs dark:text-white text-foreground truncate">
                                          {req.statements?.[0]?.description ||
                                            req.description}
                                        </span>
                                      )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  {isExpanded &&
                    (!comp.controlImplementations ||
                      comp.controlImplementations.length === 0 ||
                      comp.controlImplementations.every(
                        (ci) => !ci.implementedRequirements?.length,
                      )) && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <p className="ml-12 mr-4 mb-3 pl-4 border-l-2 border-border py-2 text-xs dark:text-white text-foreground">
                          No control implementations
                        </p>
                      </motion.div>
                    )}
                </AnimatePresence>
              </div>
            );
          };

          return (
            <>
              {/* Filter bar + New Component button on same line */}
              <div className="flex items-center gap-2 flex-wrap">
                {/* AWS-Style Property Filter Bar */}
                <div
                  className="relative flex-1 min-w-[300px] flex items-center flex-wrap gap-2 p-1.5 bg-card rounded-xl border border-border focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-500/10 transition-all cursor-text"
                  onClick={() => setShowAwsFilterDropdown(true)}
                  ref={awsFilterRef}
                >
                  <Search className="size-4 d/40 ml-2 flex-shrink-0" />

                  {/* Active Tags inside the bar with Key: Value format */}
                  {[...selectedGroupTags].map((tag) => (
                    <span
                      key={`aws-g-${tag}`}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-400 border border-indigo-200/60 dark:border-indigo-500/20"
                    >
                      <span className="size-1.5 rounded-full bg-indigo-500 flex-shrink-0" />
                      <span className="text-indigo-400 dark:text-indigo-500">
                        Group:
                      </span>{" "}
                      {tag}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleGroupTag(tag);
                        }}
                        className="ml-0.5 p-0.5 rounded hover:bg-indigo-200/50 dark:hover:bg-indigo-500/20 transition-colors"
                      >
                        <X className="size-2.5" />
                      </button>
                    </span>
                  ))}
                  {[...selectedTypeTags].map((tag) => (
                    <span
                      key={`aws-t-${tag}`}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-200/60 dark:border-emerald-500/20"
                    >
                      <span className="size-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                      <span className="text-emerald-400 dark:text-emerald-500">
                        Type:
                      </span>{" "}
                      {typeLabel(tag as ComponentType)}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleTypeTag(tag);
                        }}
                        className="ml-0.5 p-0.5 rounded hover:bg-emerald-200/50 dark:hover:bg-emerald-500/20 transition-colors"
                      >
                        <X className="size-2.5" />
                      </button>
                    </span>
                  ))}

                  {/* Input area */}
                  <div className="flex-1 relative h-7">
                    <input
                      type="text"
                      placeholder={
                        hasActiveFilters
                          ? "Add filter"
                          : "Filter by property or search"
                      }
                      value={
                        filterKey
                          ? filterKey === "Group"
                            ? groupFilterSearch
                            : typeFilterSearch
                          : detailFilterQuery
                      }
                      onChange={(e) => {
                        if (filterKey === "Group")
                          setGroupFilterSearch(e.target.value);
                        else if (filterKey === "Type")
                          setTypeFilterSearch(e.target.value);
                        else setDetailFilterQuery(e.target.value);
                        if (!showAwsFilterDropdown)
                          setShowAwsFilterDropdown(true);
                      }}
                      onFocus={() => setShowAwsFilterDropdown(true)}
                      className="absolute inset-0 w-full h-full bg-transparent border-none outline-none text-sm text-foreground placeholder:d/40 font-medium px-2"
                    />

                    {/* AWS-style Dropdown */}
                    <AnimatePresence>
                      {showAwsFilterDropdown && (
                        <motion.div
                          initial={{ opacity: 0, y: 6, scale: 0.98 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 6, scale: 0.98 }}
                          transition={{ duration: 0.15 }}
                          className="absolute left-[-44px] top-[calc(100%+8px)] z-50 w-80 rounded-xl border border-border bg-card shadow-[0_16px_48px_rgba(0,0,0,0.25)] overflow-hidden"
                        >
                          {/* Header */}
                          <div className="px-4 py-2.5 border-b border-border bg-muted/30 flex items-center justify-between">
                            {filterKey ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setFilterKey(null);
                                  setGroupFilterSearch("");
                                  setTypeFilterSearch("");
                                }}
                                className="flex items-center gap-1.5 text-xs font-bold dark:text-white text-foreground hover:text-foreground transition-colors"
                              >
                                <ChevronLeft className="size-3.5" />
                                Back to Properties
                              </button>
                            ) : (
                              <span className="text-[10px] font-black dark:text-white text-foreground tracking-widest">
                                Properties
                              </span>
                            )}
                            {hasActiveFilters && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  clearAllFilters();
                                  setShowAwsFilterDropdown(false);
                                }}
                                className="text-[10px] font-bold text-red-500 hover:text-red-600 transition-colors"
                              >
                                Clear all
                              </button>
                            )}
                          </div>

                          {/* Body */}
                          <div
                            className="p-2 max-h-[300px] overflow-y-auto"
                            style={{ scrollbarWidth: "thin" }}
                          >
                            {!filterKey && !awsSearchQ ? (
                              /* No text typed — show property key selection */
                              <div className="space-y-1">
                                {groupNames.length > 0 && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setFilterKey("Group");
                                    }}
                                    className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-500/10 text-foreground transition-colors"
                                  >
                                    <div className="flex items-center gap-3">
                                      <div className="size-7 rounded-md bg-indigo-100 dark:bg-indigo-500/20 flex items-center justify-center">
                                        <Server className="size-3.5 text-indigo-600 dark:text-indigo-400" />
                                      </div>
                                      <span className="text-sm font-medium">
                                        Group
                                      </span>
                                    </div>
                                    <ChevronRight className="size-3.5 d/30" />
                                  </button>
                                )}
                                {typeNames.length > 0 && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setFilterKey("Type");
                                    }}
                                    className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-500/10 text-foreground transition-colors"
                                  >
                                    <div className="flex items-center gap-3">
                                      <div className="size-7 rounded-md bg-emerald-100 dark:bg-emerald-500/20 flex items-center justify-center">
                                        <Cpu className="size-3.5 text-emerald-600 dark:text-emerald-400" />
                                      </div>
                                      <span className="text-sm font-medium">
                                        Component Type
                                      </span>
                                    </div>
                                    <ChevronRight className="size-3.5 d/30" />
                                  </button>
                                )}
                                {groupNames.length === 0 &&
                                  typeNames.length === 0 && (
                                    <div className="p-8 text-center dark:text-white text-foreground text-xs italic">
                                      No filter options available
                                    </div>
                                  )}
                              </div>
                            ) : !filterKey &&
                              awsSearchQ &&
                              !awsHasSearchResults ? (
                              /* Search text but no matches */
                              <div className="p-8 text-center dark:text-white text-foreground text-xs italic">
                                No results for {`"${detailFilterQuery}"`}
                              </div>
                            ) : !filterKey &&
                              awsSearchQ &&
                              awsHasSearchResults ? (
                              /* Search text with matches — show from both properties */
                              <div className="space-y-2">
                                {awsMatchingGroups.length > 0 && (
                                  <div>
                                    <div className="px-3 py-1.5 text-[10px] font-bold dark:text-white text-foreground tracking-wider flex items-center gap-1.5">
                                      <Server className="size-3 text-indigo-500" />
                                      Group
                                    </div>
                                    <div className="space-y-0.5">
                                      {awsMatchingGroups.map((name) => {
                                        const isActive =
                                          selectedGroupTags.has(name);
                                        return (
                                          <button
                                            key={`search-g-${name}`}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              toggleGroupTag(name);
                                              setDetailFilterQuery("");
                                            }}
                                            className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-all ${isActive
                                              ? "bg-indigo-600 text-white"
                                              : "hover:bg-muted text-foreground"
                                              }`}
                                          >
                                            <span className="text-sm font-medium">
                                              {name}
                                            </span>
                                            {isActive && (
                                              <CheckCircle className="size-4" />
                                            )}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                                {awsMatchingTypes.length > 0 && (
                                  <div>
                                    <div className="px-3 py-1.5 text-[10px] font-bold dark:text-white text-foreground tracking-wider flex items-center gap-1.5">
                                      <Cpu className="size-3 text-emerald-500" />
                                      Component Type
                                    </div>
                                    <div className="space-y-0.5">
                                      {awsMatchingTypes.map((t) => {
                                        const isActive =
                                          selectedTypeTags.has(t);
                                        return (
                                          <button
                                            key={`search-t-${t}`}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              toggleTypeTag(t);
                                              setDetailFilterQuery("");
                                            }}
                                            className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-all ${isActive
                                              ? "bg-emerald-600 text-white"
                                              : "hover:bg-muted text-foreground"
                                              }`}
                                          >
                                            <span className="text-sm font-medium">
                                              {typeLabel(t as ComponentType)}
                                            </span>
                                            {isActive && (
                                              <CheckCircle className="size-4" />
                                            )}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : filterKey === "Group" ? (
                              /* Group values */
                              <div className="space-y-0.5">
                                {filteredGroupNames.length > 0 ? (
                                  filteredGroupNames.map((name) => {
                                    const isActive =
                                      selectedGroupTags.has(name);
                                    return (
                                      <button
                                        key={name}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleGroupTag(name);
                                        }}
                                        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-all ${isActive
                                          ? "bg-indigo-600 text-white"
                                          : "hover:bg-muted text-foreground"
                                          }`}
                                      >
                                        <span className="text-sm font-medium">
                                          {name}
                                        </span>
                                        {isActive && (
                                          <CheckCircle className="size-4" />
                                        )}
                                      </button>
                                    );
                                  })
                                ) : (
                                  <div className="p-8 text-center dark:text-white text-foreground text-xs italic">
                                    No groups match {`"${groupFilterSearch}"`}
                                  </div>
                                )}
                              </div>
                            ) : (
                              /* Type values */
                              <div className="space-y-0.5">
                                {filteredTypeNames.length > 0 ? (
                                  filteredTypeNames.map((t) => {
                                    const isActive = selectedTypeTags.has(t);
                                    return (
                                      <button
                                        key={t}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleTypeTag(t);
                                        }}
                                        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-all ${isActive
                                          ? "bg-emerald-600 text-white"
                                          : "hover:bg-muted text-foreground"
                                          }`}
                                      >
                                        <span className="text-sm font-medium">
                                          {typeLabel(t as ComponentType)}
                                        </span>
                                        {isActive && (
                                          <CheckCircle className="size-4" />
                                        )}
                                      </button>
                                    );
                                  })
                                ) : (
                                  <div className="p-8 text-center dark:text-white text-foreground text-xs italic">
                                    No types match {`"${typeFilterSearch}"`}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Footer */}
                          <div className="px-4 py-2 border-t border-border bg-muted/10 text-[10px] dark:text-white text-foreground font-medium">
                            {filteredComps.length} of {allDefComponents.length}{" "}
                            components shown
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Clear search icon */}
                  {detailFilterQuery && !filterKey && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDetailFilterQuery("");
                      }}
                      className="p-1 rounded-full hover:bg-muted dark:text-white text-foreground transition-colors mr-1 flex-shrink-0"
                    >
                      <X className="size-3.5" />
                    </button>
                  )}
                </div>

                {/* New Component button (right-aligned) */}
                {!isPublished && (
                  <RBACAction module="component" action="create">
                    <button
                      onClick={() => setShowDefAddComp(true)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors shadow-sm flex-shrink-0"
                    >
                      <Plus className="size-3.5" /> New Component
                    </button>
                  </RBACAction>
                )}
              </div>

              {/* Component List */}
              {loadingDefComponents && (
                <div className="rounded-xl border border-border bg-card flex items-center justify-center py-20">
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="size-7 text-indigo-500 animate-spin" />
                    <span className="text-sm dark:text-white text-foreground">Loading components</span>
                  </div>
                </div>
              )}

              {!loadingDefComponents && allDefComponents.length === 0 && (
                <div className="rounded-xl border border-border bg-card text-center py-20">
                  <div className="size-14 rounded-2xl bg-muted/60 flex items-center justify-center mx-auto mb-4">
                    <Cpu className="size-7 dark:text-white text-foreground" />
                  </div>
                  <p className="text-sm font-semibold text-foreground">
                    No components yet
                  </p>
                  <p className="text-xs dark:text-white text-foreground mt-1 mb-4">
                    Add your first component to this definition.
                  </p>
                </div>
              )}

              {!loadingDefComponents &&
                allDefComponents.length > 0 &&
                filteredComps.length === 0 && (
                  <div className="rounded-xl border border-border bg-card text-center py-20">
                    <div className="size-14 rounded-2xl bg-muted/60 flex items-center justify-center mx-auto mb-4">
                      <Search className="size-7 dark:text-white text-foreground" />
                    </div>
                    <p className="text-sm font-semibold text-foreground">
                      No components match your filters
                    </p>
                    <p className="text-xs dark:text-white text-foreground mt-1 mb-3">
                      Try adjusting your filter selection
                    </p>
                    <button
                      onClick={clearAllFilters}
                      className="inline-flex items-center gap-1.5 text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors px-3 py-1.5 rounded-lg border border-indigo-200 dark:border-indigo-500/20 hover:bg-indigo-50 dark:hover:bg-indigo-500/10"
                    >
                      <X className="size-3" /> Clear All Filters
                    </button>
                  </div>
                )}

              {!loadingDefComponents && filteredComps.length > 0 && (
                <div className="rounded-xl border border-border overflow-hidden bg-card shadow-sm">
                  {filteredComps.map((comp) => renderComponent(comp))}
                </div>
              )}
            </>
          );
        })()}

        {/* Add Component Modal */}
        <Modal
          open={showDefAddComp}
          onClose={() => {
            setShowDefAddComp(false);
            setDefCompForm({
              title: "",
              componentType: "",
              description: "",
              purpose: "",
              groupName: "",
            });
          }}
          title="Add Component"
        >
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Component Name" required>
              <FormInput
                placeholder="e.g. CrowdStrike Falcon"
                value={defCompForm.title}
                onChange={(v) => setDefCompForm((f) => ({ ...f, title: v }))}
              />
            </FormField>
            <FormField label="Component Type" required>
              <FormSelect
                options={COMPONENT_TYPES.map((t) => ({
                  value: t,
                  label: typeLabel(t),
                }))}
                placeholder="Select type"
                value={defCompForm.componentType || ""}
                onChange={(v) =>
                  setDefCompForm((f) => ({
                    ...f,
                    componentType: v as ComponentType,
                  }))
                }
              />
            </FormField>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Purpose" required>
              <FormInput
                placeholder="e.g. Satisfies SI and AU control families"
                value={defCompForm.purpose}
                onChange={(v) => setDefCompForm((f) => ({ ...f, purpose: v }))}
              />
            </FormField>
            <FormField label="Grouping Name">
              <FormInput
                placeholder="Value (e.g. on-premise)"
                value={defCompForm.groupName}
                onChange={(v) =>
                  setDefCompForm((f) => ({ ...f, groupName: v }))
                }
              />
            </FormField>
          </div>
          <FormField label="Description">
            <FormInput
              placeholder="Brief description of the component"
              value={defCompForm.description}
              onChange={(v) =>
                setDefCompForm((f) => ({ ...f, description: v }))
              }
            />
          </FormField>
          <ModalFooterButtons
            onCancel={() => {
              setShowDefAddComp(false);
              setDefCompForm({
                title: "",
                componentType: "",
                description: "",
                purpose: "",
                groupName: "",
              });
            }}
            onSubmit={handleAddDefComponent}
            submitLabel={addingDefComp ? "Saving" : "Save"}
            submitDisabled={
              !defCompForm.title.trim() ||
              !defCompForm.componentType ||
              !defCompForm.purpose.trim() ||
              addingDefComp
            }
          />
        </Modal>

        {/* Export Format Modal (definition detail) */}
        <Modal
          open={showExportModal}
          onClose={() => {
            setShowExportModal(false);
            setExportTargetDocId(null);
          }}
          title="Export Component Definition"
          description="Choose a file format to export."
          size="sm"
        >
          <div className="grid grid-cols-3 gap-3">
            {(["json", "xml", "yaml"] as const).map((fmt) => (
              <button
                key={fmt}
                onClick={() => handleExport(fmt)}
                disabled={exporting}
                className="flex flex-col items-center gap-2 p-4 rounded-lg border border-border hover:border-indigo-400 dark:hover:border-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-colors group disabled:opacity-40"
              >
                <Upload className="size-6 dark:text-white text-foreground group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors" />
                <span className="text-sm font-semibold group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                  {fmt.toUpperCase()}
                </span>
              </button>
            ))}
          </div>
        </Modal>

        {/* Export Pulse Overlay */}
        {exporting && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/30 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-4 bg-card rounded-2xl px-10 py-8 shadow-2xl">
              <div className="relative flex items-center justify-center">
                <div className="absolute size-16 rounded-full bg-indigo-500/20 dark:bg-indigo-500/20 animate-ping" />
                <div className="absolute size-12 rounded-full bg-indigo-500/30 dark:bg-indigo-500/30 animate-pulse" />
                <Upload className="relative size-6 text-indigo-600 dark:text-indigo-400" />
              </div>
              <p className="text-sm font-semibold text-foreground mt-2">
                Exporting…
              </p>
              <p className="text-xs dark:text-white text-foreground">
                Please wait while we prepare your file
              </p>
            </div>
          </div>
        )}

        {/* Add Control Implementation Modal */}
        {ciModalElement}
      </div>
    );
  }

  // ─── Detail View ───────────────────────────────────────────────
  if (selectedComponent) {
    const definition = definitions.find(
      (d) => d.id === selectedComponent.definitionId,
    );
    const capabilities = definition?.capabilities || [];
    const controlImpls = selectedComponent.controlImplementations || [];
    const Icon = typeIconMap[selectedComponent.componentType] || Cpu;

    return (
      <div className="space-y-6">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              navigate("/components");
              setSelectedComponent(null);
              setSelectedDefinition(null);
            }}
            className="flex items-center gap-1.5 text-sm dark:text-white text-foreground hover:text-foreground/50 dark:hover:text-white transition-colors"
          >
            <ArrowLeft className="size-3.5" />
            Component Definition
          </button>
          {selectedDefinition && (
            <>
              <ChevronRight className="size-3.5 dark:text-white text-foreground" />
              <button
                onClick={() => setSelectedComponent(null)}
                className="text-sm dark:text-white text-foreground hover:text-foreground/50 dark:hover:text-white transition-colors"
              >
                {docTitles[selectedDefinition.documentId] ||
                  selectedDefinition.documentId}
              </button>
            </>
          )}
          <ChevronRight className="size-3.5 dark:text-white text-foreground" />
          <span className="text-sm font-medium text-foreground">
            {selectedComponent.title}
          </span>
        </div>

        {loadingDetail && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="size-6 text-indigo-600 animate-spin" />
            <span className="ml-3 text-sm dark:text-white text-foreground">Loading component details</span>
          </div>
        )}

        {!loadingDetail && (
          <>
            {/* Header card */}
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-card rounded-xl border border-border p-6"
            >
              <div className="flex items-start gap-5">
                <div className="w-14 h-12 bg-muted rounded-xl flex items-center justify-center flex-shrink-0">
                  <Icon className="size-6 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h1 className="text-2xl font-black text-foreground">
                        {selectedComponent.title}
                      </h1>
                      <p className="text-sm dark:text-white text-foreground mt-1">
                        {selectedComponent.description ||
                          "No description provided"}
                      </p>
                    </div>
                    <span className="px-3 py-1 rounded-full text-[10px] font-black tracking-wide bg-muted dark:text-white text-foreground flex-shrink-0">
                      {selectedComponent.componentType}
                    </span>
                  </div>

                  {/* Meta row */}
                  <div className="flex items-center gap-6 mt-4 pt-4 border-t border-border">
                    {selectedComponent.purpose && (
                      <div>
                        <span className="text-[10px] font-black tracking-widest dark:text-white text-foreground">
                          Purpose
                        </span>
                        <p className="text-sm font-medium text-foreground/70 mt-0.5">
                          {selectedComponent.purpose}
                        </p>
                      </div>
                    )}
                    <div>
                      <span className="text-[10px] font-black tracking-widest dark:text-white text-foreground">
                        Created
                      </span>
                      <p className="text-sm font-medium text-foreground/70 mt-0.5">
                        {new Date(
                          selectedComponent.createdAt,
                        ).toLocaleDateString("en-GB")}
                      </p>
                    </div>
                    <div>
                      <span className="text-[10px] font-black tracking-widest dark:text-white text-foreground">
                        Updated
                      </span>
                      <p className="text-sm font-medium text-foreground/70 mt-0.5">
                        {new Date(
                          selectedComponent.updatedAt,
                        ).toLocaleDateString("en-GB")}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sources */}
              {controlImpls.length > 0 && (
                <div className="mt-4 pt-4 border-t border-border">
                  <span className="text-[10px] font-black tracking-widest dark:text-white text-foreground">
                    Sources
                  </span>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    {[...new Set(controlImpls.map((ci) => ci.source))].map(
                      (source) => (
                        <span
                          key={source}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-indigo-50 dark:bg-indigo-500/10 text-xs font-medium text-indigo-700 dark:text-indigo-400"
                        >
                          <BookOpen className="size-3 flex-shrink-0" />
                          {source}
                        </span>
                      ),
                    )}
                  </div>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex items-center gap-2 mt-5 pt-4 border-t border-border">
                <button
                  onClick={() => setDeleteTarget(selectedComponent)}
                  disabled={isPublished}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-200 dark:border-red-500/30 text-red-600 dark:text-red-400 text-xs font-bold hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:border-muted-foreground disabled:d disabled:hover:bg-transparent"
                  title={
                    isPublished
                      ? "Only draft definitions can be deleted"
                      : "Delete Definition"
                  }
                >
                  <Trash2 className="size-3" />
                  Delete Definition
                </button>
              </div>
            </motion.div>

            {/* Control Implementations */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-card rounded-xl border border-border overflow-hidden"
            >
              <div className="flex items-center justify-between px-6 py-5 border-b border-border">
                <div>
                  <h3 className="text-lg font-bold text-foreground">
                    Control Implementations
                  </h3>
                  <p className="text-xs dark:text-white text-foreground mt-0.5">
                    Security controls implemented by this component
                  </p>
                </div>
                <button
                  onClick={() => {
                    resetCIForm();
                    fetchCatalogs();
                    setShowAddCI(true);
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors shadow-sm"
                >
                  <Plus className="size-3" />
                  Add Implementation
                </button>
              </div>

              {controlImpls.length === 0 ? (
                <div className="px-6 py-10 text-center">
                  <Shield className="size-8 d/50 mx-auto mb-2" />
                  <p className="text-sm dark:text-white text-foreground">
                    No control implementations defined yet
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {controlImpls.map((ci) => (
                    <ControlImplementationCard
                      key={ci.id}
                      ci={ci}
                      definitionId={selectedComponent.definitionId}
                      componentId={selectedComponent.id}
                      isPublished={isPublished}
                      onDeleted={() => handleSelectComponent(selectedComponent)}
                      onEdit={() => openEditCI(ci, selectedComponent)}
                    />
                  ))}
                </div>
              )}
            </motion.div>

            {/*
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-card rounded-xl border border-border overflow-hidden"
        >
          <div className="flex items-center justify-between px-6 py-5 border-b border-border">
            <div>
              <h3 className="text-lg font-bold text-foreground">
                Capabilities
              </h3>
              <p className="text-xs dark:text-white text-foreground mt-0.5">
                Capabilities provided by this component definition
              </p>
            </div>
            <button
              onClick={() => setShowAddCap(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors shadow-sm"
            >
              <Plus className="size-3" />
              Add Capability
            </button>
          </div>

          {capabilities.length === 0 ? (
            <div className="px-6 py-10 text-center">
              <Cpu className="size-8 d/50 mx-auto mb-2" />
              <p className="text-sm dark:text-white text-foreground">
                No capabilities defined yet
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border dark:border-border">
                    {["Name", "Description", "Created"].map((h) => (
                      <th
                        key={h}
                        className="px-6 py-3 text-left text-[10px] font-black tracking-widest dark:text-white text-foreground"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {capabilities.map((cap) => (
                    <tr
                      key={cap.id}
                      className="hover:bg-muted/50 transition-colors border-b border-border dark:border-border last:border-b-0"
                    >
                      <td className="px-6 py-4 text-sm font-medium text-foreground/80">
                        {cap.name}
                      </td>
                      <td className="px-6 py-4 text-sm dark:text-white text-foreground">
                        {cap.description || "-"}
                      </td>
                      <td className="px-6 py-4 text-sm dark:text-white text-foreground">
                        {new Date(cap.createdAt).toLocaleDateString('en-GB')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </motion.div> */}

            {/* Add Capability Modal */}
            {/* <Modal
          open={showAddCap}
          onClose={() => setShowAddCap(false)}
          title="Add Capability"
          description="Define a new capability for this component definition"
          size="md"
          footer={
            <ModalFooterButtons
              onCancel={() => setShowAddCap(false)}
              onSubmit={handleAddCapability}
              submitLabel={addingCap ? "Saving" : "Save"}
              submitDisabled={!capForm.name || addingCap}
            />
          }
        > */}
            {/* <FormField label="Name" required hint="A short name for the capability">
            <FormInput
              placeholder="e.g. Identity Management"
              value={capForm.name}
              onChange={(v) => setCapForm((f) => ({ ...f, name: v }))}
            />
          </FormField>
          <FormField label="Description">
            <FormTextarea
              placeholder="Describe what this capability provides"
              value={capForm.description}
              onChange={(v) => setCapForm((f) => ({ ...f, description: v }))}
              rows={3}
            />
          </FormField>
*/}

            {/* Add Control Implementation Modal */}
            {ciModalElement}
          </>
        )}
      </div>
    );
  }

  // ─── Skip list view if navigating via URL parameter ─────────────
  if (componentIdFromUrl && !selectedDefinition) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2 animate-pulse">
          <div className="h-3.5 w-16 rounded bg-muted" />
          <div className="h-3.5 w-3 rounded bg-muted" />
          <div className="h-3.5 w-32 rounded bg-muted" />
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          <SkeletonCard lines={6} />
          <SkeletonCard lines={6} />
        </div>
      </div>
    );
  }

  // ─── List View ─────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Page header */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between"
      >
        <div>
          <h1>Component Definition</h1>
          <p className="text-sm dark:text-white text-foreground mt-0.5">
            Manage OSCAL component definitions and their control
            implementations.
          </p>
        </div>
        <div className="flex items-center gap-3 self-start sm:self-auto flex-shrink-0">
          <RBACAction module="component" action="create">
            <button
              onClick={() => {
                resetImport();
                setShowImport(true);
                fetchAccessibleOrgs("component", "create");
              }}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-indigo-500 dark:border-indigo-400 text-sm font-bold text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-all hover:-translate-y-px"
            >
              <Import className="size-3.5" /> Import Component
            </button>
          </RBACAction>
          <RBACAction module="component" action="create">
            <button
              onClick={() => {
                resetWizard();
                setShowWizard(true);
                fetchAccessibleOrgs("component", "create");
              }}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-all shadow-[0_4px_14px_rgba(79,70,229,0.3)] dark:shadow-[0_4px_14px_rgba(79,70,229,0.3)] hover:-translate-y-px"
            >
              <Plus className="size-3.5" />
              New Component
            </button>
          </RBACAction>
        </div>
      </motion.div>

      {/* Search bar + Rows per page */}
      {!loading && totalDefinitions > 0 && (
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 size-4 d/50 pointer-events-none" />
            <input
              type="text"
              placeholder="Search definitions"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full pl-10 pr-10 py-2.5 text-sm bg-card rounded-xl border border-border outline-none focus:border-indigo-500 dark:focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10 dark:focus:ring-indigo-500/10 transition-all placeholder:d dark:placeholder:d text-foreground"
            />
            {searchQuery && (
              <button
                onClick={() => handleSearchChange("")}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 dark:text-white text-foreground hover:d dark:hover:text-white/60 transition-colors"
              >
                <X className="size-4" />
              </button>
            )}
          </div>
          <Dropdown
            variant="pill"
            className="min-w-[150px]"
            value={filterStatus}
            onChange={(v) => handleStatusFilterChange(v)}
            options={[
              { value: "", label: "All Status" },
              { value: "draft", label: "Draft", dot: "bg-slate-400" },
              { value: "in_review", label: "In Review", dot: "bg-amber-500" },
              { value: "approved", label: "Approved", dot: "bg-blue-500" },
              { value: "published", label: "Published", dot: "bg-emerald-500" },
              { value: "rejected", label: "Rejected", dot: "bg-red-500" },
            ]}
          />
          <div className="flex items-center gap-3 ml-auto flex-shrink-0">
            <ViewModeToggle
              value={viewMode}
              onChange={(mode) => {
                setViewMode(mode);
                // The page-size options differ per view — snap the current
                // value to a valid option for the view being switched to
                // instead of leaving the dropdown holding a value (e.g. 25)
                // that isn't in the new list (e.g. [12,24,48,96]).
                handlePageSizeChange(resolvePageSizeForViewMode(pageSize, mode));
              }}
            />
            <label className="text-sm dark:text-white text-foreground whitespace-nowrap">Rows per page</label>
            <Dropdown
              value={String(pageSize)}
              onChange={(v) => {
                handlePageSizeChange(Number(v));
              }}
              options={getPageSizeOptions(viewMode).map((n) => ({
                value: String(n),
                label: String(n),
              }))}
              size="sm"
              align="end"
              className="!min-w-[80px]"
            />
          </div>
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-lg border border-border bg-card text-sm dark:text-white text-foreground">
          <span>Something went wrong.</span>
          <button
            onClick={() => {
              setError(null);
              fetchDefinitions();
            }}
            className="ml-auto text-xs underline hover:no-underline"
          >
            Retry
          </button>
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 animate-pulse">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="rounded-lg border border-border bg-card p-5 space-y-3"
            >
              <div className="flex items-center gap-3">
                <div className="size-9 rounded-lg bg-muted" />
                <div className="space-y-1.5 flex-1">
                  <div className="h-4 w-2/3 rounded bg-muted" />
                  <div className="h-3 w-1/3 rounded bg-muted" />
                </div>
              </div>
              <div className="h-3.5 w-full rounded bg-muted" />
              <div className="h-3.5 w-4/5 rounded bg-muted" />
            </div>
          ))}
        </div>
      )}

      {/* Definition table */}
      <DataTable
        columns={[
          {
            key: "sno",
            header: "#",
            align: "center",
            render: (_row, idx) => (
              <span className="d font-medium">
                {currentPage * pageSize + idx + 1}
              </span>
            ),
          },
          {
            key: "name",
            header: "Definition Name",
            sortable: true,
            align: "left",
            ellipsis: 2,
            tooltip: (row) =>
              docTitles[(row as any).documentId] ||
              (row as any).documentId ||
              "",
            render: (row) => (
              <span className="font-medium">
                {docTitles[(row as any).documentId] || (row as any).documentId}
              </span>
            ),
          },
          {
            key: "components",
            header: "Components",
            sortable: true,
            align: "center",
            render: (row) => (
              <span className="font-mono">
                {defCounts[(row as any).id]?.componentCount ?? 0}
              </span>
            ),
          },
          {
            key: "controls",
            header: "Controls",
            sortable: true,
            align: "center",
            render: (row) => (
              <span className="font-mono">
                {defCounts[(row as any).id]?.controlCount ?? 0}
              </span>
            ),
          },
          {
            key: "status",
            header: "Status",
            sortable: true,
            align: "center",
            render: (row) => {
              const lc = defCounts[(row as any).id]?.lifecycleStatus || "draft";
              return (
                <StatusBadge
                  status={lc as Parameters<typeof StatusBadge>[0]["status"]}
                />
              );
            },
          },
          {
            key: "created",
            header: "Created On",
            sortable: true,
            align: "center",
            render: (row) => (
              <span className="d">
                {new Date((row as any).createdAt).toLocaleDateString("en-GB")}
              </span>
            ),
          },
          {
            key: "updated",
            header: "Updated On",
            sortable: true,
            align: "center",
            render: (row) => (
              <span className="d">
                {new Date((row as any).updatedAt).toLocaleDateString("en-GB")}
              </span>
            ),
          },
          {
            key: "action",
            header: "Action",
            align: "center",
            render: (row) => (
              <div className="flex items-center justify-center gap-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openExportModal((row as any).documentId);
                  }}
                  className="p-1 rounded hover:bg-accent dark:text-white text-foreground hover:text-indigo-600 transition-colors"
                  title="Export"
                >
                  <SquareArrowOutUpRight className="size-4" />
                </button>
                <RBACAction module="component" action="delete">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteTarget({
                        ...((row as any).components?.[0] || ({} as any)),
                        definitionId: (row as any).id,
                        title:
                          docTitles[(row as any).documentId] ||
                          (row as any).documentId,
                      } as ComponentWithDef);
                    }}
                    disabled={
                      defCounts[(row as any).id]?.lifecycleStatus ===
                      "published"
                    }
                    className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-500/10 dark:text-white text-foreground hover:text-red-600 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:d"
                    title={
                      defCounts[(row as any).id]?.lifecycleStatus ===
                        "published"
                        ? "Only draft definitions can be deleted"
                        : "Delete"
                    }
                  >
                    <Trash2 className="size-4" />
                  </button>
                </RBACAction>
              </div>
            ),
          },
        ]}
        data={paginatedDefinitions}
        rowKey={(r) => (r as any).id}
        onRowClick={(row) => handleSelectDefinition(row as any)}
        loading={loading}
        emptyText="No component definitions found"
        page={currentPage}
        pageSize={pageSize}
        totalItems={searchFilteredDefinitions.length}
        onPageChange={setCurrentPage}
        itemLabel="definitions"
        sortKey={compSortKey}
        sortDir={compSortDir}
        onSort={handleCompSort}
        enableCardView
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        renderCard={(rows) => (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 pt-2 px-1">
            {rows.map((row) => {
              const r = row as any;
              const counts = defCounts[r.id];
              const lc = (counts?.lifecycleStatus || "draft") as Parameters<
                typeof StatusBadge
              >[0]["status"];
              const accent = statusAccent(lc);
              const title = docTitles[r.documentId] || r.documentId;
              const isPublished = counts?.lifecycleStatus === "published";
              return (
                <div
                  key={r.id}
                  onClick={() => handleSelectDefinition(r)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleSelectDefinition(r);
                    }
                  }}
                  className={`group relative flex h-full flex-col rounded-2xl border-2 border-[#e8edf2] dark:border-white/20 bg-card dark:bg-white/[0.04] overflow-hidden transition-all duration-300 hover:-translate-y-1 ${accent.hoverBorder} ${accent.hoverGlow} cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40`}
                >
                  <span
                    aria-hidden
                    className={`pointer-events-none absolute inset-y-0 left-0 w-1/2 -translate-x-full skew-x-12 bg-gradient-to-r from-transparent ${accent.sheenVia} to-transparent group-hover:translate-x-[220%] group-hover:transition-transform group-hover:duration-[900ms] group-hover:ease-out`}
                  />
                  <div className="@container relative z-[1] flex flex-1 flex-col gap-3 p-3.5">
                    <div className="flex items-start justify-between gap-2.5">
                      <div className="flex-1 min-w-0">
                        <TitleTooltip text={title}>
                          <h3 className="text-[13px] font-bold leading-snug tracking-[-0.01em] text-foreground truncate group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                            {title}
                          </h3>
                        </TitleTooltip>
                      </div>
                      <div className="shrink-0">
                        <Cpu className="size-4 text-indigo-500 dark:text-indigo-400" />
                      </div>
                    </div>
                    <div className="mt-auto flex flex-col gap-3">
                      <StatTiles>
                        <StatTile
                          icon={<Settings className="size-3.5" />}
                          accent="indigo"
                          value={counts?.componentCount ?? 0}
                          label="Components"
                        />
                        <StatTile
                          icon={<Shield className="size-3.5" />}
                          accent="emerald"
                          value={counts?.controlCount ?? 0}
                          label="Controls"
                        />
                      </StatTiles>
                      <MetaDates
                        created={safeFormatDate(r.createdAt)}
                        updated={safeFormatDate(r.updatedAt)}
                      />
                      <div className="flex items-center justify-between gap-2 pt-3 border-t border-[#eef2f6] dark:border-white/[0.06]">
                        <StatusBadge status={lc} dot />
                        <div
                          className="flex items-center gap-1 flex-shrink-0"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Tip label="Export">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openExportModal(r.documentId);
                              }}
                              className="p-1.5 rounded-md hover:bg-indigo-50 dark:hover:bg-indigo-500/10 text-[#64748b] dark:text-white/60 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                            >
                              <SquareArrowOutUpRight className="size-4" />
                            </button>
                          </Tip>
                          <RBACAction module="component" action="delete">
                            <Tip
                              label={
                                isPublished
                                  ? "Only draft definitions can be deleted"
                                  : "Delete"
                              }
                            >
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteTarget({
                                    ...(r.components?.[0] || ({} as any)),
                                    definitionId: r.id,
                                    title,
                                  } as ComponentWithDef);
                                }}
                                disabled={isPublished}
                                className="p-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-500/10 text-[#64748b] dark:text-white/60 hover:text-red-600 dark:hover:text-red-400 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                              >
                                <Trash2 className="size-4" />
                              </button>
                            </Tip>
                          </RBACAction>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      />

      {/* No search results */}
      {!loading &&
        definitions.length > 0 &&
        searchFilteredDefinitions.length === 0 &&
        searchQuery.trim() && (
          <div className="text-center py-12">
            <Search className="size-10 dark:text-white text-foreground mx-auto mb-3" />
            <p className="text-sm font-medium dark:text-white text-foreground">
              No definitions match "{searchQuery}"
            </p>
            <button
              onClick={() => handleSearchChange("")}
              className="text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors mt-2"
            >
              Clear search
            </button>
          </div>
        )}

      {/* Capabilities overview */}
      {/* {!loading && definitions.length > 0 && (
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          <div className="flex items-center justify-between px-6 py-5 border-b border-border">
            <div>
              <h3 className="text-lg font-bold text-foreground">
                Capabilities
              </h3>
              <p className="text-xs dark:text-white text-foreground mt-0.5">
                Capabilities across all component definitions
              </p>
            </div>
            <button
              onClick={() => {
                // If there's only one definition, target it directly
                if (definitions.length === 1) {
                  setCapTargetDefId(definitions[0].id);
                  setShowAddCap(true);
                } else {
                  setCapTargetDefId(null);
                  setShowAddCap(true);
                }
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors shadow-sm"
            >
              <Plus className="size-3" />
              Add Capability
            </button>
          </div>

          {totalCapabilities === 0 ? (
            <div className="px-6 py-10 text-center">
              <Cpu className="size-8 d/50 mx-auto mb-2" />
              <p className="text-sm dark:text-white text-foreground">
                No capabilities defined yet
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border dark:border-border">
                    {["Name", "Description", "Definition", "Created"].map((h) => (
                      <th
                        key={h}
                        className="px-6 py-3 text-left text-[10px] font-black tracking-widest dark:text-white text-foreground"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {definitions.flatMap((d) =>
                    (d.capabilities || []).map((cap) => (
                      <tr
                        key={cap.id}
                        className="hover:bg-muted/50 transition-colors border-b border-border dark:border-border last:border-b-0"
                      >
                        <td className="px-6 py-4 text-sm font-medium text-foreground/80">
                          {cap.name}
                        </td>
                        <td className="px-6 py-4 text-sm dark:text-white text-foreground">
                          {cap.description || "-"}
                        </td>
                        <td className="px-6 py-4 text-sm dark:text-white text-foreground">
                          {d.documentId}
                        </td>
                        <td className="px-6 py-4 text-sm dark:text-white text-foreground">
                          {new Date(cap.createdAt).toLocaleDateString('en-GB')}
                        </td>
                      </tr>
                    )),
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )} */}

      {/* Add Capability Modal (list view) */}
      {/* <Modal
        open={showAddCap}
        onClose={() => {
          setShowAddCap(false);
          setCapTargetDefId(null);
        }}
        title="Add Capability"
        description="Define a new capability for a component definition"
        size="md"
        footer={
          <ModalFooterButtons
            onCancel={() => {
              setShowAddCap(false);
              setCapTargetDefId(null);
            }}
            onSubmit={handleAddCapability}
            submitLabel={addingCap ? "Saving" : "Save"}
            submitDisabled={!capForm.name || !capTargetDefId || addingCap}
          />
        }
      >
        {definitions.length > 1 && (
          <FormField label="Component Definition" required hint="Select which definition to add this capability to">
            <FormSelect
              options={definitions.map((d) => d.documentId)}
              placeholder="Select definition"
              value={capTargetDefId ? definitions.find((d) => d.id === capTargetDefId)?.documentId || "" : ""}
              onChange={(v) => {
                const def = definitions.find((d) => d.documentId === v);
                setCapTargetDefId(def?.id || null);
              }}
            />
          </FormField>
        )}
        <FormField label="Name" required hint="A short name for the capability">
          <FormInput
            placeholder="e.g. Identity Management"
            value={capForm.name}
            onChange={(v) => setCapForm((f) => ({ ...f, name: v }))}
          />
        </FormField>
        <FormField label="Description">
          <FormTextarea
            placeholder="Describe what this capability provides"
            value={capForm.description}
            onChange={(v) => setCapForm((f) => ({ ...f, description: v }))}
            rows={3}
          />
        </FormField>
      </Modal> */}

      {/* Delete Confirmation */}
      <DeleteConfirmDialog
        open={!!deleteTarget}
        type="Component Definition"
        name={deleteTarget?.title || ""}
        deleting={deleting}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => handleDelete()}
      />

      {/* ── OSCAL Import Pipeline Modal ── */}
      <Modal
        open={showImport}
        onClose={() => {
          setShowImport(false);
          resetImport();
        }}
        title="Import Component Definition"
        size="md"
        footer={
          importStep === "select" ? (
            <div className="flex justify-end w-full gap-2">
              <button
                onClick={() => {
                  setShowImport(false);
                  resetImport();
                }}
                className="px-4 py-2 rounded-lg bg-muted text-sm font-medium hover:bg-accent transition-colors"
              >
                Cancel
              </button>
              {importFile && (
                <button
                  onClick={runImport}
                  disabled={!validateResult || validating}
                  className={`px-4 py-2 rounded-lg text-white text-sm font-bold transition-colors shadow-md ${!validateResult || validating
                    ? "bg-indigo-400 dark:bg-indigo-400 opacity-50 cursor-not-allowed shadow-indigo-500/10 dark:shadow-indigo-500/10"
                    : "bg-indigo-600 dark:bg-indigo-600 hover:bg-indigo-700 dark:hover:bg-indigo-700 shadow-indigo-500/20 dark:shadow-indigo-500/20"
                    }`}
                >
                  Import
                </button>
              )}
            </div>
          ) : importStep === "url" ? (
            <ModalFooterButtons
              onCancel={() => setImportStep("select")}
              onSubmit={startValidation}
              submitLabel="Import from URL"
              submitDisabled={!importUrl.trim()}
            />
          ) : importStep === "validated" ? (
            <div className="flex justify-between w-full gap-2">
              <button
                onClick={() => {
                  resetImport();
                }}
                className="px-4 py-2 rounded-lg bg-muted text-sm font-medium hover:bg-accent transition-colors"
              >
                Back
              </button>
              <button
                onClick={runImport}
                className="px-4 py-2 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors shadow-md shadow-indigo-500/20 dark:shadow-indigo-500/20"
              >
                Proceed to Import
              </button>
            </div>
          ) : importStep === "findings" ? (
            <div className="flex items-center justify-between w-full gap-3">
              <button
                onClick={() => {
                  setShowImport(false);
                  resetImport();
                }}
                className="px-3 py-1.5 rounded-md border border-border text-sm hover:bg-accent transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => {
                  resetImport();
                }}
                className="flex items-center gap-2 px-4 py-1.5 rounded-md bg-foreground text-background text-sm font-medium hover:opacity-90 transition-opacity"
              >
                Try Again
              </button>
            </div>
          ) : null
        }
      >
        {/* Step 0: Select source */}
        {importStep === "select" && (
          <div className="py-6 flex flex-col items-center gap-6">
            <input
              id="comp-import-file-input"
              type="file"
              accept=".json,.xml,.yaml,.yml"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  setImportFile(file);
                  setValidateResult(null);
                  setImportError(null);
                  setValidating(true);
                  (async () => {
                    try {
                      const res = await validateImport(file);
                      if (
                        res.data.documentType.toLowerCase() !==
                        "component-definition"
                      ) {
                        setImportError(
                          `Expected a component-definition document but got "${res.data.documentType}"`,
                        );
                      } else {
                        setValidateResult(res.data);
                        // Parse file for visualizer preview
                        try {
                          const text = await file.text();
                          const json = JSON.parse(
                            text,
                          ) as OscalComponentDefinition;
                          if (json["component-definition"]) {
                            loadVisualizerJson(json, file.name);
                          }
                        } catch {
                          /* preview parse failed - non-blocking */
                        }
                      }
                    } catch (err: unknown) {
                      setImportError(
                        err instanceof Error
                          ? err.message
                          : "Validation failed",
                      );
                    } finally {
                      setValidating(false);
                    }
                  })();
                }
              }}
            />

            {/* Organization selector */}
            <div className="w-full">
              <p className="text-xs font-medium dark:text-white text-foreground mb-2">Organization</p>
              {accessibleOrgsLoading ? (
                <div className="flex items-center gap-2 py-2">
                  <Loader2 className="size-4 animate-spin dark:text-white text-foreground" />
                  <span className="text-sm dark:text-white text-foreground">Loading</span>
                </div>
              ) : accessibleOrgs.length === 0 ? (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200/50 dark:border-red-500/20">
                  <AlertTriangle className="size-4 text-red-500 flex-shrink-0" />
                  <span className="text-sm text-red-600 dark:text-red-400">
                    No accessible organizations.
                  </span>
                </div>
              ) : accessibleOrgs.length === 1 ? (
                <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[#f5f7fb] dark:bg-white/[0.04] border border-[#e2e8f0] dark:border-white/[0.07] text-sm">
                  <span className="font-medium">{accessibleOrgs[0].name}</span>
                  <span className="d">({accessibleOrgs[0].shortName})</span>
                </div>
              ) : (
                <FormSelect
                  options={accessibleOrgs.map((o) => ({
                    value: o.id,
                    label: `${o.name} (${o.shortName})`,
                  }))}
                  value={importOrgId}
                  onChange={setImportOrgId}
                  placeholder="Select organization"
                />
              )}
            </div>

            {/* Import mode selector */}
            <div className="w-full">
              <p className="text-xs font-medium dark:text-white text-foreground mb-2">Import Mode</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setImportMode("full")}
                  className={`flex-1 px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors ${importMode === "full"
                    ? "border-indigo-500 dark:border-indigo-500 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-400"
                    : "border-border hover:bg-accent d"
                    }`}
                >
                  <span className="block font-semibold">Full</span>
                  <span className="block text-[11px] mt-0.5 font-normal opacity-70">
                    Replace entire definition
                  </span>
                </button>
                <button
                  disabled
                  className="flex-1 px-4 py-2.5 rounded-lg border border-border text-sm font-medium dark:text-white text-foreground opacity-50 cursor-not-allowed"
                  title="Coming soon"
                >
                  <span className="block font-semibold">Delta</span>
                  <span className="block text-[11px] mt-0.5 font-normal opacity-70">
                    Coming soon
                  </span>
                </button>
              </div>
            </div>

            <p className="text-sm font-semibold text-foreground">
              Select a source to add from
            </p>
            <div className="flex items-center justify-center gap-8">
              <button
                onClick={() =>
                  document.getElementById("comp-import-file-input")?.click()
                }
                className="flex flex-col items-center gap-3 group"
              >
                <div className="size-20 rounded-full bg-muted flex items-center justify-center group-hover:bg-accent transition-colors">
                  <Upload className="size-8 dark:text-white text-foreground group-hover:text-foreground transition-colors" />
                </div>
                <span className="text-sm dark:text-white text-foreground group-hover:text-foreground transition-colors">
                  Upload file
                </span>
              </button>
              <button
                onClick={() => setImportStep("url")}
                className="flex flex-col items-center gap-3 group"
              >
                <div className="size-20 rounded-full bg-muted flex items-center justify-center group-hover:bg-accent transition-colors">
                  <Link2 className="size-8 dark:text-white text-foreground group-hover:text-foreground transition-colors" />
                </div>
                <span className="text-sm dark:text-white text-foreground group-hover:text-foreground transition-colors">
                  Source link
                </span>
              </button>
            </div>
            {/* Show selected file inline with validation status */}
            {importFile && (
              <div
                className={`flex items-center gap-3 px-4 py-3 rounded-lg border w-full ${validateResult
                  ? "border-emerald-200 dark:border-emerald-500/20 bg-emerald-50 dark:bg-emerald-500/10"
                  : importError
                    ? "border-red-200 dark:border-red-500/20 bg-red-50 dark:bg-red-500/10"
                    : "border-border bg-muted"
                  }`}
              >
                <FileText
                  className={`size-6 flex-shrink-0 ${validateResult
                    ? "text-emerald-500"
                    : importError
                      ? "text-red-500"
                      : "d"
                    }`}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {importFile.name}
                  </p>
                  <p className="text-xs dark:text-white text-foreground">
                    {(importFile.size / 1024).toFixed(1)} KB
                  </p>
                  {validating && (
                    <p className="text-xs text-indigo-600 dark:text-indigo-400 flex items-center gap-1.5 mt-1">
                      <Loader2 className="size-3 animate-spin" /> Validating…
                    </p>
                  )}
                  {!validating && validateResult && (
                    <p className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5 mt-1">
                      <CheckCircle className="size-3" /> Validated -{" "}
                      {validateResult.documentType}, OSCAL{" "}
                      {validateResult.oscalVersion}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => {
                    setImportFile(null);
                    setValidateResult(null);
                    setImportError(null);
                    setValidating(false);
                  }}
                  className="p-1 rounded hover:bg-accent flex-shrink-0"
                >
                  <X className="size-4 dark:text-white text-foreground" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Step 1b: URI link */}
        {importStep === "url" && (
          <div className="space-y-4">
            <FormField
              label="Document URL"
              required
              hint="Provide a URL to an OSCAL component-definition document"
            >
              <FormInput
                placeholder="https://example.com/component-definition.json"
                value={importUrl}
                onChange={(v) => setImportUrl(v)}
              />
            </FormField>
          </div>
        )}

        {/* Validating */}
        {importStep === "validating" && (
          <div className="py-12 flex flex-col items-center gap-4">
            <Loader2 className="size-10 animate-spin text-indigo-500 dark:text-indigo-500" />
            <p className="text-sm font-medium">Validating document…</p>
            <p className="text-xs dark:text-white text-foreground text-center">
              Checking OSCAL schema and document structure.
            </p>
          </div>
        )}

        {/* Validated successfully */}
        {importStep === "validated" && validateResult && (
          <div className="py-8 flex flex-col items-center gap-4 text-center">
            <div className="size-12 rounded-full bg-emerald-100 dark:bg-emerald-500/20 flex items-center justify-center">
              <CheckCircle className="size-6 text-emerald-600 dark:text-emerald-400" />
            </div>
            <p className="text-sm font-semibold">Validation Passed</p>
            <div className="px-4 py-3 rounded-lg bg-muted border border-border w-full text-left space-y-1.5">
              <p className="text-xs">
                <span className="d">Document Type:</span>{" "}
                <span className="font-medium">
                  {validateResult.documentType}
                </span>
              </p>
              <p className="text-xs">
                <span className="d">OSCAL Version:</span>{" "}
                <span className="font-medium">
                  {validateResult.oscalVersion}
                </span>
              </p>
              {importFile && (
                <p className="text-xs">
                  <span className="d">File:</span>{" "}
                  <span className="font-medium">{importFile.name}</span>
                </p>
              )}
              <p className="text-xs">
                <span className="d">Import Mode:</span>{" "}
                <span className="font-medium capitalize">{importMode}</span>
              </p>
              {validateResult.findings.length > 0 && (
                <div className="pt-1.5 border-t border-border mt-1.5">
                  <p className="text-xs font-semibold text-amber-600 dark:text-amber-400 mb-1">
                    Findings ({validateResult.findings.length})
                  </p>
                  {validateResult.findings.map((f, i) => (
                    <p key={i} className="text-xs dark:text-white text-foreground">
                      {f.severity}: {f.message}
                    </p>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Importing */}
        {importStep === "importing" && (
          <div className="py-12 flex flex-col items-center gap-4">
            <Loader2 className="size-10 animate-spin text-indigo-500 dark:text-indigo-500" />
            <p className="text-sm font-medium">Importing document…</p>
            <p className="text-xs dark:text-white text-foreground text-center">
              Uploading and processing your OSCAL component definition. This may
              take a moment for large files.
            </p>
          </div>
        )}
      </Modal>

      {/* ── Import Session Modal ── */}
      <Modal
        open={showSession}
        onClose={() => {
          setShowSession(false);
          setSessionData(null);
        }}
        title="Import Component Definition"
        size="md"
        closable={false}
        footer={
          sessionData ? (
            <div className="flex justify-between w-full gap-2">
              {sessionData.jobStatus.toLowerCase() === "committed" ? (
                <button
                  onClick={() => {
                    setShowSession(false);
                    setSessionData(null);
                    resetImport();
                  }}
                  className="px-4 py-2 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors shadow-md shadow-indigo-500/20 dark:shadow-indigo-500/20"
                >
                  Done
                </button>
              ) : sessionData.jobStatus.toLowerCase() === "failed" ||
                sessionData.jobStatus.toLowerCase() === "aborted" ? (
                <button
                  onClick={() => {
                    setShowSession(false);
                    setSessionData(null);
                    resetImport();
                  }}
                  className="px-4 py-2 rounded-lg bg-muted text-sm font-medium hover:bg-accent transition-colors"
                >
                  Close
                </button>
              ) : (
                <>
                  <button
                    onClick={handleAbortSession}
                    disabled={aborting || committing}
                    className="px-4 py-2 rounded-lg border border-red-200 dark:border-red-500/20 text-red-600 dark:text-red-400 text-sm font-medium hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors disabled:opacity-40"
                  >
                    {aborting ? "Cancelling…" : "Cancel"}
                  </button>
                  <button
                    onClick={handleCommitSession}
                    disabled={
                      committing ||
                      aborting ||
                      sessionData.jobStatus.toLowerCase() !== "validated"
                    }
                    className={`px-4 py-2 rounded-lg text-white text-sm font-bold transition-colors shadow-md ${committing ||
                      aborting ||
                      sessionData.jobStatus.toLowerCase() !== "validated"
                      ? "bg-indigo-400 dark:bg-indigo-400 opacity-50 cursor-not-allowed shadow-indigo-500/10 dark:shadow-indigo-500/10"
                      : "bg-indigo-600 dark:bg-indigo-600 hover:bg-indigo-700 dark:hover:bg-indigo-700 shadow-indigo-500/20 dark:shadow-indigo-500/20"
                      }`}
                  >
                    {committing ? "Save…" : "Save"}
                  </button>
                </>
              )}
            </div>
          ) : null
        }
      >
        {sessionLoading && (
          <div className="py-12 flex flex-col items-center gap-4">
            <Loader2 className="size-10 animate-spin text-indigo-500 dark:text-indigo-500" />
            <p className="text-sm font-medium">Loading session…</p>
          </div>
        )}

        {sessionError && (
          <div className="flex items-start gap-2 px-3 py-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-sm text-red-700 dark:text-red-400 mb-4">
            <AlertTriangle className="size-4 mt-0.5 flex-shrink-0" />
            <p className="text-xs">{sessionError}</p>
          </div>
        )}

        {sessionData && (
          <div className="space-y-5">
            {committing && (
              <div className="py-6 flex flex-col items-center gap-3">
                <Loader2 className="size-8 animate-spin text-indigo-500 dark:text-indigo-500" />
                <p className="text-sm font-medium">Committing import…</p>
                <div className="w-full max-w-xs h-1.5 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full rounded-full bg-indigo-500 dark:bg-indigo-500 animate-pulse"
                    style={{ width: "70%" }}
                  />
                </div>
              </div>
            )}

            {!committing && (
              <>
                <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
                  <div className="flex items-center justify-between py-1.5 border-b border-border">
                    <span className="text-xs dark:text-white text-foreground">Status</span>
                    <span
                      className={`text-sm font-semibold capitalize ${sessionData.jobStatus.toLowerCase() === "committed"
                        ? "text-emerald-600 dark:text-emerald-400"
                        : sessionData.jobStatus.toLowerCase() === "failed" ||
                          sessionData.jobStatus.toLowerCase() === "aborted"
                          ? "text-red-600 dark:text-red-400"
                          : sessionData.jobStatus.toLowerCase() ===
                            "validated"
                            ? "text-indigo-600 dark:text-indigo-400"
                            : "text-amber-600 dark:text-amber-400"
                        }`}
                    >
                      {sessionData.jobStatus}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-1.5 border-b border-border">
                    <span className="text-xs dark:text-white text-foreground">Doc Type</span>
                    <span className="text-sm font-medium capitalize">
                      {sessionData.documentType}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-1.5">
                    <span className="text-xs dark:text-white text-foreground">Name</span>
                    <span className="text-sm font-medium">
                      {sessionData.sourceFilename}
                    </span>
                  </div>
                </div>

                {sessionData.findings.length > 0 && (
                  <div className="rounded-lg border border-border p-4">
                    <h4 className="text-xs font-bold dark:text-white text-foreground mb-2">
                      Findings ({sessionData.findings.length})
                    </h4>
                    <div className="space-y-2">
                      {sessionData.findings.map((f) => (
                        <div
                          key={f.id}
                          className="flex items-start gap-2 text-xs"
                        >
                          <span
                            className={`px-1.5 py-0.5 rounded font-bold text-[10px] ${f.severity === "info"
                              ? "bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400"
                              : f.severity === "warning"
                                ? "bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400"
                                : "bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400"
                              }`}
                          >
                            {f.severity}
                          </span>
                          <div className="flex-1">
                            <p className="font-medium">{f.title}</p>
                            <p className="d">{f.detail}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </Modal>

      {/* ── Export Format Modal ── */}
      <Modal
        open={showExportModal}
        onClose={() => {
          setShowExportModal(false);
          setExportTargetDocId(null);
        }}
        title="Export Component Definition"
        description="Choose a file format to export."
        size="sm"
      >
        <div className="grid grid-cols-3 gap-3">
          {(["json", "xml", "yaml"] as const).map((fmt) => (
            <button
              key={fmt}
              onClick={() => handleExport(fmt)}
              disabled={exporting}
              className="flex flex-col items-center gap-2 p-4 rounded-lg border border-border hover:border-indigo-400 dark:hover:border-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-colors group disabled:opacity-40"
            >
              <Upload className="size-6 dark:text-white text-foreground group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors" />
              <span className="text-sm font-semibold group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                {fmt.toUpperCase()}
              </span>
            </button>
          ))}
        </div>
      </Modal>

      {/* Add Control Implementation Modal */}
      {ciModalElement}

      {/* Export Pulse Overlay */}
      {exporting && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-4 bg-card rounded-2xl px-10 py-8 shadow-2xl">
            <div className="relative flex items-center justify-center">
              <div className="absolute size-16 rounded-full bg-indigo-500/20 dark:bg-indigo-500/20 animate-ping" />
              <div className="absolute size-12 rounded-full bg-indigo-500/30 dark:bg-indigo-500/30 animate-pulse" />
              <Upload className="relative size-6 text-indigo-600 dark:text-indigo-400" />
            </div>
            <p className="text-sm font-semibold text-foreground mt-2">
              Exporting…
            </p>
            <p className="text-xs dark:text-white text-foreground">Please wait while we prepare your file</p>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Control Implementation Card (detail view) ───────────────── */

function ControlImplementationCard({
  ci,
  definitionId,
  componentId,
  isPublished,
  onDeleted,
  onEdit,
}: {
  ci: ControlImplementationResponse;
  definitionId: string;
  componentId: string;
  isPublished: boolean;
  onDeleted: () => void;
  onEdit?: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [selectedIrIds, setSelectedIrIds] = useState<Set<string>>(new Set());
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const reqCount = ci.implementedRequirements?.length || 0;

  const toggleSelect = (irId: string) => {
    setSelectedIrIds((prev) => {
      const next = new Set(prev);
      next.has(irId) ? next.delete(irId) : next.add(irId);
      return next;
    });
  };

  const handleDelete = async (ids: string[]) => {
    setDeletingIds(new Set(ids));
    try {
      await deleteImplementedRequirements(definitionId, componentId, ids);
      useToastStore.getState().showSuccess(
        "Removed",
        `${ids.length} control${ids.length !== 1 ? "s" : ""} removed`,
      );
      setSelectedIrIds(new Set());
      onDeleted();
    } catch (err) {
      useToastStore.getState().showError(
        "Error",
        err instanceof Error ? err.message : "Failed to remove controls",
      );
    } finally {
      setDeletingIds(new Set());
    }
  };

  return (
    <div className="px-6 py-4">
      <div className="flex items-center gap-2">
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex-1 flex items-center justify-between text-left"
        >
          <div>
            <h4 className="text-sm font-bold text-foreground">{ci.source}</h4>
            {ci.description && (
              <p className="text-xs dark:text-white text-foreground mt-0.5">{ci.description}</p>
            )}
          </div>
          <div className="flex items-center gap-3 flex-shrink-0 mr-2">
            <span className="text-xs font-medium dark:text-white text-foreground">
              {reqCount} requirement{reqCount !== 1 ? "s" : ""}
            </span>
            <ChevronRight
              className={`size-4 d transition-transform ${expanded ? "rotate-90" : ""}`}
            />
          </div>
        </button>
        {!isPublished && onEdit && (
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(); }}
            className="flex-shrink-0 p-1.5 rounded hover:bg-indigo-50 dark:hover:bg-indigo-500/10 text-indigo-500 dark:text-indigo-400 transition-colors"
            title="Edit control implementation"
          >
            <Pencil className="size-3.5" />
          </button>
        )}
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            {/* Top-level set parameters */}
            {ci.setParameters?.length > 0 && (
              <div className="mt-3 ml-4 space-y-1">
                <span className="text-[10px] font-black tracking-widest dark:text-white text-foreground">
                  Parameters
                </span>
                {ci.setParameters.map((p: any, idx: number) => (
                  <div key={idx} className="flex items-center gap-2 text-xs">
                    <span className="font-mono text-indigo-600 dark:text-indigo-400">
                      {p.paramId}
                    </span>
                    <span className="d">{p.values?.join(", ")}</span>
                    {p.remarks && (
                      <span className="d italic">- {p.remarks}</span>
                    )}
                  </div>
                ))}
              </div>
            )}

            {ci.implementedRequirements?.length > 0 && (
              <div className="mt-3 ml-4 border-l-2 border-border pl-4">
                {/* Bulk action bar */}
                {!isPublished && selectedIrIds.size > 0 && (
                  <div className="flex items-center gap-2 mb-2 py-1.5 px-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20">
                    <span className="text-xs text-red-700 dark:text-red-400 flex-1">
                      {selectedIrIds.size} selected
                    </span>
                    <button
                      onClick={() => handleDelete([...selectedIrIds])}
                      disabled={deletingIds.size > 0}
                      className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50"
                    >
                      <Trash2 className="size-3" />
                      Remove selected
                    </button>
                    <button
                      onClick={() => setSelectedIrIds(new Set())}
                      className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                )}

                <div className="space-y-2">
                  {ci.implementedRequirements.map((ir) => (
                    <div key={ir.id} className="py-2 group">
                      <div className="flex items-start gap-2">
                        {/* Checkbox for bulk select (draft only) */}
                        {!isPublished && (
                          <input
                            type="checkbox"
                            checked={selectedIrIds.has(ir.id)}
                            onChange={() => toggleSelect(ir.id)}
                            className="mt-0.5 accent-indigo-600 cursor-pointer flex-shrink-0"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-0.5 rounded bg-indigo-50 dark:bg-indigo-500/10 text-[10px] font-black text-indigo-600 dark:text-indigo-400">
                              {ir.controlId?.toUpperCase()}
                            </span>
                          </div>
                          {ir.description && (
                            <p className="text-xs dark:text-white text-foreground mt-1">{ir.description}</p>
                          )}

                          {/* Requirement-level set parameters */}
                          {ir.setParameters?.length > 0 && (
                            <div className="mt-2 space-y-1">
                              <span className="text-[10px] font-bold tracking-widest dark:text-white text-foreground dark:text-white/20">
                                Parameters
                              </span>
                              {ir.setParameters.map((p: any, idx: number) => (
                                <div key={idx} className="flex items-center gap-2 text-xs">
                                  <span className="font-mono text-indigo-600 dark:text-indigo-400">
                                    {p.paramId}
                                  </span>
                                  <span className="d">{p.values?.join(", ")}</span>
                                  {p.remarks && (
                                    <span className="d italic">- {p.remarks}</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}

                          {ir.statements?.length > 0 && (
                            <div className="mt-2 space-y-1">
                              <span className="text-[10px] font-bold tracking-widest dark:text-white text-foreground dark:text-white/20">
                                Statements
                              </span>
                              {ir.statements.map((stmt) => (
                                <div key={stmt.id} className="flex items-start gap-2 text-xs">
                                  <span className="font-mono dark:text-white text-foreground flex-shrink-0">
                                    {stmt.statementId?.toUpperCase()}
                                  </span>
                                  <span className="d">{stmt.description}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Per-row trash (draft only) */}
                        {!isPublished && (
                          <button
                            onClick={() => handleDelete([ir.id])}
                            disabled={deletingIds.has(ir.id)}
                            className="flex-shrink-0 p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-red-50 dark:hover:bg-red-500/10 text-red-500 transition-all disabled:opacity-50"
                            title="Remove this control"
                          >
                            <Trash2 className="size-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
